/**
 * Field definitions — Tilleggsmoduler (C): FIELDS entries for entities that
 * are NOT part of the Dyrejournal build. Spread into FIELDS by lib/fields.js.
 * The Dyrejournal-repo export replaces this file with `export default {};`.
 * Classification source of truth: docs/dyrejournal-separation/manifest.md
 */
import { h } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

import { TIMEZONE_OPTIONS, raceZone, raceZoneText } from '../lib/raceTime';

import { COLOR_PICKER_LIST, GRADIENT_PICKER_LIST, BACKGROUND_PICKER_LIST, findInvalidEmailChars, apiServer, postnummerLookup, spiderPage, getSpiderUrl, addTagsIfMissing } from '../lib/fieldsHelpers';
import { MARKETPLACE_CATEGORIES } from '../lib/marketplaceCategories';
import {
    BACKGROUND_TEXT_EFFECTS,
    BACKGROUND_TEXT_STYLES,
    BACKGROUND_TEXT_ANIMATIONS,
    BACKGROUND_DECORATIONS,
    BACKGROUND_MOTIONS,
    IMAGE_OVERLAYS,
    IMAGE_FILTERS,
} from '../lib/backgroundPostText';
import { FISH_SPECIES, CATCH_METHODS, WATER_TYPES } from '../lib/fishCatch';
import { CONTACT_TYPES } from '../lib/fishContact';

export default {
    // Extensions of sections defined in the shared lib/fields.js. Applied by
    // lib/fields.js via applyFieldExtensions(); absent in a product export that
    // stubs this registry, so these fields never reach the Dyrejournal build.
    fieldExtensions: [
        // NSF terminliste (2026-10-24): per-user push opt-out for competition alerts.
        {
            entity: 'notifications',
            sectionKey: 'notifications',
            fields: [
                {
                    title: <Text id='push.personal.nsfCompetition'>Terminliste: new, moved or cancelled competitions</Text>,
                    name: 'notifications.nsfCompetition',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-days',
                    getValue: obj => (obj?.notifications?.nsfCompetition === 0 ? 0 : 1),
                },
            ],
        },
        {
            entity: 'user',
            sectionKey: 'User admin settings',
            fields: [
                {
                    title: <Text id='input.isRangeOfficer'>Range officer (skyteleder)</Text>,
                    placeholder: <Text id='input.isRangeOfficer'>Range officer (skyteleder)</Text>,
                    help: <Text id='input.isRangeOfficer-help'>May register ammo sales and club operations events for the club.</Text>,
                    name: 'isRangeOfficer',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-shield',
                    getValue: user => user.isRangeOfficer ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isPartner'>Partner (dealer/supplier)</Text>,
                    placeholder: <Text id='input.isPartner'>Partner (dealer/supplier)</Text>,
                    help: <Text id='input.isPartner-help'>May import marketplace listings from Excel/CSV files, like an admin.</Text>,
                    name: 'isPartner',
                    type: 'toggle',
                    icon: 'fa-solid fa-handshake',
                    getValue: user => user.isPartner ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
            ],
        },
        {
            entity: 'user',
            sectionKey: 'User address',
            fields: [
                {
                    title: <Text id='input.bankAccount'>Bank account</Text>,
                    placeholder: <Text id='input.bankAccount-placeholder'>1234.56.78901</Text>,
                    help: <Text id='input.bankAccount-help'>Used when the club pays you back for expenses you have covered.</Text>,
                    name: 'bankAccount',
                    type: 'text',
                    icon: 'fa-solid fa-building-columns',
                    getValue: user => user.bankAccount || '',
                    showInList: false,
                },
            ],
        },
        // Terminliste stevner on the club's public calendar (2026-10-26): radius around
        // the club (its own stevner give the position); own krets and NM always show.
        {
            entity: 'customer',
            sectionKey: 'frontpageSettings',
            fields: [
                {
                    title: <Text id='input.frontpageItems-terminlisteRadiusKm'>Terminliste: radius around the club (km)</Text>,
                    placeholder: <Text id='input.frontpageItems-terminlisteRadiusKm-placeholder'>100</Text>,
                    help: <Text id='input.frontpageItems-terminlisteRadiusKm-help'>NSF competitions within this distance of the club are shown in the public calendar, together with the club's own krets and every NM. 0 = no radius.</Text>,
                    name: 'frontpageItems.terminlisteRadiusKm',
                    type: 'number',
                    icon: 'fa-solid fa-location-crosshairs',
                    getValue: obj => (obj?.frontpageItems?.terminlisteRadiusKm === undefined || obj?.frontpageItems?.terminlisteRadiusKm === null
                        ? 100 : obj.frontpageItems.terminlisteRadiusKm),
                    isAdminField: true,
                },
            ],
        },
        {
            entity: 'customer',
            sectionKey: 'menuSettings',
            fields: [
                {
                    title: <Text id='input.menuItems-ammoSales'>Ammo sales (shooting club)</Text>,
                    placeholder: <Text id='input.menuItems-ammoSales'>Ammo sales (shooting club)</Text>,
                    name: 'menuItems.ammoSales',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.ammoSales ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-terminliste'>Terminliste (NSF competitions)</Text>,
                    placeholder: <Text id='input.menuItems-terminliste'>Terminliste (NSF competitions)</Text>,
                    name: 'menuItems.terminliste',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-days',
                    getValue: obj => obj.menuItems?.terminliste ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-clubOperations'>Club operations (klubbdrift)</Text>,
                    placeholder: <Text id='input.menuItems-clubOperations'>Club operations (klubbdrift)</Text>,
                    name: 'menuItems.clubOperations',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.clubOperations ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.helpSourceCustomer'>Help articles from customer (shared pool)</Text>,
                    placeholder: <Text id='input.helpSourceCustomer'>Help articles from customer (shared pool)</Text>,
                    help: <Text id='input.helpSourceCustomer-help'>customer.id whose help articles also show on this site. 86 = Pistolklubb.app pool. 0 = none.</Text>,
                    name: 'helpSourceCustomer',
                    type: 'number',
                    icon: 'fa-duotone fa-circle-question',
                    getValue: obj => obj.helpSourceCustomer || 0,
                    isAdminField: true,
                },
            ],
        },
        {
            entity: 'customer',
            sectionKey: 'Customer most used - Admin',
            fields: [
                {
                    title: <Text id='input.trackerPoolSize'>GPS trackers owned</Text>,
                    placeholder: <Text id='input.trackerPoolSize-placeholder'>0</Text>,
                    help: <Text id='input.trackerPoolSize-help'>Total number of GPS trackers available for races. Shown against daily demand in the race tracker calendar.</Text>,
                    name: 'trackerPoolSize',
                    type: 'number',
                    icon: 'fa-solid fa-satellite-dish',
                    getValue: obj => obj.trackerPoolSize,
                    showInList: false,
                    isAdminField: true,
                },
            ],
        },
        {
            entity: 'customer',
            sectionKey: 'Customer featureFlags',
            fields: [
                {
                    title: <Text id='input.ammoStockless'>Ammo sales without stock</Text>,
                    placeholder: <Text id='input.ammoStockless'>Ammo sales without stock</Text>,
                    help: <Text id='input.ammoStockless-help'>Register ammo sales for the legal record only. Stock is kept in another system and is not changed.</Text>,
                    name: 'featureFlags.ammoStockless',
                    type: 'toggle',
                    icon: 'fa-solid fa-box-open',
                    getValue: obj => parseInt(obj.featureFlags?.ammoStockless, 10) === 1 ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.adventCalendar'>Advent calendar (Julekalender)</Text>,
                    placeholder: <Text id='input.adventCalendar'>Advent calendar (Julekalender)</Text>,
                    help: <Text id='input.adventCalendar-help'>Shows the club advent calendar to members in December: 24 doors with quiz, greetings, season numbers, sponsor deals, games and challenges. Club admins fill the doors under Klubbdrift.</Text>,
                    name: 'featureFlags.adventCalendar',
                    type: 'toggle',
                    icon: 'fa-solid fa-gift',
                    getValue: obj => parseInt(obj.featureFlags?.adventCalendar, 10) === 1 ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.supportTicketExpense'>Book support tickets as expenses</Text>,
                    placeholder: <Text id='input.supportTicketExpense'>Book support tickets as expenses</Text>,
                    help: <Text id='input.supportTicketExpense-help'>Shows a "Register as expense" button on support tickets for club officers. It opens the club economy entry prefilled from the ticket (refund requests).</Text>,
                    name: 'featureFlags.supportTicketExpense',
                    type: 'toggle',
                    icon: 'fa-solid fa-money-bill-transfer',
                    getValue: obj => parseInt(obj.featureFlags?.supportTicketExpense, 10) === 1 ? 1 : 0,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.accountantEmail'>Accountant e-mail</Text>,
                    placeholder: <Text id='input.accountantEmail-placeholder'>regnskap@example.no</Text>,
                    help: <Text id='input.accountantEmail-help'>Prefilled when an expense is e-mailed from the club economy. The address can be changed before each send.</Text>,
                    name: 'featureFlags.accountantEmail',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.featureFlags?.accountantEmail || '',
                    showInList: false,
                },
            ],
        },
        {
            entity: 'product',
            section: {
                name: <Text id='input.product-ammo-title'>Ammunition (shooting club)</Text>,
                key: 'Product ammunition',
                isCollapsed: true,
                fields: [
                    {
                        title: <Text id='input.isAmmo'>Ammunition</Text>,
                        placeholder: <Text id='input.isAmmo'>Ammunition</Text>,
                        help: <Text id='input.isAmmo-help'>Shows this product in the ammo sales module.</Text>,
                        name: 'isAmmo',
                        type: 'toggle',
                        icon: 'fa-solid fa-box-open',
                        getValue: obj => obj.isAmmo ? 1 : 0,
                        showInList: false,
                    },
                    {
                        title: <Text id='input.caliber'>Caliber</Text>,
                        placeholder: <Text id='input.caliber-placeholder'>9mm, .22 LR, .308 Win</Text>,
                        name: 'caliber',
                        type: 'text',
                        icon: 'fa-solid fa-ruler',
                        getValue: obj => obj.caliber,
                        showInList: false,
                    },
                    {
                        title: <Text id='input.roundsPerUnit'>Rounds per unit</Text>,
                        placeholder: <Text id='input.roundsPerUnit'>Rounds per unit</Text>,
                        help: <Text id='input.roundsPerUnit-help'>Rounds in one sold unit, usually one box.</Text>,
                        name: 'roundsPerUnit',
                        type: 'number',
                        icon: 'fa-solid fa-input-numeric',
                        getValue: obj => obj.roundsPerUnit,
                        showInList: false,
                    },
                    {
                        title: <Text id='input.supplierName'>Supplier</Text>,
                        placeholder: <Text id='input.supplierName'>Supplier</Text>,
                        name: 'supplierName',
                        type: 'text',
                        icon: 'fa-solid fa-truck',
                        getValue: obj => obj.supplierName,
                        showInList: false,
                    },
                    {
                        title: <Text id='input.supplierContact'>Supplier contact</Text>,
                        placeholder: <Text id='input.supplierContact-placeholder'>Phone or e-mail</Text>,
                        name: 'supplierContact',
                        type: 'text',
                        icon: 'fa-solid fa-address-card',
                        getValue: obj => obj.supplierContact,
                        showInList: false,
                    },
                    {
                        title: <Text id='input.supplierUrl'>Supplier URL</Text>,
                        placeholder: <Text id='input.supplierUrl'>Supplier URL</Text>,
                        name: 'supplierUrl',
                        type: 'text',
                        icon: 'fa-solid fa-link',
                        getValue: obj => obj.supplierUrl,
                        showInList: false,
                    },
                ],
            },
        },
    ],
    // NSF terminliste watch (2026-10-24): scalar fields rendered by FormFieldsCompact in
    // components/nsfCompetition/watchDrawer.js. The three name lists (gren,
    // øvelsesgruppe, krets) are chip pickers in the drawer, not form fields.
    nsfCompetitionWatch: [
        {
            name: <Text id='input.nsfCompetitionWatch-basics-title'>Watch</Text>,
            key: 'Watch',
            fields: [
                {
                    title: <Text id='input.nsfCompetitionWatch-name'>Name</Text>,
                    placeholder: <Text id='input.nsfCompetitionWatch-name-placeholder'>E.g. Feltpistol near home</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-duotone fa-tag',
                    getValue: watch => watch.name || '',
                },
                {
                    title: <Text id='input.nsfCompetitionWatch-maxDistanceKm'>Max distance from home (km)</Text>,
                    placeholder: '0',
                    help: <Text id='input.nsfCompetitionWatch-maxDistanceKm-help'>0 = no distance limit. Needs a home position.</Text>,
                    name: 'maxDistanceKm',
                    type: 'number',
                    icon: 'fa-duotone fa-route',
                    getValue: watch => watch.maxDistanceKm || 0,
                },
                {
                    title: <Text id='input.nsfCompetitionWatch-notifyPush'>Push notification</Text>,
                    name: 'notifyPush',
                    type: 'toggle',
                    icon: 'fa-duotone fa-bell',
                    getValue: watch => (watch.notifyPush === 0 ? 0 : 1),
                },
                {
                    title: <Text id='input.nsfCompetitionWatch-notifyEmail'>E-mail</Text>,
                    name: 'notifyEmail',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope',
                    getValue: watch => (watch.notifyEmail === 0 ? 0 : 1),
                },
                {
                    title: <Text id='input.nsfCompetitionWatch-weeklyDigest'>Weekly summary</Text>,
                    help: <Text id='input.nsfCompetitionWatch-weeklyDigest-help'>Monday morning e-mail with the competitions this watch matches in the next 14 days.</Text>,
                    name: 'weeklyDigest',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-week',
                    getValue: watch => (watch.weeklyDigest === 1 ? 1 : 0),
                },
                {
                    title: <Text id='input.nsfCompetitionWatch-active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-duotone fa-toggle-on',
                    getValue: watch => (watch.active === 0 ? 0 : 1),
                },
            ],
        },
    ],
    // Marketplace feed: a web page read on a schedule and turned into listings (importDrawer «Automatiske kilder»).
    marketplaceFeed: [
        {
            name: <Text id='input.marketplaceFeed-basics-title'>Feed</Text>,
            key: 'Feed basics',
            fields: [
                {
                    title: <Text id='input.marketplaceFeed-name'>Name</Text>,
                    placeholder: <Text id='input.marketplaceFeed-name-placeholder'>Shop or seller name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-duotone fa-tag',
                    getValue: feed => feed.name || '',
                },
                {
                    title: <Text id='input.marketplaceFeed-url'>Page to read</Text>,
                    placeholder: <Text id='input.marketplaceFeed-url-placeholder'>https://...</Text>,
                    help: <Text id='input.marketplaceFeed-url-help'>A page with a table of items: type, name, calibre and price. Read once a day.</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-duotone fa-link',
                    getValue: feed => feed.url || '',
                },
                {
                    title: <Text id='input.marketplaceFeed-intervalHours'>Hours between reads</Text>,
                    placeholder: '24',
                    name: 'intervalHours',
                    type: 'number',
                    icon: 'fa-duotone fa-clock',
                    getValue: feed => feed.intervalHours || 24,
                },
            ],
        },
    ],
    news: [
        {
            name: <Text id='input.news-basics-title'>News basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'News basics',
            fields: [
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: news => news.active || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'select',
                    icon: 'fa-solid fa-list',
                    getValue: obj => obj.race,
                    datalistName: 'races',
                    // datalist: [['birthday', 'Birthday'], ['buying', 'Buying'], ['christmas', 'Christmas'], ['dreaming', 'Dreaming about'], ['wedding', 'Wedding'], ['other', 'Other']],
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}`,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: news => news.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: news => news.title,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.sort'>Sort</Text>,
                    placeholder: <Text id='input.sort'>Sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: news => news.sort,
                    showInList: true,
                },
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: news => news.ingress,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textareaEditor',
                    viewHtml: false,
                    icon: 'fa-solid fa-pen-field',
                    getValue: news => news.body,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.inlineImages'>inlineImages</Text>,
                    placeholder: <Text id='input.inlineImages'>inlineImages</Text>,
                    name: 'inlineImages',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.inlineImages,
                    showInList: false,
                },
                {
                    title: <Text id='input.createdDate'>createdDate</Text>,
                    placeholder: <Text id='input.createdDate'>createdDate</Text>,
                    name: 'createdDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.createdDate,
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.publishDate'>publishDate</Text>,
                    placeholder: <Text id='input.publishDate'>publishDate</Text>,
                    name: 'publishDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.publishDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.expireDate'>expireDate</Text>,
                    placeholder: <Text id='input.expireDate'>expireDate</Text>,
                    name: 'expireDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.expireDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.tag'>Tag</Text>,
                    placeholder: <Text id='input.tag'>Tag</Text>,
                    help: <Text id='input.tag-help'>Tag is used to place news on the correct page. If unsure, do not change this..</Text>,
                    name: 'tag',
                    type: 'text',
                    icon: 'fa-solid fa-hashtag',
                    getValue: news => news.tag,
                    showInList: true,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    help: <Text id='input.tags-help'>Tags are used to find similar news. Add as many as you like.</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: news => news.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.customers'>customers</Text>,
                    placeholder: <Text id='input.customers'>customers</Text>,
                    name: 'customers',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminCustomers',
                    getValue: obj => obj.customers?.map(m => m),
                    datalist: [
                        'id', 'name'
                    ],
                    displayArrayObject: obj => `${obj.id}: ${obj.name}`,

                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
        {
            name: <Text id='input.news-simple-title'>Your news</Text>,
            key: 'News simple',
            showSectionName: false,
            marginTop: 'mt-1',
            fields: [
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: news => news.ingress,
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: news => news.images,
                    showInList: false,
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='input.videos'>Videos</Text>,
                    placeholder: <Text id='input.videos'>Videos</Text>,
                    help: <Text id='input.videos-help'>Add a video to the article. Videos will take a couple of minutes to process.</Text>,
                    addFileText: <Text id='input.add-video'>Add video</Text>,
                    name: 'files',
                    type: 'file',
                    displayLargeFiles: true,
                    getValue: news => news.files,
                    showInList: false,
                    isSimpleEdit: true,
                },
            ],
        },
    ],
    installation: [
        {
            name: <Text id='input.installations-advanced-title'>Installation advanced</Text>,
            // help: <Text id='input.installation-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation advanced',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: installation => installation.id || null,
                    isReadOnly: true,
                    showInList: true,
                    isAdminField: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: installation => installation.customer || null,
                    showInList: true,
                    isAdminField: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: installation => installation.user || null,
                    showInList: true,
                    isAdminField: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.qr'>Qr</Text>,
                    placeholder: <Text id='input.qr'>Qr</Text>,
                    name: 'qr',
                    type: 'text',
                    icon: 'fa-solid fa-qrcode',
                    getValue: installation => installation.code || null,
                    showQR: true,
                    isReadOnly: true,
                    showInList: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.code'>Code</Text>,
                    placeholder: <Text id='input.code'>Code</Text>,
                    name: 'code',
                    type: 'text',
                    icon: 'fa-solid fa-key',
                    getValue: installation => installation.code || null,
                    showQR: false,
                    isReadOnly: true,
                    showInList: true,
                    hideOnCreate: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.ordernumber'>OrderNumber</Text>,
                    placeholder: <Text id='input.ordernumber'>OrderNumber</Text>,
                    name: 'orderNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.orderNumber || null,
                    showInList: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.isWishList'>isWishList</Text>,
                    placeholder: <Text id='input.isWishList'>isWishList</Text>,
                    name: 'isWishList',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: part => part.isWishList ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.wishListType'>wishListType</Text>,
                    placeholder: <Text id='input.wishListType'>wishListType</Text>,
                    name: 'wishListType',
                    type: 'select',
                    icon: 'fa-solid fa-list',
                    getValue: obj => obj.wishListType,
                    datalist: [['birthday', 'Birthday'], ['buying', 'Buying'], ['christmas', 'Christmas'], ['dreaming', 'Dreaming about'], ['wedding', 'Wedding'], ['other', 'Other']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },



            ],
        },
        {
            name: <Text id='input.installations-basics-title'>Installation basics</Text>,
            // help: <Text id='input.installation-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation basics',
            fields: [
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.name || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: installation => installation.description,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.installations-location-title'>Installation location</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation location',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.address,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.postalcode,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalcode',
                                    value: obj.postalcode,
                                    id: obj.id,
                                });
                                updateValue({
                                    name: 'place',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.place'>Place</Text>,
                    placeholder: <Text id='input.place'>Place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.place,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-earth-europe',
                    getValue: installation => installation.country,
                    showInList: true,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.installations-social-title'>Installation social</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation social',
            fields: [
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: installation => installation.url,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-brands fa-facebook',
                    getValue: installation => installation.facebook,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-brands fa-instagram',
                    getValue: installation => installation.instagram,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.installations-related-title'>Installation related</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation related',
            fields: [
                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: installation => installation.members?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.parts'>Parts</Text>,
                    placeholder: <Text id='input.parts'>Parts</Text>,
                    name: 'parts',
                    type: 'arrayObject',
                    lookupArray: 'adminParts',
                    icon: 'fa-solid fa-layer-group',
                    getValue: installation => installation.parts?.map(file => file),
                    datalist: [
                        'qty', 'id', 'name', 'partNumber',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.inspections'>Inspections</Text>,
                    placeholder: <Text id='input.inspections'>Inspections</Text>,
                    name: 'inspections',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-user-magnifying-glass',
                    getValue: installation => installation.inspections?.map(yt => yt),
                    datalist: [
                        'partNumber',
                        'check1', 'check2', 'check3', 'check4', 'check5',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '100%',
                    displayHeight: '200px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.inspectionDate'>InspectionDate</Text>,
                    placeholder: <Text id='input.inspectionDate'>InspectionDate</Text>,
                    name: 'inspectionDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.inspectionDate,
                    showInList: true,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.installations-media-title'>Installation media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: installation => installation.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: installation => installation.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.youtubeVideos'>YoutubeVideos</Text>,
                    placeholder: <Text id='input.youtubeVideos'>YoutubeVideos</Text>,
                    name: 'youtubeVideos',
                    type: 'arrayObject',
                    // displayValue: (el) => `${el.url}`,
                    icon: 'fa-brands fa-youtube',
                    getValue: installation => installation.youtubeVideos?.map(file => file),
                    datalist: [
                        'url', 'title', 'description',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    hideOnCreate: true,
                },
            ],
        },
    ],
    installationPart: [
        {
            name: <Text id='input.installations-part-basics-title'>InstallationPart basics</Text>,
            key: 'InstallationPart basics',
            fields: [
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.qty || null,
                },
                {
                    title: <Text id='input.partNumber'>PartNumber</Text>,
                    placeholder: <Text id='input.partNumber'>PartNumber</Text>,
                    name: 'partNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.partNumber || null,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name || null,
                },
            ],
        },
    ],
    part: [
        {
            // name: 'Part related',
            name: <Text id='input.part-related-title'>Part Related</Text>,
            help: <Text id='input.part-related-help'>If this part is related to another part, you should add the part in this section.</Text>,
            key: 'Part related',
            lookupArray: 'parts',
            foundListName: 'foundParts',
            foundListText: 'Found parent parts',
            foundListDisplayKeys: ['id', 'name'],
            foundListReplaceKeys: [
                ['id', 'parent'],
                ['name', 'parentTitle'],
            ],
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            fields: [
                {
                    title: <Text id='input.part-parentTitle'>Parent Title</Text>,
                    placeholder: <Text id='input.part-parentTitle'>Parent Title</Text>,
                    name: 'parentTitle',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.parentTitle || null,
                    showInList: true,
                    // isAdminField: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.parent'>Parent</Text>,
                    placeholder: <Text id='input.parent'>Parent</Text>,
                    name: 'parent',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: part => part.parent || null,
                    showInList: true,
                    // isAdminField: true,
                    // hideOnCreate: true,
                },
            ],
        },
        {
            name:  <Text id='input.part-basics-title'>Part Basics</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part basics',
            fields: [
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: part => part.customer || null,
                    showInList: true,
                    isAdminField: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: part => part.qty || null,
                    showInList: false,
                    isAdminField: true,
                    isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.sold'>sold</Text>,
                    placeholder: <Text id='input.sold'>sold</Text>,
                    name: 'sold',
                    type: 'toggle',
                    icon: 'fa-solid fa-house-user',
                    getValue: part => part.sold ? 1 : 0,
                    showInList: true,
                    displayValue: val => val ? <Text id='input.yes'>Yes</Text> : <Text id='input.no'>No</Text>,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.updatedDate'>Last updated</Text>,
                    placeholder: <Text id='input.updatedDate'>Last updated</Text>,
                    name: 'updatedDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: part => part.updatedDate || null,
                    showInList: false,
                    isAdminField: true,
                    isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    help: <Text id='input.url-help'>If you add a url to a part, the system will try to fetch the data from the url.</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    callback: async (part, updateValue) => {
                        if (part.url && part.url.startsWith('http')) {
                            const response = await spiderPage(part.url);
                            //
                            if (response) {
                                updateValue({
                                    name: 'name',
                                    value: response.title,
                                });
                                updateValue({
                                    name: 'description',
                                    value: response.description,
                                });
                                updateValue({
                                    name: 'imageUrl',
                                    value: response.image || response.icon,
                                });
                                updateValue({
                                    name: 'brand',
                                    value: response.product?.brand?.name,
                                });
                                updateValue({
                                    name: 'category',
                                    value: response.product?.category,
                                });
                                updateValue({
                                    name: 'sku',
                                    value: response.product?.sku,
                                });
                                updateValue({
                                    name: 'mpn',
                                    value: response.product?.mpn,
                                });
                                updateValue({
                                    name: 'gtin13',
                                    value: response.product?.gtin13,
                                });
                                updateValue({
                                    name: 'price',
                                    value: response.product?.offers?.price,
                                });
                                updateValue({
                                    name: 'rating',
                                    value: response.product?.aggregateRating?.ratingValue,
                                });
                                updateValue({
                                    name: 'ratingCount',
                                    value: response.product?.aggregateRating?.reviewCount,
                                });
                            }
                        }
                    },
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.imageUrl'>ImageUrl</Text>,
                    placeholder: <Text id='input.imageUrl'>ImageUrl</Text>,
                    name: 'imageUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl || null,
                    showInList: true,
                },

                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    getValue: part => part.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.part-media-title'>Part media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: part => part.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: part => part.files,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.youtubeVideos'>Youtube Videos</Text>,
                    placeholder: <Text id='input.youtubeVideos'>Youtube Videos</Text>,
                    name: 'youtubeVideos',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.youtubeVideos?.map(yt => yt),
                    datalist: [
                        'url', 'title', 'description',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.part-links'>Links</Text>,
                    placeholder: <Text id='input.part-links'>Links</Text>,
                    help: <Text id='input.part-links-help'>Links related to this part, e.g. the post about it on litt.no.</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.links?.map(link => link),
                    displayArrayObject: link => <>{link.name} (<a href={link.url} target='_blank' rel='noopener noreferrer'>{link.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                },
            ],
        },
        {
            name:  <Text id='input.part-extra-info'>Part Extra info</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part extra info',
            fields: [
                {
                    title: <Text id='input.url2'>Url 2</Text>,
                    placeholder: <Text id='input.url2'>Url 2</Text>,
                    help: <Text id='input.url2-help'>If you add an extra url to a part, you will be able to try to fetch data from this url.</Text>,
                    name: 'url2',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url2,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.url3'>Url 3</Text>,
                    placeholder: <Text id='input.url3'>Url 3</Text>,
                    help: <Text id='input.url2-help'>If you add an extra url to a part, you will be able to try to fetch data from this url.</Text>,
                    name: 'url3',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url3,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.url4'>Url 4</Text>,
                    placeholder: <Text id='input.url4'>Url 4</Text>,
                    help: <Text id='input.url2-help'>If you add an extra url to a part, you will be able to try to fetch data from this url.</Text>,
                    name: 'url4',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url4,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.url5'>Url 5</Text>,
                    placeholder: <Text id='input.url5'>Url 5</Text>,
                    help: <Text id='input.url2-help'>If you add an extra url to a part, you will be able to try to fetch data from this url.</Text>,
                    name: 'url5',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url5,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.name2'>name2</Text>,
                    placeholder: <Text id='input.name2'>name2</Text>,
                    help: <Text id='input.name2-help'>This name is fetched from the related URL.</Text>,
                    name: 'name2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name2 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.name3'>name3</Text>,
                    placeholder: <Text id='input.name3'>name3</Text>,
                    help: <Text id='input.name2-help'>This name is fetched from the related URL.</Text>,
                    name: 'name3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name3 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.name4'>name4</Text>,
                    placeholder: <Text id='input.name4'>name4</Text>,
                    help: <Text id='input.name2-help'>This name is fetched from the related URL.</Text>,
                    name: 'name4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name4 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.name5'>name5</Text>,
                    placeholder: <Text id='input.name5'>name5</Text>,
                    help: <Text id='input.name2-help'>This name is fetched from the related URL.</Text>,
                    name: 'name5',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name5 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.imageUrl2'>imageUrl2</Text>,
                    placeholder: <Text id='input.imageUrl2'>imageUrl2</Text>,
                    help: <Text id='input.imageUrl2-help'>This image URL is fetched from the related URL.</Text>,
                    name: 'imageUrl2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl2 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.imageUrl3'>imageUrl3</Text>,
                    placeholder: <Text id='input.imageUrl3'>imageUrl3</Text>,
                    help: <Text id='input.imageUrl2-help'>This image URL is fetched from the related URL.</Text>,
                    name: 'imageUrl3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl3 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.imageUrl4'>imageUrl4</Text>,
                    placeholder: <Text id='input.imageUrl4'>imageUrl4</Text>,
                    help: <Text id='input.imageUrl2-help'>This image URL is fetched from the related URL.</Text>,
                    name: 'imageUrl4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl4 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.imageUrl5'>imageUrl5</Text>,
                    placeholder: <Text id='input.imageUrl5'>imageUrl5</Text>,
                    help: <Text id='input.imageUrl2-help'>This image URL is fetched from the related URL.</Text>,
                    name: 'imageUrl5',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl5 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.part-review'>Review</Text>,
                    placeholder: <Text id='input.part-review'>Review</Text>,
                    help: <Text id='input.part-review-help'>Your own notes about this gear: why you bought it, how it works, what you measured. Used as the basis for posts on litt.no.</Text>,
                    name: 'description2',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description2,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.description3'>description3</Text>,
                    placeholder: <Text id='input.description3'>description3</Text>,
                    help: <Text id='input.description2-help'>This description is fetched from the related URL.</Text>,
                    name: 'description3',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description3,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.description4'>description4</Text>,
                    placeholder: <Text id='input.description4'>description4</Text>,
                    help: <Text id='input.description2-help'>This description is fetched from the related URL.</Text>,
                    name: 'description4',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description4,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.description5'>description5</Text>,
                    placeholder: <Text id='input.description5'>description5</Text>,
                    help: <Text id='input.description2-help'>This description is fetched from the related URL.</Text>,
                    name: 'description5',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description3,
                    showInList: false,
                    // hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.part-details-title'>Part details</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part details',
            fields: [
                {
                    title: <Text id='input.category'>category</Text>,
                    placeholder: <Text id='input.category'>category</Text>,
                    help: <Text id='input.category-help'>The main product category.</Text>,
                    name: 'category',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.category || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.gtin13'>gtin13</Text>,
                    placeholder: <Text id='input.gtin13'>gtin13</Text>,
                    help: <Text id='input.gtin13-help'>Global Trade Item Number according to the GTIN13 standard.</Text>,
                    name: 'gtin13',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.gtin13 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.sku'>sku</Text>,
                    placeholder: <Text id='input.sku'>sku</Text>,
                    help: <Text id='input.sku-help'>Internal product number for the shop you bought it from.</Text>,
                    name: 'sku',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.sku || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.mpn'>mpn</Text>,
                    placeholder: <Text id='input.mpn'>mpn</Text>,
                    help: <Text id='input.mpn-help'>MPN stands for Manufacturer Part Number, a unique identifier for a product issued by the manufacturer.</Text>,
                    name: 'mpn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.mpn || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.commodityCode'>commodityCode</Text>,
                    placeholder: <Text id='input.commodityCode'>commodityCode</Text>,
                    help: <Text id='input.commodityCode-help'>Commodity Code is a numeric code for classifying goods in international trade. Used for customs and tariffs.</Text>,
                    name: 'commodityCode',
                    type: 'number',
                    icon: 'fa-solid fa-money-bill',
                    getValue: part => part.commodityCode || null,
                    displayValue: val => val ? val : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.brand'>Brand</Text>,
                    placeholder: <Text id='input.brand'>Brand</Text>,
                    name: 'brand',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.brand || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.model'>Model</Text>,
                    placeholder: <Text id='input.model'>Model</Text>,
                    name: 'model',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.model || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isConsumable'>IsConsumable</Text>,
                    placeholder: <Text id='input.isConsumable'>IsConsumable</Text>,
                    help: <Text id='input.isConsumable-help'>Is this product something you consume in some way. Ie: food, one time batteries, etc.</Text>,
                    name: 'isConsumable',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: part => part.isConsumable ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.serialNumber'>SerialNumber</Text>,
                    placeholder: <Text id='input.serialNumber'>SerialNumber</Text>,
                    name: 'serialNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.serialNumber || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.caliber'>Caliber</Text>,
                    placeholder: <Text id='input.caliber-placeholder'>9mm, .22 LR, .308 Win</Text>,
                    name: 'caliber',
                    type: 'text',
                    icon: 'fa-solid fa-ruler',
                    getValue: part => part.caliber || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.regNumber'>RegNumber</Text>,
                    placeholder: <Text id='input.regNumber'>RegNumber</Text>,
                    name: 'regNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.regNumber || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.partNumber'>PartNumber</Text>,
                    placeholder: <Text id='input.partNumber'>PartNumber</Text>,
                    name: 'partNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.partNumber || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.purchaseDate'>PurchaseDate</Text>,
                    placeholder: <Text id='input.purchaseDate'>PurchaseDate</Text>,
                    name: 'purchaseDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: part => part.purchaseDate || null,
                    displayValue: val => val ? util.formatDate(val, { locale: 'nb-NO' }) : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.currency'>currency</Text>,
                    placeholder: <Text id='input.currency'>currency</Text>,
                    name: 'currency',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.currency || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-money-bill',
                    getValue: part => part.price || null,
                    displayValue: val => val ? util.format(val, 0, ',') : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.latestPrice'>latestPrice</Text>,
                    placeholder: <Text id='input.latestPrice'>latestPrice</Text>,
                    name: 'latestPrice',
                    type: 'number',
                    icon: 'fa-solid fa-money-bill',
                    getValue: part => part.latestPrice || null,
                    displayValue: val => val ? util.format(val, 0, ',') : null,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.part-rating-title'>Part rating</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part rating',
            fields: [
                {
                    title: <Text id='input.rating'>rating</Text>,
                    placeholder: <Text id='input.rating'>rating</Text>,
                    name: 'rating',
                    type: 'number',
                    icon: 'fa-solid fa-star',
                    getValue: part => part.rating || null,
                    displayValue: val => val ? util.format(val, 1, ',') : null,
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.ratingCount'>ratingCount</Text>,
                    placeholder: <Text id='input.ratingCount'>ratingCount</Text>,
                    name: 'ratingCount',
                    type: 'number',
                    icon: 'fa-solid fa-star-exclamation',
                    getValue: part => part.ratingCount || null,
                    displayValue: val => val ? util.format(val, 0, ',') : null,
                    showInList: true,
                    isReadOnly: true,
                },
            ],
        },
        {
            name: <Text id='input.part-usage-title'>Part usage</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part usage',
            fields: [
                {
                    title: <Text id='input.requireInspection'>RequireInspection</Text>,
                    placeholder: <Text id='input.requireInspection'>RequireInspection</Text>,
                    name: 'requireInspection',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: part => part.requireInspection ? 1 : 0,
                    showInList: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.serviceDate'>ServiceDate</Text>,
                    placeholder: <Text id='input.serviceDate'>ServiceDate</Text>,
                    name: 'serviceDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: part => part.serviceDate || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.totalDistance'>TotalDistance</Text>,
                    placeholder: <Text id='input.totalDistance'>TotalDistance</Text>,
                    name: 'totalDistance',
                    type: 'number',
                    icon: 'fa-solid fa-road',
                    getValue: part => part.totalDistance || null,
                    showInList: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.totalHours'>TotalHours</Text>,
                    placeholder: <Text id='input.totalHours'>TotalHours</Text>,
                    name: 'totalHours',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: part => part.totalHours || null,
                    showInList: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.inspections'>Inspections</Text>,
                    placeholder: <Text id='input.inspections'>Inspections</Text>,
                    name: 'inspections',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-user-magnifying-glass',
                    getValue: part => part.inspections?.map(yt => yt),
                    datalist: [
                        'image', 'check1', 'check2', 'check3', 'check4', 'check5'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '100%',
                    displayHeight: '200px',
                    // hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.part-owner-title'>Part owner</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part owner',
            lookupArray: 'loaners',
            foundListName: 'foundLoaners',
            foundListText: 'Found similar owners',
            foundListDisplayKeys: ['firstname', 'lastname', 'cellphone', 'email'],
            foundListReplaceKeys: [
                ['firstname', 'ownerFirstname'],
                ['lastname', 'ownerLastname'],
                ['cellphone', 'ownerCellphone'],
                ['email', 'ownerEmail'],
            ],
            // foundListKeysName: 'foundLoanersKeys',
            fields: [
                {
                    title: <Text id='input.ownedByOther'>OwnedByOther</Text>,
                    placeholder: <Text id='input.ownedByOther'>OwnedByOther</Text>,
                    name: 'ownedByOther',
                    type: 'toggle',
                    icon: 'fa-solid fa-house-user',
                    getValue: part => part.ownedByOther ? 1 : 0,
                    showInList: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.ownerFirstname'>OwnerFirstname</Text>,
                    placeholder: <Text id='input.ownerFirstname'>OwnerFirstname</Text>,
                    name: 'ownerFirstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.ownerFirstname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ownerLastname'>OwnerLastname</Text>,
                    placeholder: <Text id='input.ownerLastname'>OwnerLastname</Text>,
                    name: 'ownerLastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.ownerLastname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ownerCellphone'>OwnerCellphone</Text>,
                    placeholder: <Text id='input.ownerCellphone'>OwnerCellphone</Text>,
                    name: 'ownerCellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: part => part.ownerCellphone || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ownerEmail'>OwnerEmail</Text>,
                    placeholder: <Text id='input.ownerEmail'>OwnerEmail</Text>,
                    name: 'ownerEmail',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: part => part.ownerEmail || null,
                    showInList: true,
                },
            ],
        },
    ],
    inspection: [
        {
            name: <Text id='input.inspection-basics-title'>Inspection basics</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Inspection basics',
            fields: [
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: inspection => inspection.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: inspection => inspection.name || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: inspection => inspection.description,
                    showInList: false,
                },
                {
                    title: <Text id='input.installation'>Installation</Text>,
                    placeholder: <Text id='input.installation'>Installation</Text>,
                    name: 'installation',
                    type: 'number',
                    lookupArray: 'adminInstallations',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: inspection => inspection.installation,
                    showInList: true,
                },
                {
                    title: <Text id='input.score'>Score</Text>,
                    placeholder: <Text id='input.score'>Score</Text>,
                    name: 'score',
                    type: 'number',
                    min: 1,
                    max: 6,
                    icon: 'fa-solid fa-input-numeric',
                    getValue: inspection => inspection.score,
                    showInList: true,
                },
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: inspection => inspection.url,
                    showInList: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: inspection => inspection.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: inspection => inspection.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.inspections'>Inspections</Text>,
                    placeholder: <Text id='input.inspections'>Inspections</Text>,
                    name: 'inspections',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-user-magnifying-glass',
                    getValue: inspection => inspection.inspections?.map(yt => yt),
                    datalist: [
                        'name', 'value', 'description', 'date',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '100%',
                    displayHeight: '100px',
                },
            ],
        },
    ],
    partLoan: [
        {
            name: <Text id='input.part-loan-basics-title'>Friend basics</Text>,
            help: <Text id='input.part-loan-basics-help'>The loaner is the person who is borrowing the part. Enter all the details of the person borrowing the part.</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Friend basics',
            fields: [
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: loan => loan.qty || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: loan => loan.firstname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: loan => loan.lastname,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: loan => loan.cellphone,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: loan => loan.images,
                    showInList: false,
                },
            ],
        },
    ],
    loan: [
        {
            name: <Text id='input.loan-basics-title'>Loan basics</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Loan basics',
            fields: [
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.loaner'>Loaner</Text>,
                    placeholder: <Text id='input.loaner'>Loaner</Text>,
                    name: 'loaner',
                    type: 'number',
                    lookupArray: 'adminLoaners',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.loaner || null,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.part'>Part</Text>,
                    placeholder: <Text id='input.part'>Part</Text>,
                    name: 'part',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    lookupArray: 'adminParts',
                    getValue: obj => obj.part || null,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.installation'>Installation</Text>,
                    placeholder: <Text id='input.installation'>Installation</Text>,
                    name: 'installation',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.installation || null,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.qty || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name || null,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.description,
                    showInList: false,
                },
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.comment,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.loan-loaner-title'>Loan loaner</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Loan loaner',
            fields: [
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.firstname,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.lastname,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.loan-info-title'>Loan info</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Loan info',
            fields: [
                {
                    title: <Text id='input.requiredReturnDate'>RequiredReturnDate</Text>,
                    placeholder: <Text id='input.requiredReturnDate'>RequiredReturnDate</Text>,
                    name: 'requiredReturnDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.requiredReturnDate,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.startDate'>StartDate</Text>,
                    placeholder: <Text id='input.startDate'>StartDate</Text>,
                    name: 'startDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.startDate,
                    showInList: true,
                    hideOnCreate: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.endDate,
                    minFunc: obj => obj.startDate ? util.isoDate(obj.startDate, { dateTimeSep: 'T' }) : null,
                    showInList: false,
                    hideOnCreate: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.loan-media-title'>Loan media</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Loan media',
            fields: [
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.url,
                    showInList: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    article: [
        {
            name: <Text id='input.article-basics-title'>Article basics</Text>,
            key: 'Article basics',
            fields: [
                {
                    lang: { en: 'Id', no: 'Id' },
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    lang: { en: 'Offline', no: 'Avpublisert' },
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    // help: <Text id='input.offline-help'>Set to 1 to delete the article</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                },
                {
                    title: <Text id='input.sort'>Sort</Text>,
                    placeholder: <Text id='input.sort'>Sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sort,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.race'>Race</Text>,
                //     placeholder: <Text id='input.race'>Race</Text>,
                //     name: 'race',
                //     type: 'number',
                //     icon: 'fa-solid fa-input-numeric',
                //     getValue: obj => obj.race,
                //     showInList: true,
                //     isAdminField: true,
                // },

                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.category'>category</Text>,
                    placeholder: <Text id='input.category'>category</Text>,
                    help: <Text id='input.category-help'>Category is used to group articles together</Text>,
                    name: 'category',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.category,
                    showInList: true,
                },
                {
                    title: <Text id='input.pinToTop'>Pin to Top</Text>,
                    placeholder: <Text id='input.pinToTop'>Pin to Top</Text>,
                    name: 'pinToTop',
                    type: 'toggle',
                    icon: 'fa-solid fa-thumbtack',
                    getValue: obj => obj.pinToTop || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.forRaceAdmins'>For Race Admins Only</Text>,
                    placeholder: <Text id='input.forRaceAdmins'>For Race Admins Only</Text>,
                    name: 'forRaceAdmins',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-shield',
                    getValue: obj => obj.forRaceAdmins || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.forCustomerAdmins'>For Customer Admins Only</Text>,
                    placeholder: <Text id='input.forCustomerAdmins'>For Customer Admins Only</Text>,
                    help: <Text id='input.forCustomerAdmins-help'>If on, this article is only shown to customer admins (e.g. help for admin-only features).</Text>,
                    name: 'forCustomerAdmins',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-shield',
                    getValue: obj => obj.forCustomerAdmins || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.requiresMembership'>Members only (Pluss)</Text>,
                    placeholder: <Text id='input.requiresMembership'>Members only (Pluss)</Text>,
                    help: <Text id='input.requiresMembership-help'>If on, only members with an active subscription read the full article. Others see a teaser and a "Bli medlem" call-to-action. Add the paywall marker in the body to set where the free preview ends.</Text>,
                    name: 'requiresMembership',
                    type: 'toggle',
                    icon: 'fa-solid fa-lock',
                    getValue: obj => obj.requiresMembership || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.headingIcon'>headingIcon</Text>,
                    placeholder: <Text id='input.headingIcon'>headingIcon</Text>,
                    name: 'headingIcon',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.headingIcon,
                    showInList: true,
                },
                {
                    title: <Text id='input.heading'>heading</Text>,
                    placeholder: <Text id='input.heading'>heading</Text>,
                    name: 'heading',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.heading,
                    showInList: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.videos'>Videos</Text>,
                    placeholder: <Text id='input.videos'>Videos</Text>,
                    name: 'videoCount',
                    type: 'custom',
                    icon: 'fa-solid fa-video',
                    getValue: obj => {
                        if (!obj.files || !Array.isArray(obj.files)) return 0;
                        return obj.files.filter(f => f.mimetype && f.mimetype.startsWith('video/')).length;
                    },
                    displayValue: obj => {
                        if (!obj.files || !Array.isArray(obj.files)) return '0';
                        const videoCount = obj.files.filter(f => f.mimetype && f.mimetype.startsWith('video/')).length;
                        if (videoCount === 0) return '';
                        return <span class='badge bg-secondary'>{videoCount}</span>;
                    },
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.transcribed'>Transcribed</Text>,
                    placeholder: <Text id='input.transcribed'>Transcribed</Text>,
                    name: 'transcriptionStatus',
                    type: 'custom',
                    icon: 'fa-solid fa-closed-captioning',
                    getValue: obj => {
                        if (!obj.files || !Array.isArray(obj.files)) return false;
                        const videos = obj.files.filter(f => f.mimetype && f.mimetype.startsWith('video/'));
                        if (videos.length === 0) return false;
                        const transcribedCount = videos.filter(v => v.transcript && v.transcript.length > 0).length;
                        return transcribedCount === videos.length;
                    },
                    displayValue: obj => {
                        if (!obj.files || !Array.isArray(obj.files)) return '';
                        const videos = obj.files.filter(f => f.mimetype && f.mimetype.startsWith('video/'));
                        if (videos.length === 0) return '';
                        const transcribedCount = videos.filter(v => v.transcript && v.transcript.length > 0).length;
                        if (transcribedCount === videos.length) {
                            return <span class='text-success'><i class='fa-solid fa-check-circle' /></span>;
                        } else if (transcribedCount > 0) {
                            return <span class='text-warning' title={`${transcribedCount}/${videos.length} transcribed`}><i class='fa-solid fa-exclamation-circle' /></span>;
                        } else {
                            return <span class='text-muted'><i class='fa-solid fa-circle' /></span>;
                        }
                    },
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.english'>English</Text>,
                    placeholder: <Text id='input.english'>English</Text>,
                    name: 'englishTranslationStatus',
                    type: 'custom',
                    icon: 'fa-solid fa-language',
                    getValue: obj => {
                        if (!obj.files || !Array.isArray(obj.files)) return false;
                        const videos = obj.files.filter(f => f.mimetype && f.mimetype.startsWith('video/') && f.transcript);
                        if (videos.length === 0) return false;
                        const translatedCount = videos.filter(v => v.transcriptEnglish && v.transcriptEnglish.length > 0).length;
                        return translatedCount === videos.length;
                    },
                    displayValue: obj => {
                        if (!obj.files || !Array.isArray(obj.files)) return '';
                        const videos = obj.files.filter(f => f.mimetype && f.mimetype.startsWith('video/') && f.transcript);
                        if (videos.length === 0) return '';
                        const translatedCount = videos.filter(v => v.transcriptEnglish && v.transcriptEnglish.length > 0).length;
                        if (translatedCount === videos.length) {
                            return <span class='text-success'><i class='fa-solid fa-check-circle' /></span>;
                        } else if (translatedCount > 0) {
                            return <span class='text-warning' title={`${translatedCount}/${videos.length} translated`}><i class='fa-solid fa-exclamation-circle' /></span>;
                        } else {
                            return <span class='text-muted'><i class='fa-solid fa-circle' /></span>;
                        }
                    },
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Article ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                    aiPost: true,
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Article text</Text>,
                    name: 'body',
                    type: 'textareaEditor',
                    audioTranscribe: true,
                    viewHtml: false,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.body,
                    displayValue: obj => obj.body,
                    showInList: false,
                    aiPost: true,
                },
                {
                    title: 'TL;DR',
                    placeholder: 'AI-generert oppsummering',
                    name: 'tldr',
                    type: 'textarea',
                    icon: 'fa-solid fa-robot',
                    getValue: obj => obj.tldr || '',
                    showInList: false,
                },
                {
                    title: <Text id='input.factSource'>Source (Kilde)</Text>,
                    placeholder: <Text id='input.factSource-placeholder'>e.g. Statens vegvesen</Text>,
                    help: <Text id='input.factSource-help'>Shown as "Kilde: ..." at the bottom of the factbox.</Text>,
                    name: 'factSource',
                    type: 'text',
                    icon: 'fa-solid fa-quote-right',
                    getValue: obj => obj.factSource || '',
                    showInList: false,
                },
                {
                    title: <Text id='input.inlineImages'>inlineImages</Text>,
                    placeholder: <Text id='input.inlineImages'>inlineImages</Text>,
                    name: 'inlineImages',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.inlineImages,
                    showInList: false,
                },
                {
                    title: <Text id='input.youtube'>youtube</Text>,
                    placeholder: <Text id='input.youtube'>youtube</Text>,
                    name: 'youtube',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.youtube,
                    showInList: true,
                },
                {
                    title: <Text id='input.spotify'>spotify</Text>,
                    placeholder: <Text id='input.spotify'>spotify</Text>,
                    name: 'spotify',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.spotify,
                    showInList: true,
                },
                {
                    title: <Text id='input.googleSheetId'>googleSheetId</Text>,
                    placeholder: <Text id='input.googleSheetId'>googleSheetId</Text>,
                    name: 'googleSheetId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.googleSheetId,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.article-meta-title'>Article meta</Text>,
            key: 'Article meta',
            fields: [
                // {
                //     title: <Text id='input.location'>location</Text>,
                //     placeholder: <Text id='input.location'>location</Text>,
                //     name: 'location',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.location,
                //     showInList: true
                // },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.publishDate'>publishDate</Text>,
                    placeholder: <Text id='input.publishDate'>publishDate</Text>,
                    name: 'publishDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.publishDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.expireDate'>expireDate</Text>,
                    placeholder: <Text id='input.expireDate'>expireDate</Text>,
                    name: 'expireDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.expireDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.styles'>styles</Text>,
                    placeholder: <Text id='input.styles'>Article styles</Text>,
                    name: 'styles',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.styles,
                    showInList: false,
                },
                {
                    title: <Text id='input.price'>price</Text>,
                    placeholder: <Text id='input.price'>price</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.price || null,
                    showInList: true,
                    // isMarketPlace: true,
                },
                {
                    title: <Text id='input.isSold'>isSold</Text>,
                    placeholder: <Text id='input.isSold'>isSold</Text>,
                    name: 'isSold',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isSold || null,
                    showInList: true,
                    // isMarketPlace: true,
                },
                {
                    title: 'Autoplay Video',
                    placeholder: 'Autoplay Video',
                    name: 'autoplayVideo',
                    type: 'toggle',
                    icon: 'fa-solid fa-video',
                    getValue: obj => obj.autoplayVideo || null,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.article-sponsors-title'>Article sponsors</Text>,
            help: <Text id='input.article-sponsors-help'>Sponsor logos shown as a bar at the bottom of the autoplay video. Sponsor meta is matched by position: first logo = first meta row.</Text>,
            key: 'Article sponsors',
            fields: [
                {
                    title: <Text id='input.sponsors'>Sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>Sponsors</Text>,
                    name: 'sponsors',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                    addToImages: false,
                },
                {
                    title: <Text id='input.sponsorsMeta'>Sponsors meta</Text>,
                    placeholder: <Text id='input.sponsorsMeta'>Sponsors meta</Text>,
                    name: 'sponsorsMeta',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.sponsorsMeta,
                    datalist: [
                        'url', 'name',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>url:</span> {obj.url}<br />
                            <span class='font-weight-lighter'>name:</span> {obj.name}<br />
                        </div>
                    </>,
                    showInList: false,
                    displayHeight: '80px',
                    displayWidth: '300px',
                },
            ],
        },
        {
            name: <Text id='input.article-content-title'>Article content</Text>,
            key: 'Article content',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-upload-help'>Add one or more images. Images takes some seconds to prepare for web view. Please wait until it is ready.</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        // console.log('fields.callback', { obj, updateValue });
                        if (obj.images?.length > 0) {
                            const img = obj.images[0];
                            if (img?.exif?.image?.Artist) {
                                updateValue({
                                    name: 'attribution',
                                    value: img?.exif?.image?.Artist,
                                });
                            }
                            if (img?.exif?.exif?.DateTimeOriginal) {
                                updateValue({
                                    name: 'whenIsTheMediaRecorded',
                                    value: img?.exif?.exif?.DateTimeOriginal,
                                });
                            }
                        }
                    },
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.videos'>Videos</Text>,
                    placeholder: <Text id='input.videos'>Videos</Text>,
                    help: <Text id='input.videos-help'>Add a video to the article. Videos will take a couple of minutes to process.</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                    // isSimpleEdit: true,
                },
                // {
                //     title: <Text id='input.links'>Links</Text>,
                //     placeholder: <Text id='input.links'>Links</Text>,
                //     name: 'links',
                //     type: 'arrayObject',
                //     icon: 'fa-solid fa-tag',
                //     getValue: obj => obj.links,
                //     datalist: [
                //         'url', 'name', 'description'
                //     ],
                //     showInList: false,
                //     displayHeight: '60px',
                //     hideOnCreate: false,
                // },
            ],
        },
        {
            name: <Text id='input.article-simple-title'>Your content</Text>,
            key: 'Article simple',
            showSectionName: false,
            marginTop: 'mt-1',
            fields: [
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                    isSimpleEdit: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.ingress'>Article ingress</Text>,
                    placeholder: <Text id='input.ingress-what-is-on-your-mind'>What is on your mind,</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    audioTranscribe: true,
                    // icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    // onInputcallbackField: (id, name, value) => {
                    //     // Handle input changes for the ingress field
                    //     console.log(`Input changed in field ${name} with id ${id}:`, value);
                    // },
                    callback: async (obj, updateValue, holdingStore, value) => {
                        // console.log('fields.callback', { obj, updateValue, value });
                        if (obj.url) {
                            return false; // Prevent default callback if URL is present
                        }
                        // Use the passed value parameter (current text) instead of obj.ingress
                        // because obj.ingress might not be updated yet due to MobX timing
                        const currentText = value || obj.ingress || '';
                        // look for a URL terminated by whitespace
                        const regex = /https?:\/\/[^\s]+(?=\s)/;
                        const match = currentText.match(regex);
                        if (match && !obj.url) {
                            const foundUrl = match[0];
                            // Pass updated obj with current text for getSpiderUrl
                            getSpiderUrl({ ...obj, ingress: currentText }, updateValue, foundUrl);
                        }

                        // Look for tags terminated by whitespace
                        const tagRegex = /#(\w+)(?=\s)/g;
                        const tags = [];
                        let tagMatch;
                        while ((tagMatch = tagRegex.exec(currentText)) !== null) {
                            tags.push(tagMatch[1]);
                        }
                        if (tags.length > 0) {
                            addTagsIfMissing(obj, updateValue, tags);
                        }
                    },
                    displayInputInfo: (obj) => {
                        if (!obj.url) return null;
                        return (<>
                            <div class='d-flex bg-light position-relative' style='width: 75vw; max-width: 850px;'>
                                {obj.urlImageUrl && <div
                                    class='w-20 text-center imageContainer d-flex justify-content-center align-items-center overflow-hidden'
                                    style={`
                                        max-height: 100px;
                                        width: 100px;
                                        min-width: 100px;
                                    `}
                                >
                                    <img
                                        src={`${obj.urlImageUrl}`}
                                        class='img-fluid'
                                        style={`
                                            object-fit: cover;
                                        `}
                                    />
                                </div>}

                                <div class='flex-fill d-flex flex-column  border-bottom p-2 overflow-hidden'>
                                    <div class='d-flex flex-column'>
                                        <div class='d-flex overflow-hidden'>
                                            <small class='text-muted'>
                                                <a href={obj.url} class='stretched-link text-dark' target='_blank' rel='noopener noreferrer'>
                                                    {obj.urlDomain}
                                                </a>
                                            </small>
                                        </div>
                                        <div class='d-flex text-truncate'>
                                            <small class='font-weight-bold'>
                                                {obj.urlTitle}
                                            </small>
                                        </div>
                                        <div class='d-flex text-truncate'>
                                            <small class='font-weight-light'>
                                                {obj.urlDescription}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>);
                    },
                    showInList: false,
                    isSimpleEdit: true,
                    addNameAfterFieldname: true,
                    showEmojis: true,
                    showBackgroundSelector: true,
                    backgroundColorOptions: BACKGROUND_PICKER_LIST,
                    aiPost: true,
                },

                // {
                //     title: <Text id='input.url'>Url</Text>,
                //     placeholder: <Text id='input.url'>Url</Text>,
                //     help: <Text id='input.url-help'>If you add a url to a part, the system will try to fetch the data from the url.</Text>,
                //     name: 'url',
                //     type: 'text',
                //     icon: 'fa-solid fa-link',
                //     getValue: part => part.url,
                //     displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                //     callback: async (part, updateValue) => {
                //         if (part.url && part.url.startsWith('http')) {
                //             const response = await spiderPage(part.url);
                //             //
                //             if (response) {
                //                 updateValue({
                //                     name: 'name',
                //                     value: response.title,
                //                 });
                //                 updateValue({
                //                     name: 'description',
                //                     value: response.description,
                //                 });
                //                 updateValue({
                //                     name: 'imageUrl',
                //                     value: response.image || response.icon,
                //                 });
                //                 updateValue({
                //                     name: 'brand',
                //                     value: response.product?.brand?.name,
                //                 });
                //                 updateValue({
                //                     name: 'category',
                //                     value: response.product?.category,
                //                 });
                //                 updateValue({
                //                     name: 'sku',
                //                     value: response.product?.sku,
                //                 });
                //                 updateValue({
                //                     name: 'mpn',
                //                     value: response.product?.mpn,
                //                 });
                //                 updateValue({
                //                     name: 'gtin13',
                //                     value: response.product?.gtin13,
                //                 });
                //                 updateValue({
                //                     name: 'price',
                //                     value: response.product?.offers?.price,
                //                 });
                //                 updateValue({
                //                     name: 'rating',
                //                     value: response.product?.aggregateRating?.ratingValue,
                //                 });
                //                 updateValue({
                //                     name: 'ratingCount',
                //                     value: response.product?.aggregateRating?.reviewCount,
                //                 });
                //             }
                //         }
                //     },
                //     showInList: false,
                //     // hideOnCreate: true,
                // },

                {
                    title: <Text id='input.background'>background</Text>,
                    placeholder: <Text id='input.background'>background</Text>,
                    name: 'background',
                    type: 'colorPicker',
                    datalist: COLOR_PICKER_LIST,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.background,
                    // isSimpleEdit: true, // Hidden in simple feed edit mode
                },
                {
                    title: <Text id='input.backgroundImage'>backgroundImage</Text>,
                    placeholder: <Text id='input.backgroundImage'>backgroundImage</Text>,
                    name: 'backgroundImage',
                    type: 'select',
                    datalist: GRADIENT_PICKER_LIST,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.backgroundImage,
                    // isSimpleEdit: true, // Hidden in simple feed edit mode
                    showBackgroundImage: true,
                },
                {
                    title: <Text id='article.backgroundTextEffect'>Text effect</Text>,
                    placeholder: <Text id='article.backgroundTextEffect'>Text effect</Text>,
                    name: 'backgroundTextEffect',
                    type: 'select',
                    datalist: BACKGROUND_TEXT_EFFECTS.map(effect => [
                        effect.key,
                        <Text id={effect.textKey}>{effect.label}</Text>,
                    ]),
                    icon: 'fa-solid fa-wand-magic-sparkles',
                    getValue: obj => obj.backgroundTextEffect || 'outline',
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='article.backgroundTextStyle'>Text style</Text>,
                    placeholder: <Text id='article.backgroundTextStyle'>Text style</Text>,
                    name: 'backgroundTextStyle',
                    type: 'select',
                    datalist: BACKGROUND_TEXT_STYLES.map(style => [
                        style.key,
                        <Text id={style.textKey}>{style.label}</Text>,
                    ]),
                    icon: 'fa-solid fa-palette',
                    getValue: obj => obj.backgroundTextStyle || 'auto',
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='article.backgroundTextAnimation'>Text animation</Text>,
                    placeholder: <Text id='article.backgroundTextAnimation'>Text animation</Text>,
                    name: 'backgroundTextAnimation',
                    type: 'select',
                    datalist: BACKGROUND_TEXT_ANIMATIONS.map(animation => [
                        animation.key,
                        <Text id={animation.textKey}>{animation.label}</Text>,
                    ]),
                    icon: 'fa-solid fa-wave-square',
                    getValue: obj => obj.backgroundTextAnimation || 'none',
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='article.backgroundDecoration'>Decoration</Text>,
                    placeholder: <Text id='article.backgroundDecoration'>Decoration</Text>,
                    name: 'backgroundDecoration',
                    type: 'select',
                    datalist: BACKGROUND_DECORATIONS.map(decoration => [
                        decoration.key,
                        <Text id={decoration.textKey}>{decoration.label}</Text>,
                    ]),
                    icon: 'fa-solid fa-icons',
                    getValue: obj => obj.backgroundDecoration || 'none',
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='article.backgroundMotion'>Background motion</Text>,
                    placeholder: <Text id='article.backgroundMotion'>Background motion</Text>,
                    name: 'backgroundMotion',
                    type: 'select',
                    datalist: BACKGROUND_MOTIONS.map(motion => [
                        motion.key,
                        <Text id={motion.textKey}>{motion.label}</Text>,
                    ]),
                    icon: 'fa-solid fa-wand-magic-sparkles',
                    getValue: obj => obj.backgroundMotion || 'none',
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='article.imageOverlay'>Image effect</Text>,
                    placeholder: <Text id='article.imageOverlay'>Image effect</Text>,
                    name: 'imageOverlay',
                    type: 'select',
                    datalist: IMAGE_OVERLAYS.map(overlay => [
                        overlay.key,
                        <Text id={overlay.textKey}>{overlay.label}</Text>,
                    ]),
                    icon: 'fa-solid fa-wand-magic-sparkles',
                    getValue: obj => obj.imageOverlay || 'none',
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='article.imageOverlayLocationLabel'>Location sticker text</Text>,
                    placeholder: <Text id='article.imageOverlayLocationPlaceholder'>Place name</Text>,
                    name: 'imageOverlayLocation',
                    type: 'text',
                    icon: 'fa-solid fa-location-dot',
                    maxLength: 80,
                    getValue: obj => obj.imageOverlayLocation || '',
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='article.imageFilter'>Image filter</Text>,
                    placeholder: <Text id='article.imageFilter'>Image filter</Text>,
                    name: 'imageFilter',
                    type: 'select',
                    datalist: IMAGE_FILTERS.map(filter => [
                        filter.key,
                        <Text id={filter.textKey}>{filter.label}</Text>,
                    ]),
                    icon: 'fa-solid fa-sliders',
                    getValue: obj => obj.imageFilter || 'none',
                    isSimpleEdit: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-upload-help'>Add one or more images. Images takes some seconds to prepare for web view. Please wait until it is ready.</Text>,
                    name: 'images',
                    type: 'image',
                    displayLargeImages: true,
                    displayWidth: '180px',
                    displayHeight: 'auto',
                    // icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        // console.log('fields.callback', { obj, updateValue });
                        if (obj.images?.length > 0) {
                            const img = obj.images[0];
                            if (img?.exif?.image?.Artist) {
                                updateValue({
                                    name: 'attribution',
                                    value: img?.exif?.image?.Artist,
                                });
                            }
                            if (img?.exif?.exif?.DateTimeOriginal) {
                                updateValue({
                                    name: 'whenIsTheMediaRecorded',
                                    value: img?.exif?.exif?.DateTimeOriginal,
                                });
                            }
                        }
                    },
                    isSimpleEdit: true,
                },
            ],
        },
        {
            name: <Text id='input.article-simple-marketplace-title'>Marketplace content</Text>,
            key: 'Article simple marketplace',
            showSectionName: false,
            marginTop: 'mt-1',
            fields: [
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                    isSimpleEdit: true,
                    isMarketPlace: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.ingress'>Article ingress</Text>,
                    placeholder: <Text id='input.ingress-describe-the-item'>Describe the item you are selling</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    audioTranscribe: true,
                    // icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                    isSimpleEdit: true,
                    isMarketPlace: true,
                    aiPost: true,
                    // addNameAfterFieldname: true,
                },
                {
                    title: <Text id='input.ingressTechDetails'>Article ingress tech details</Text>,
                    placeholder: <Text id='input.ingressTechDetails-describe-the-item'>Technical details about the item you are selling</Text>,
                    name: 'ingressTechDetails',
                    type: 'textarea',
                    // icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingressTechDetails,
                    showInList: false,
                    isSimpleEdit: true,
                    isMarketPlace: true,
                    aiPost: true,
                    // addNameAfterFieldname: true,
                },
                {
                    title: <Text id='input.price'>price</Text>,
                    placeholder: <Text id='input.price'>price</Text>,
                    name: 'price',
                    type: 'number',
                    // icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.price || null,
                    showInList: true,
                    isMarketPlace: true,
                },
                {
                    title: <Text id='input.isSold'>isSold</Text>,
                    placeholder: <Text id='input.isSold'>isSold</Text>,
                    name: 'isSold',
                    type: 'toggle',
                    // icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isSold || null,
                    showInList: true,
                    isMarketPlace: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.marketplaceCategory'>Category</Text>,
                    placeholder: <Text id='input.marketplaceCategory'>Category</Text>,
                    help: <Text id='input.marketplaceCategory-help'>Lets buyers filter the marketplace by type of item.</Text>,
                    name: 'marketplaceCategory',
                    type: 'select',
                    icon: 'fa-duotone fa-tags',
                    datalist: MARKETPLACE_CATEGORIES.map(category => [category.key, category.label]),
                    getValue: obj => obj.marketplaceCategory || '',
                    showInList: true,
                    isMarketPlace: true,
                },
                {
                    title: <Text id='input.marketplaceUrl'>Link to Finn ad</Text>,
                    placeholder: <Text id='input.marketplaceUrl-placeholder'>https://www.finn.no/...</Text>,
                    help: <Text id='input.marketplaceUrl-help'>Paste the link to your ad on Finn.no. Ad text and price are filled in from the ad when they are empty.</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-duotone fa-link',
                    getValue: obj => obj.url || '',
                    showInList: true,
                    isMarketPlace: true,
                    callback: async (obj, updateValue, holdingStore, value) => {
                        // Spider a pasted link: a full http(s) url with a path. Links are pasted, not typed, so this fires once.
                        const link = (value || '').trim();
                        if (!/^https?:\/\/\S+\/\S{4,}$/.test(link)) {
                            return;
                        }
                        const response = await spiderPage(link);
                        if (!response || response.status >= 300) {
                            return;
                        }
                        updateValue({ name: 'urlDomain', value: response.domain || '' });
                        updateValue({ name: 'urlBaseUrl', value: response.baseUrl || '' });
                        updateValue({ name: 'urlTitle', value: response.title || '' });
                        updateValue({ name: 'urlDescription', value: response.description || '' });
                        updateValue({ name: 'urlImageUrl', value: response.image || response.icon || '' });
                        const spideredPrice = parseFloat(response.product?.offers?.price);
                        if (spideredPrice > 0) {
                            updateValue({ name: 'urlPrice', value: spideredPrice });
                        }
                        // Prefill the listing itself only where the seller has written nothing yet.
                        // The marketplace form has no title field: the ad text is the ingress.
                        if (!(obj.ingress || '').trim() && response.title) {
                            updateValue({ name: 'ingress', value: response.title });
                        }
                        if (!(obj.price > 0) && spideredPrice > 0) {
                            updateValue({ name: 'price', value: Math.round(spideredPrice) });
                        }
                    },
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-upload-help'>Add one or more images. Images takes some seconds to prepare for web view. Please wait until it is ready.</Text>,
                    name: 'images',
                    type: 'image',
                    displayLargeImages: true,
                    displayWidth: '180px',
                    displayHeight: 'auto',
                    // icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        // console.log('fields.callback', { obj, updateValue });
                        if (obj.images?.length > 0) {
                            const img = obj.images[0];
                            if (img?.exif?.image?.Artist) {
                                updateValue({
                                    name: 'attribution',
                                    value: img?.exif?.image?.Artist,
                                });
                            }
                            if (img?.exif?.exif?.DateTimeOriginal) {
                                updateValue({
                                    name: 'whenIsTheMediaRecorded',
                                    value: img?.exif?.exif?.DateTimeOriginal,
                                });
                            }
                        }
                    },
                    isSimpleEdit: true,
                    isMarketPlace: true,
                },
                {
                    title: <Text id='input.product-models'>3D model (STL)</Text>,
                    placeholder: <Text id='input.product-models'>3D model (STL)</Text>,
                    help: <Text id='input.product-models-help'>Upload a 3D-print STL file to show an interactive 3D preview. A preview image is generated automatically.</Text>,
                    name: 'models',
                    type: 'file',
                    icon: 'fa-duotone fa-cube',
                    getValue: obj => obj.models,
                    showInList: false,
                    isSimpleEdit: true,
                    isMarketPlace: true,
                },
            ],
        },
        {
            name: <Text id='input.article-simple-video-title'>Video content</Text>,
            key: 'Article simple video',
            isCollapsed: true,
            fields: [
                {
                    title: <Text id='input.videos'>Videos</Text>,
                    placeholder: <Text id='input.videos'>Videos</Text>,
                    help: <Text id='input.videos-help'>Add a video to the article. Videos will take a couple of minutes to process.</Text>,
                    addFileText: <Text id='input.add-video'>Add video</Text>,
                    name: 'files',
                    type: 'file',
                    displayLargeFiles: true,
                    // icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                    isSimpleEdit: true,
                },
            ],
        },
    ],
    fishCatch: [
        {
            key: 'Fish catch',
            showSectionName: false,
            marginTop: 'mt-1',
            fields: [
                {
                    title: <Text id='fishCatch.images'>Photo</Text>,
                    placeholder: <Text id='fishCatch.images-placeholder'>Add a photo of the catch</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-duotone fa-camera',
                    getValue: obj => obj.images || [],
                },
                {
                    title: <Text id='fishCatch.species'>Species</Text>,
                    placeholder: <Text id='fishCatch.species-placeholder'>e.g. trout</Text>,
                    name: 'species',
                    type: 'datalist',
                    icon: 'fa-duotone fa-fish',
                    datalist: FISH_SPECIES,
                    getValue: obj => obj.species || '',
                },
                {
                    title: <Text id='fishCatch.lengthCm'>Length (cm)</Text>,
                    placeholder: <Text id='fishCatch.lengthCm-placeholder'>Length in cm</Text>,
                    name: 'lengthCm',
                    type: 'number',
                    icon: 'fa-duotone fa-ruler',
                    getValue: obj => obj.lengthCm,
                },
                {
                    title: <Text id='fishCatch.weightKg'>Weight (kg)</Text>,
                    placeholder: <Text id='fishCatch.weightKg-placeholder'>Weight in kg</Text>,
                    name: 'weightKg',
                    type: 'number',
                    icon: 'fa-duotone fa-weight-scale',
                    getValue: obj => obj.weightKg,
                },
                {
                    title: <Text id='fishCatch.released'>Released</Text>,
                    placeholder: <Text id='fishCatch.released'>Released</Text>,
                    name: 'released',
                    type: 'toggle',
                    icon: 'fa-duotone fa-arrows-turn-to-dots',
                    getValue: obj => obj.released || false,
                },
                {
                    title: <Text id='fishCatch.method'>Method</Text>,
                    placeholder: <Text id='fishCatch.method-placeholder'>How was it caught</Text>,
                    name: 'method',
                    type: 'select',
                    icon: 'fa-duotone fa-feather',
                    datalist: CATCH_METHODS,
                    getValue: obj => obj.method || '',
                },
                {
                    title: <Text id='fishCatch.fly'>Fly</Text>,
                    placeholder: <Text id='fishCatch.fly-placeholder'>Which fly did you use</Text>,
                    name: 'fly',
                    // 'datalist' (not 'text'): input.js only reads the `datalist` prop
                    // for type 'datalist'/'select' — 'text' renders a bare input with
                    // no `list` attribute. This is how fishCatch/drawer.js's
                    // buildSections augments this field with the user's own previous
                    // fly values from GET /api/articles/fishcatch-values.
                    type: 'datalist',
                    icon: 'fa-duotone fa-feather-pointed',
                    getValue: obj => obj.fly || '',
                },
                {
                    title: <Text id='fishCatch.equipment'>Equipment</Text>,
                    placeholder: <Text id='fishCatch.equipment-placeholder'>Rod, reel, line</Text>,
                    name: 'equipment',
                    // See the 'fly' field above: 'datalist' so drawer.js's
                    // buildSections can populate suggestions from prior catches.
                    type: 'datalist',
                    icon: 'fa-duotone fa-fishing-rod',
                    getValue: obj => obj.equipment || '',
                },
                {
                    title: <Text id='fishCatch.waterType'>Water</Text>,
                    placeholder: <Text id='fishCatch.waterType-placeholder'>River, lake or sea</Text>,
                    name: 'waterType',
                    type: 'select',
                    icon: 'fa-duotone fa-water',
                    datalist: WATER_TYPES,
                    getValue: obj => obj.waterType || '',
                },
                {
                    title: <Text id='fishCatch.waterTemperature'>Water temperature (°C)</Text>,
                    placeholder: <Text id='fishCatch.waterTemperature-placeholder'>Water temperature</Text>,
                    name: 'waterTemperature',
                    type: 'number',
                    icon: 'fa-duotone fa-temperature-water',
                    getValue: obj => obj.waterTemperature,
                },
                {
                    title: <Text id='fishCatch.placeName'>Place</Text>,
                    placeholder: <Text id='fishCatch.placeName-placeholder'>e.g. Gaula, Rognes</Text>,
                    name: 'placeName',
                    // See the 'fly' field above: 'datalist' so drawer.js's
                    // buildSections can populate suggestions from prior catches.
                    type: 'datalist',
                    icon: 'fa-duotone fa-location-dot',
                    getValue: obj => obj.placeName || '',
                },
                {
                    title: <Text id='fishCatch.showExactPosition'>Show exact position</Text>,
                    placeholder: <Text id='fishCatch.showExactPosition'>Show exact position</Text>,
                    help: <Text id='fishCatch.showExactPosition-help'>Off by default. When off, the feed shows only an approximate location.</Text>,
                    name: 'showExactPosition',
                    type: 'toggle',
                    icon: 'fa-duotone fa-map-pin',
                    getValue: obj => obj.showExactPosition || false,
                },
                {
                    title: <Text id='fishCatch.catchDate'>Caught at</Text>,
                    placeholder: <Text id='fishCatch.catchDate'>Caught at</Text>,
                    name: 'catchDate',
                    type: 'datetime-local',
                    icon: 'fa-duotone fa-clock',
                    getValue: obj => obj.catchDate,
                },
                {
                    title: <Text id='fishCatch.notes'>Notes</Text>,
                    placeholder: <Text id='fishCatch.notes-placeholder'>Anything else worth remembering</Text>,
                    name: 'notes',
                    type: 'textarea',
                    icon: 'fa-duotone fa-note',
                    getValue: obj => obj.notes || '',
                },
            ],
        },
    ],
    fishContact: [
        {
            key: 'Fish contact',
            showSectionName: false,
            marginTop: 'mt-1',
            fields: [
                {
                    title: <Text id='fishContact.images'>Photo</Text>,
                    placeholder: <Text id='fishContact.images-placeholder'>Add a photo</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-duotone fa-camera',
                    getValue: obj => obj.images || [],
                },
                {
                    title: <Text id='fishContact.species'>Species</Text>,
                    placeholder: <Text id='fishContact.species-placeholder'>e.g. trout</Text>,
                    name: 'species',
                    type: 'datalist',
                    icon: 'fa-duotone fa-fish',
                    datalist: FISH_SPECIES,
                    getValue: obj => obj.species || '',
                },
                {
                    title: <Text id='fishContact.contactType'>What happened</Text>,
                    placeholder: <Text id='fishContact.contactType-placeholder'>Take, follow, lost or miss</Text>,
                    name: 'contactType',
                    type: 'select',
                    icon: 'fa-duotone fa-fish-hook',
                    datalist: CONTACT_TYPES,
                    getValue: obj => obj.contactType || '',
                },
                {
                    title: <Text id='fishContact.contactCount'>Number of contacts</Text>,
                    placeholder: <Text id='fishContact.contactCount-placeholder'>How many</Text>,
                    help: <Text id='fishContact.contactCount-help'>Log several identical contacts as one entry.</Text>,
                    name: 'contactCount',
                    type: 'number',
                    icon: 'fa-duotone fa-hashtag',
                    getValue: obj => obj.contactCount || 1,
                },
                {
                    title: <Text id='fishContact.estimatedLengthCm'>Estimated length (cm)</Text>,
                    placeholder: <Text id='fishContact.estimatedLengthCm-placeholder'>Estimated length in cm</Text>,
                    name: 'estimatedLengthCm',
                    type: 'number',
                    icon: 'fa-duotone fa-ruler',
                    getValue: obj => obj.estimatedLengthCm || '',
                },
                {
                    title: <Text id='fishContact.estimatedWeightKg'>Estimated weight (kg)</Text>,
                    placeholder: <Text id='fishContact.estimatedWeightKg-placeholder'>Estimated weight in kg</Text>,
                    name: 'estimatedWeightKg',
                    type: 'number',
                    icon: 'fa-duotone fa-weight-scale',
                    getValue: obj => obj.estimatedWeightKg || '',
                },
                {
                    title: <Text id='fishContact.method'>Method</Text>,
                    placeholder: <Text id='fishContact.method-placeholder'>How were you fishing</Text>,
                    name: 'method',
                    type: 'select',
                    icon: 'fa-duotone fa-feather',
                    datalist: CATCH_METHODS,
                    getValue: obj => obj.method || '',
                },
                {
                    title: <Text id='fishContact.fly'>Fly</Text>,
                    placeholder: <Text id='fishContact.fly-placeholder'>Which fly did you use</Text>,
                    name: 'fly',
                    // 'datalist' (not 'text'): input.js only reads the `datalist` prop
                    // for type 'datalist'/'select' — 'text' renders a bare input with
                    // no `list` attribute. This is how entryDrawer.js's buildSections
                    // augments this field with the user's own previous fly values
                    // from GET /api/articles/fishcatch-values.
                    type: 'datalist',
                    icon: 'fa-duotone fa-feather-pointed',
                    getValue: obj => obj.fly || '',
                },
                {
                    title: <Text id='fishContact.equipment'>Equipment</Text>,
                    placeholder: <Text id='fishContact.equipment-placeholder'>Rod, reel, line</Text>,
                    name: 'equipment',
                    // See the 'fly' field above.
                    type: 'datalist',
                    icon: 'fa-duotone fa-fishing-rod',
                    getValue: obj => obj.equipment || '',
                },
                {
                    title: <Text id='fishContact.waterType'>Water</Text>,
                    placeholder: <Text id='fishContact.waterType-placeholder'>River, lake or sea</Text>,
                    name: 'waterType',
                    type: 'select',
                    icon: 'fa-duotone fa-water',
                    datalist: WATER_TYPES,
                    getValue: obj => obj.waterType || '',
                },
                {
                    title: <Text id='fishContact.waterTemperature'>Water temperature (°C)</Text>,
                    placeholder: <Text id='fishContact.waterTemperature-placeholder'>Water temperature</Text>,
                    name: 'waterTemperature',
                    type: 'number',
                    icon: 'fa-duotone fa-temperature-water',
                    getValue: obj => obj.waterTemperature || '',
                },
                {
                    title: <Text id='fishContact.placeName'>Place</Text>,
                    placeholder: <Text id='fishContact.placeName-placeholder'>e.g. Gaula, Rognes</Text>,
                    name: 'placeName',
                    // See the 'fly' field above.
                    type: 'datalist',
                    icon: 'fa-duotone fa-location-dot',
                    getValue: obj => obj.placeName || '',
                },
                {
                    title: <Text id='fishContact.showExactPosition'>Show exact position</Text>,
                    placeholder: <Text id='fishContact.showExactPosition'>Show exact position</Text>,
                    help: <Text id='fishContact.showExactPosition-help'>Off by default. When off, the feed shows only an approximate location.</Text>,
                    name: 'showExactPosition',
                    type: 'toggle',
                    icon: 'fa-duotone fa-map-pin',
                    getValue: obj => obj.showExactPosition || false,
                },
                {
                    title: <Text id='fishContact.contactDate'>Contact at</Text>,
                    placeholder: <Text id='fishContact.contactDate'>Contact at</Text>,
                    name: 'contactDate',
                    type: 'datetime-local',
                    icon: 'fa-duotone fa-clock',
                    getValue: obj => obj.contactDate || '',
                },
                {
                    title: <Text id='fishContact.notes'>Notes</Text>,
                    placeholder: <Text id='fishContact.notes-placeholder'>Anything else worth remembering</Text>,
                    name: 'notes',
                    type: 'textarea',
                    icon: 'fa-duotone fa-note',
                    getValue: obj => obj.notes || '',
                },
            ],
        },
    ],
    // Organiser club of a race, edited by RACE ADMINS on /race/:id/admin/vipps-setup
    // through /api/raceorganisers/:raceId (id = race id). Only the fields that
    // route whitelists are defined here; secrets are write-only (the backend
    // returns <field>Set booleans, never the values).
    raceOrganiser: [
        {
            name: <Text id='input.race-organiser-identity-title'>Organiser</Text>,
            key: 'Organiser identity',
            fields: [
                {
                    title: <Text id='input.orgNumber'>Organisation number</Text>,
                    placeholder: <Text id='input.orgNumber'>Organisation number</Text>,
                    help: <Text id='input.race-organiser-orgNumber-help'>Registered organisation number of the club. Vipps looks for it on the terms page.</Text>,
                    name: 'orgNumber',
                    type: 'text',
                    icon: 'fa-solid fa-building',
                    getValue: obj => obj.orgNumber,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-location-dot',
                    getValue: obj => obj.address,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.postalcode'>Postal code</Text>,
                    placeholder: <Text id='input.postalcode'>Postal code</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-mailbox',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.place'>Place</Text>,
                    placeholder: <Text id='input.place'>Place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-city',
                    getValue: obj => obj.place,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.email'>E-mail</Text>,
                    placeholder: <Text id='input.email'>E-mail</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-at',
                    getValue: obj => obj.email,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.phone'>Phone</Text>,
                    placeholder: <Text id='input.phone'>Phone</Text>,
                    name: 'phone',
                    type: 'text',
                    icon: 'fa-solid fa-phone',
                    getValue: obj => obj.phone,
                    showInList: false,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.race-organiser-terms-title'>Terms of sale</Text>,
            key: 'Organiser terms of sale',
            fields: [
                {
                    title: <Text id='input.race-organiser-vippsTermsOfSale'>Terms of sale (Norwegian)</Text>,
                    placeholder: <Text id='input.race-organiser-vippsTermsOfSale'>Terms of sale (Norwegian)</Text>,
                    help: <Text id='input.race-organiser-vippsTermsOfSale-help'>Markdown. Must cover payment, right of withdrawal, returns and complaints.</Text>,
                    name: 'vippsTermsOfSale',
                    type: 'textarea',
                    icon: 'fa-solid fa-file-contract',
                    getValue: obj => obj.vippsTermsOfSale,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.race-organiser-vippsTermsOfSaleEn'>Terms of sale (English)</Text>,
                    placeholder: <Text id='input.race-organiser-vippsTermsOfSaleEn'>Terms of sale (English)</Text>,
                    name: 'vippsTermsOfSaleEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-file-contract',
                    getValue: obj => obj.vippsTermsOfSaleEn,
                    showInList: false,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.race-organiser-vipps-keys-title'>Vipps API keys</Text>,
            key: 'Organiser Vipps keys',
            fields: [
                {
                    title: <Text id='input.vippsMerchantSerialNumber'>Merchant serial number (MSN)</Text>,
                    placeholder: <Text id='input.vippsMerchantSerialNumber'>Merchant serial number (MSN)</Text>,
                    name: 'vippsMerchantSerialNumber',
                    type: 'text',
                    icon: 'fa-solid fa-hashtag',
                    getValue: obj => obj.vippsMerchantSerialNumber,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    // Write-only: the backend never echoes the stored value.
                    title: <Text id='input.vippsClientId'>Client ID</Text>,
                    placeholder: <Text id='input.vippsClientId'>Client ID</Text>,
                    name: 'vippsClientId',
                    type: 'password',
                    icon: 'fa-solid fa-key',
                    getValue: obj => obj.vippsClientId,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    // Write-only: the backend never echoes the stored value.
                    title: <Text id='input.vippsClientSecret'>Client secret</Text>,
                    placeholder: <Text id='input.vippsClientSecret'>Client secret</Text>,
                    name: 'vippsClientSecret',
                    type: 'password',
                    icon: 'fa-solid fa-key',
                    getValue: obj => obj.vippsClientSecret,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    // Write-only: the backend never echoes the stored value.
                    title: <Text id='input.vippsSubscriptionKey1'>Subscription key (Ocp-Apim-Subscription-Key)</Text>,
                    placeholder: <Text id='input.vippsSubscriptionKey1'>Subscription key (Ocp-Apim-Subscription-Key)</Text>,
                    name: 'vippsSubscriptionKey1',
                    type: 'password',
                    icon: 'fa-solid fa-key',
                    getValue: obj => obj.vippsSubscriptionKey1,
                    showInList: false,
                    isRecommended: true,
                },
            ],
        },
    ],

    race: [
        {
            name: <Text id='input.race-basics-title'>Race basics</Text>,
            key: 'Race basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    styles: 'min-width: 180px;',
                    isRecommended: true,
                },
                {
                    title: <Text id='input.tag'>Tag</Text>,
                    placeholder: <Text id='input.tag'>Tag</Text>,
                    name: 'tag',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.tag,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-config-title'>Race config</Text>,
            key: 'Race config',
            fields: [
                {
                    title: <Text id='input.logo'>logo</Text>,
                    placeholder: <Text id='input.logo'>logo</Text>,
                    name: 'logo',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.logo,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.logoHeight'>logoHeight</Text>,
                    placeholder: <Text id='input.logoHeight'>logoHeight</Text>,
                    name: 'logoHeight',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.logoHeight,
                    showInList: true,
                },
                {
                    title: <Text id='input.logoUrl'>logoUrl</Text>,
                    placeholder: <Text id='input.logoUrl'>logoUrl</Text>,
                    name: 'logoUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.logoUrl,
                    showInList: true,
                },
                {
                    title: <Text id='input.css'>css</Text>,
                    placeholder: <Text id='input.css'>css</Text>,
                    name: 'css',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.css,
                    showInList: false,
                },
                {
                    title: <Text id='input.qr'>Qr</Text>,
                    placeholder: <Text id='input.qr'>Qr</Text>,
                    name: 'qr',
                    type: 'text',
                    icon: 'fa-solid fa-qrcode',
                    getValue: obj => obj.id || null,
                    showQR: true,
                    getQrValue: obj => `${apiServer}/#/race/${obj.id}`,
                    isReadOnly: true,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.hasTracking'>hasTracking</Text>,
                    placeholder: <Text id='input.hasTracking'>hasTracking</Text>,
                    name: 'hasTracking',
                    searchKeys: ['trackerkalender'],
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasTracking,
                    showInList: true,
                },
                {
                    title: <Text id='input.shippingDaysBefore'>Tracker shipping days before</Text>,
                    placeholder: <Text id='input.shippingDaysBefore-placeholder'>3</Text>,
                    help: <Text id='input.shippingDaysBefore-help'>Days before the race start that trackers are reserved for shipping. Empty = 3.</Text>,
                    name: 'shippingDaysBefore',
                    searchKeys: ['trackerkalender'],
                    type: 'number',
                    icon: 'fa-solid fa-truck',
                    getValue: obj => obj.shippingDaysBefore,
                    showInList: false,
                },
                {
                    title: <Text id='input.shippingDaysAfter'>Tracker shipping days after</Text>,
                    placeholder: <Text id='input.shippingDaysAfter-placeholder'>3</Text>,
                    help: <Text id='input.shippingDaysAfter-help'>Days after the race end that trackers are reserved for return shipping. Empty = 3.</Text>,
                    name: 'shippingDaysAfter',
                    searchKeys: ['trackerkalender'],
                    type: 'number',
                    icon: 'fa-solid fa-truck-arrow-right',
                    getValue: obj => obj.shippingDaysAfter,
                    showInList: false,
                },
                {
                    title: <Text id='input.estimatedTrackers'>Estimated trackers</Text>,
                    placeholder: <Text id='input.estimatedTrackers-placeholder'>0</Text>,
                    help: <Text id='input.estimatedTrackers-help'>How many GPS trackers the organiser expects to need. The tracker calendar reserves the higher of this and the signup count.</Text>,
                    name: 'estimatedTrackers',
                    searchKeys: ['trackerkalender'],
                    type: 'number',
                    icon: 'fa-solid fa-satellite-dish',
                    getValue: obj => obj.estimatedTrackers,
                    showInList: false,
                },
                {
                    title: <Text id='input.hasResults'>hasResults</Text>,
                    placeholder: <Text id='input.hasResults'>hasResults</Text>,
                    name: 'hasResults',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasResults,
                    showInList: true,
                },
                {
                    title: <Text id='input.hasNews'>hasNews</Text>,
                    placeholder: <Text id='input.hasNews'>hasNews</Text>,
                    name: 'hasNews',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasNews,
                    showInList: true,
                },
                {
                    title: <Text id='input.hasLiveFeed'>hasLiveFeed</Text>,
                    placeholder: <Text id='input.hasLiveFeed'>hasLiveFeed</Text>,
                    name: 'hasLiveFeed',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasLiveFeed,
                    showInList: true,
                },
                {
                    title: <Text id='input.liveFeedStartDate'>liveFeedStartDate</Text>,
                    placeholder: <Text id='input.liveFeedStartDate'>liveFeedStartDate</Text>,
                    name: 'liveFeedStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.liveFeedStartDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.liveFeedEndDate'>liveFeedEndDate</Text>,
                    placeholder: <Text id='input.liveFeedEndDate'>liveFeedEndDate</Text>,
                    name: 'liveFeedEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.liveFeedEndDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.isCancelled'>isCancelled</Text>,
                    placeholder: <Text id='input.isCancelled'>isCancelled</Text>,
                    name: 'isCancelled',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isCancelled,
                    showInList: true,
                },
                {
                    title: <Text id='input.forcePaymentBeforeSignupApproval'>Require payment before approval</Text>,
                    placeholder: <Text id='input.forcePaymentBeforeSignupApproval'>Require payment before approval</Text>,
                    help: <Text id='input.forcePaymentBeforeSignupApproval-help'>Public signups reserve a spot for a limited time (see the reservation minutes below) and are approved only after verified online payment.</Text>,
                    name: 'forcePaymentBeforeSignupApproval',
                    type: 'toggle',
                    icon: 'fa-solid fa-credit-card',
                    getValue: obj => obj.forcePaymentBeforeSignupApproval,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupPaymentReservationMinutes'>Payment reservation (minutes)</Text>,
                    placeholder: <Text id='input.signupPaymentReservationMinutes-placeholder'>20</Text>,
                    help: <Text id='input.signupPaymentReservationMinutes-help'>How many minutes a signup keeps its spot while waiting for online payment when payment is required before approval. Empty = 20. Minimum 5, maximum 1440 (24 hours).</Text>,
                    name: 'signupPaymentReservationMinutes',
                    type: 'number',
                    icon: 'fa-solid fa-hourglass-half',
                    getValue: obj => obj.signupPaymentReservationMinutes,
                    showInList: false,
                },
                {
                    title: <Text id='input.closeSignupWhenClassIsFull'>Close signup when a class is full</Text>,
                    placeholder: <Text id='input.closeSignupWhenClassIsFull'>Close signup when a class is full</Text>,
                    help: <Text id='input.closeSignupWhenClassIsFull-help'>When the last seat in a class with a musher limit is taken, the class signup end date is set to now. Seats freed later are filled by moving contestants in from the waiting-list class.</Text>,
                    name: 'closeSignupWhenClassIsFull',
                    type: 'toggle',
                    icon: 'fa-solid fa-door-closed',
                    getValue: obj => obj.closeSignupWhenClassIsFull,
                    showInList: false,
                    isRecommended: true,
                },
                // Required public signup fields. Firstname, lastname, cellphone and email are
                // always required; these toggles decide the rest (lib/raceSignupRequiredFields.js).
                {
                    title: <Text id='input.signupRequireSportsClub'>Signup requires sports club</Text>,
                    placeholder: <Text id='input.signupRequireSportsClub'>Signup requires sports club</Text>,
                    help: <Text id='input.signupRequireSportsClub-help'>Mushers must fill in their sports club to sign up. Firstname, lastname, cellphone and email are always required.</Text>,
                    name: 'signupRequireSportsClub',
                    type: 'toggle',
                    icon: 'fa-solid fa-people-group',
                    getValue: obj => obj.signupRequireSportsClub,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupRequireKennel'>Signup requires kennel</Text>,
                    placeholder: <Text id='input.signupRequireKennel'>Signup requires kennel</Text>,
                    help: <Text id='input.signupRequireKennel-help'>Mushers must fill in their kennel to sign up.</Text>,
                    name: 'signupRequireKennel',
                    type: 'toggle',
                    icon: 'fa-solid fa-dog',
                    getValue: obj => obj.signupRequireKennel,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupRequireBirth'>Signup requires birth date</Text>,
                    placeholder: <Text id='input.signupRequireBirth'>Signup requires birth date</Text>,
                    help: <Text id='input.signupRequireBirth-help'>Mushers must fill in their birth date to sign up.</Text>,
                    name: 'signupRequireBirth',
                    type: 'toggle',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupRequireBirth,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.juniorMaxAge'>Junior up to and including age</Text>,
                    placeholder: <Text id='input.juniorMaxAge-placeholder'>16</Text>,
                    help: <Text id='input.juniorMaxAge-help'>Mushers who turn at most this age in the calendar year of the race start get a «Junior» badge on the start and result lists. The birth date itself is never shown publicly. Mushers without a birth date get no badge. Empty or 0 = no badge.</Text>,
                    name: 'juniorMaxAge',
                    type: 'number',
                    icon: 'fa-solid fa-child-reaching',
                    getValue: obj => obj.juniorMaxAge,
                    showInList: false,
                },
                {
                    title: <Text id='input.signupRequireCountry'>Signup requires country</Text>,
                    placeholder: <Text id='input.signupRequireCountry'>Signup requires country</Text>,
                    help: <Text id='input.signupRequireCountry-help'>Mushers must fill in their country to sign up.</Text>,
                    name: 'signupRequireCountry',
                    type: 'toggle',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj.signupRequireCountry,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupRequireAddress'>Signup requires address</Text>,
                    placeholder: <Text id='input.signupRequireAddress'>Signup requires address</Text>,
                    help: <Text id='input.signupRequireAddress-help'>Mushers must fill in address, postal code and postal place to sign up.</Text>,
                    name: 'signupRequireAddress',
                    type: 'toggle',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.signupRequireAddress,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupRequireLicenseNumber'>Signup requires license number</Text>,
                    placeholder: <Text id='input.signupRequireLicenseNumber'>Signup requires license number</Text>,
                    help: <Text id='input.signupRequireLicenseNumber-help'>Mushers must fill in their license number to sign up.</Text>,
                    name: 'signupRequireLicenseNumber',
                    type: 'toggle',
                    icon: 'fa-duotone fa-id-card',
                    getValue: obj => obj.signupRequireLicenseNumber,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupRequireQualifying'>Signup requires qualifying</Text>,
                    placeholder: <Text id='input.signupRequireQualifying'>Signup requires qualifying</Text>,
                    help: <Text id='input.signupRequireQualifying-help'>Mushers must describe their qualifying for the race to sign up.</Text>,
                    name: 'signupRequireQualifying',
                    type: 'toggle',
                    icon: 'fa-solid fa-shield-dog',
                    getValue: obj => obj.signupRequireQualifying,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.showResults'>showResults</Text>,
                    placeholder: <Text id='input.showResults'>showResults</Text>,
                    name: 'showResults',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.showResults,
                    showInList: true,
                },
                {
                    title: <Text id='input.showTracking'>showTracking</Text>,
                    placeholder: <Text id='input.showTracking'>showTracking</Text>,
                    name: 'showTracking',
                    type: 'toggle',
                    icon: 'fa-solid fa-map-location-dot',
                    getValue: obj => obj.showTracking,
                    showInList: true,
                },
                {
                    title: <Text id='input.addAutoResultsHidden'>addAutoResultsHidden</Text>,
                    placeholder: <Text id='input.addAutoResultsHidden'>addAutoResultsHidden</Text>,
                    name: 'addAutoResultsHidden',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye-slash',
                    getValue: obj => obj.addAutoResultsHidden,
                    showInList: false,
                },
                {
                    title: <Text id='input.hideResults'>hideResults</Text>,
                    placeholder: <Text id='input.hideResults'>hideResults</Text>,
                    name: 'hideResults',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye-slash',
                    getValue: obj => obj.hideResults,
                    showInList: false,
                },
                {
                    title: <Text id='input.showStreaming'>showStreaming</Text>,
                    placeholder: <Text id='input.showStreaming'>showStreaming</Text>,
                    name: 'showStreaming',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.showStreaming,
                    showInList: false,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.hideFromList'>hide From List</Text>,
                    placeholder: <Text id='input.hideFromList'>hide From List</Text>,
                    name: 'hideFromList',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.hideFromList || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.mergeNewsWithRace'>Merge news with race</Text>,
                    placeholder: <Text id='input.mergeNewsWithRace'>Merge news with race</Text>,
                    name: 'mergeNewsWithRace',
                    type: 'number',
                    icon: 'fa-solid fa-list',
                    getValue: obj => obj.mergeNewsWithRace,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-dates-title'>Race dates</Text>,
            key: 'Race dates',
            fields: [
                {
                    title: <Text id='input.timezone'>Time zone</Text>,
                    placeholder: <Text id='input.timezone'>Time zone</Text>,
                    help: <Text id='input.timezone-help'>The time zone the race runs in. Admin pages show and enter times in this zone. Followers see their own clock plus the race time as a label.</Text>,
                    name: 'timezone',
                    type: 'select',
                    icon: 'fa-solid fa-clock',
                    datalist: TIMEZONE_OPTIONS,
                    getValue: obj => obj.timezone || '',
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.startDate'>StartDate</Text>,
                    placeholder: <Text id='input.startDate'>StartDate</Text>,
                    name: 'startDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.startDate,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.startDateRaceLocal'>Start in race time zone</Text>,
                    name: 'startDateRaceLocal',
                    type: 'text',
                    icon: 'fa-solid fa-clock',
                    isReadOnly: true,
                    conditionFunc: obj => !!raceZone(obj),
                    getValue: obj => raceZoneText(obj.startDate, obj, 'isoDate'),
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.endDate,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.endDateRaceLocal'>End in race time zone</Text>,
                    name: 'endDateRaceLocal',
                    type: 'text',
                    icon: 'fa-solid fa-clock',
                    isReadOnly: true,
                    conditionFunc: obj => !!raceZone(obj),
                    getValue: obj => raceZoneText(obj.endDate, obj, 'isoDate'),
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupStartDate'>SignupStartDate</Text>,
                    placeholder: <Text id='input.signupStartDate'>SignupStartDate</Text>,
                    name: 'signupStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupStartDate,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupStartDateRaceLocal'>Signup opens in race time zone</Text>,
                    name: 'signupStartDateRaceLocal',
                    type: 'text',
                    icon: 'fa-solid fa-clock',
                    isReadOnly: true,
                    conditionFunc: obj => !!raceZone(obj),
                    getValue: obj => raceZoneText(obj.signupStartDate, obj, 'isoDate'),
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupEndDate'>SignupEndDate</Text>,
                    placeholder: <Text id='input.signupEndDate'>SignupEndDate</Text>,
                    name: 'signupEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupEndDate,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupEndDateRaceLocal'>Signup closes in race time zone</Text>,
                    name: 'signupEndDateRaceLocal',
                    type: 'text',
                    icon: 'fa-solid fa-clock',
                    isReadOnly: true,
                    conditionFunc: obj => !!raceZone(obj),
                    getValue: obj => raceZoneText(obj.signupEndDate, obj, 'isoDate'),
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupPaymentDueDate'>Signup Payment Due Date</Text>,
                    placeholder: <Text id='input.signupPaymentDueDate'>Signup Payment Due Date</Text>,
                    name: 'signupPaymentDueDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupPaymentDueDate,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-info-title'>Race Info</Text>,
            key: 'Race Info',
            fields: [
                {
                    title: <Text id='input.tagFacebookUsers'>TagFacebookUsers</Text>,
                    placeholder: <Text id='input.tagFacebookUsers'>TagFacebookUsers</Text>,
                    name: 'tagFacebookUsers',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.tagFacebookUsers,
                    showInList: false,
                },
                {
                    title: <Text id='input.tagInstagramUsers'>TagInstagramUsers</Text>,
                    placeholder: <Text id='input.tagInstagramUsers'>TagInstagramUsers</Text>,
                    name: 'tagInstagramUsers',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.tagInstagramUsers,
                    showInList: false,
                },
                {
                    title: <Text id='input.raceTrackerRace'>RaceTrackerRace</Text>,
                    placeholder: <Text id='input.raceTrackerRace'>RaceTrackerRace</Text>,
                    name: 'raceTrackerRace',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.raceTrackerRace,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Ingress</Text>,
                    help: <Text id='input.ingress-help'>Intro text for this race.</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    help: <Text id='input.body-help'>Main text for this race.</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.body,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.info'>Info</Text>,
                    placeholder: <Text id='input.info'>Info</Text>,
                    help: <Text id='input.info-help'>General info about the race. Can be info about signup, payment, etc.</Text>,
                    name: 'info',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.info,
                    showInList: false,
                },
                {
                    title: <Text id='input.terms'>Terms</Text>,
                    placeholder: <Text id='input.terms'>Terms</Text>,
                    help: <Text id='input.terms-help'>Race terms. Describe the terms for the race and all classes.</Text>,
                    name: 'terms',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.terms,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.trackInfo'>TrackInfo</Text>,
                    placeholder: <Text id='input.trackInfo'>TrackInfo</Text>,
                    help: <Text id='input.trackInfo-help'>Information about the race track. General info and worthy mentions.</Text>,
                    name: 'trackInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    help: <Text id='input.qualifying-help'>Describe the qualifying for this race.</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: false,
                },
                {
                    title: <Text id='input.emergencyInfo'>EmergencyInfo</Text>,
                    placeholder: <Text id='input.emergencyInfo'>EmergencyInfo</Text>,
                    help: <Text id='input.emergencyInfo-help'>Emergency plan and information. Include contact info at the top.</Text>,
                    name: 'emergencyInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.emergencyInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.rules'>Rules</Text>,
                    placeholder: <Text id='input.rules'>Rules</Text>,
                    help: <Text id='input.rules-help'>Rules for this race.</Text>,
                    name: 'rules',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rules,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.declaration'>Declaration</Text>,
                    placeholder: <Text id='input.declaration'>Declaration</Text>,
                    help: <Text id='input.declaration-help'>Musher declaration form.</Text>,
                    name: 'declaration',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.declaration,
                    showInList: false,
                },
                {
                    title: <Text id='input.veterinaryInfo'>VeterinaryInfo</Text>,
                    placeholder: <Text id='input.veterinaryInfo'>VeterinaryInfo</Text>,
                    help: <Text id='input.info-help'>Veterinary information for this race.</Text>,
                    name: 'veterinaryInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veterinaryInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkpointsInfo'>CheckpointsInfo</Text>,
                    placeholder: <Text id='input.checkpointsInfo'>CheckpointsInfo</Text>,
                    help: <Text id='input.checkpointsInfo-help'>Information about all checkpoints.</Text>,
                    name: 'checkpointsInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.checkpointsInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.trailDescription'>TrailDescription</Text>,
                    placeholder: <Text id='input.trailDescription'>TrailDescription</Text>,
                    help: <Text id='input.trailDescription-help'>Not used.</Text>,
                    name: 'trailDescription',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trailDescription,
                    showInList: false,
                },
                {
                    title: <Text id='input.program'>Program</Text>,
                    placeholder: <Text id='input.program'>Program</Text>,
                    help: <Text id='input.program-help'>The program for this race.</Text>,
                    name: 'program',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.program,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.prices'>Prices</Text>,
                    placeholder: <Text id='input.prices'>Prices</Text>,
                    help: <Text id='input.prices-help'>List the prices and diplomas for this race.</Text>,
                    name: 'prices',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.prices,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-info-englist-title'>Race Info English</Text>,
            key: 'Race Info English',
            fields: [
                {
                    title: <Text id='input.bodyEn'>bodyEn</Text>,
                    placeholder: <Text id='input.bodyEn'>bodyEn</Text>,
                    name: 'bodyEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bodyEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.programEn'>programEn</Text>,
                    placeholder: <Text id='input.programEn'>programEn</Text>,
                    name: 'programEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.programEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkpointsInfoEn'>checkpointsInfoEn</Text>,
                    placeholder: <Text id='input.checkpointsInfoEn'>checkpointsInfoEn</Text>,
                    name: 'checkpointsInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.checkpointsInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.termsEn'>termsEn</Text>,
                    placeholder: <Text id='input.termsEn'>termsEn</Text>,
                    name: 'termsEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.termsEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.rulesEn'>rulesEn</Text>,
                    placeholder: <Text id='input.rulesEn'>rulesEn</Text>,
                    name: 'rulesEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rulesEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.veterinaryInfoEn'>veterinaryInfoEn</Text>,
                    placeholder: <Text id='input.veterinaryInfoEn'>veterinaryInfoEn</Text>,
                    name: 'veterinaryInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veterinaryInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.declarationEn'>declarationEn</Text>,
                    placeholder: <Text id='input.declarationEn'>declarationEn</Text>,
                    name: 'declarationEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.declarationEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.emergencyInfoEn'>emergencyInfoEn</Text>,
                    placeholder: <Text id='input.emergencyInfoEn'>emergencyInfoEn</Text>,
                    name: 'emergencyInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.emergencyInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.trackInfoEn'>trackInfoEn</Text>,
                    placeholder: <Text id='input.trackInfoEn'>trackInfoEn</Text>,
                    help: <Text id='input.trackInfoEn-help'>Information about the race track. General info and worthy mentions.</Text>,
                    name: 'trackInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifyingEn'>qualifyingEn</Text>,
                    placeholder: <Text id='input.qualifyingEn'>qualifyingEn</Text>,
                    name: 'qualifyingEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifyingEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.pricesEn'>pricesEn</Text>,
                    placeholder: <Text id='input.pricesEn'>pricesEn</Text>,
                    help: <Text id='input.pricesEn-help'>List the prices and diplomas for this race.</Text>,
                    name: 'pricesEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.pricesEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.vippsTermsOfSaleEn'>vippsTermsOfSaleEn</Text>,
                    placeholder: <Text id='input.vippsTermsOfSaleEn'>vippsTermsOfSaleEn</Text>,
                    help: <Text id='input.vippsTermsOfSaleEn-help'>Terms of sale in english.</Text>,
                    name: 'vippsTermsOfSaleEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTermsOfSaleEn,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-results-title'>Race results info</Text>,
            key: 'Race results info',
            fields: [
                {
                    title: <Text id='input.resultsText'>ResultsText</Text>,
                    placeholder: <Text id='input.resultsText'>ResultsText</Text>,
                    name: 'resultsText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultsText,
                    showInList: false,
                },
                {
                    title: <Text id='input.resultsUrl'>ResultsUrl</Text>,
                    placeholder: <Text id='input.resultsUrl'>ResultsUrl</Text>,
                    name: 'resultsUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultsUrl,
                    showInList: false,
                },
                {
                    title: <Text id='input.resultsHide'>ResultsHide</Text>,
                    placeholder: <Text id='input.resultsHide'>ResultsHide</Text>,
                    name: 'resultsHide',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.resultsHide,
                    showInList: false,
                },
                {
                    title: <Text id='input.contestantsText'>ContestantsText</Text>,
                    placeholder: <Text id='input.contestantsText'>ContestantsText</Text>,
                    name: 'contestantsText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contestantsText,
                    showInList: false,
                },
                {
                    title: <Text id='input.contestantsUrl'>ContestantsUrl</Text>,
                    placeholder: <Text id='input.contestantsUrl'>ContestantsUrl</Text>,
                    name: 'contestantsUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contestantsUrl,
                    showInList: false,
                },
                {
                    title: <Text id='input.resultDanger'>ResultDanger</Text>,
                    placeholder: <Text id='input.resultDanger'>ResultDanger</Text>,
                    name: 'resultDanger',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultDanger,
                    showInList: false,
                },
                {
                    title: <Text id='input.resultWarning'>ResultWarning</Text>,
                    placeholder: <Text id='input.resultWarning'>ResultWarning</Text>,
                    name: 'resultWarning',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultWarning,
                    showInList: true,
                },
                {
                    title: <Text id='input.resultSuccess'>ResultSuccess</Text>,
                    placeholder: <Text id='input.resultSuccess'>ResultSuccess</Text>,
                    name: 'resultSuccess',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultSuccess,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-subscription-title'>Race subscription info</Text>,
            key: 'Race subscription info',
            fields: [
                {
                    title: <Text id='input.isSubscriptionsTracking'>isSubscriptionsTracking</Text>,
                    placeholder: <Text id='input.isSubscriptionsTracking'>isSubscriptionsTracking</Text>,
                    name: 'isSubscriptionsTracking',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSubscriptionsTracking,
                    showInList: false,
                },
                {
                    title: <Text id='input.isSubscriptionsStreaming'>isSubscriptionsStreaming</Text>,
                    placeholder: <Text id='input.isSubscriptionsStreaming'>isSubscriptionsStreaming</Text>,
                    name: 'isSubscriptionsStreaming',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSubscriptionsStreaming,
                    showInList: false,
                },
                {
                    title: <Text id='input.isSubscriptionsNews'>isSubscriptionsNews</Text>,
                    placeholder: <Text id='input.isSubscriptionsNews'>isSubscriptionsNews</Text>,
                    name: 'isSubscriptionsNews',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSubscriptionsNews,
                    showInList: false,
                },
                {
                    title: <Text id='input.isSubscriptionsResults'>isSubscriptionsResults</Text>,
                    placeholder: <Text id='input.isSubscriptionsResults'>isSubscriptionsResults</Text>,
                    name: 'isSubscriptionsResults',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSubscriptionsResults,
                    showInList: false,
                },
                {
                    title: <Text id='input.isAllAllowedToPublishNews'>isAllAllowedToPublishNews</Text>,
                    placeholder: <Text id='input.isAllAllowedToPublishNews'>isAllAllowedToPublishNews</Text>,
                    name: 'isAllAllowedToPublishNews',
                    type: 'toggle',
                    icon: 'fa-solid fa-newspaper',
                    getValue: obj => obj.isAllAllowedToPublishNews,
                    showInList: false,
                },
                {
                    title: <Text id='input.isMediaAllowedToPublishNews'>isMediaAllowedToPublishNews</Text>,
                    placeholder: <Text id='input.isMediaAllowedToPublishNews'>isMediaAllowedToPublishNews</Text>,
                    name: 'isMediaAllowedToPublishNews',
                    type: 'toggle',
                    icon: 'fa-solid fa-camera',
                    getValue: obj => obj.isMediaAllowedToPublishNews,
                    showInList: false,
                },

                {
                    title: <Text id='input.subscriptionTitle'>subscriptionTitle</Text>,
                    placeholder: <Text id='input.subscriptionTitle'>subscriptionTitle</Text>,
                    help: <Text id='input.subscriptionTitle-help'>Title for the top of the subscription page.</Text>,
                    name: 'subscriptionTitle',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionTitle,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBody'>subscriptionBody</Text>,
                    placeholder: <Text id='input.subscriptionBody'>subscriptionBody</Text>,
                    help: <Text id='input.subscriptionBody-help'>Body text for the subscription page.</Text>,
                    name: 'subscriptionBody',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBody,
                    showInList: false,
                },

                {
                    title: <Text id='input.subscriptionBox1Title'>subscriptionBox1Title</Text>,
                    placeholder: <Text id='input.subscriptionBox1Title'>subscriptionBox1Title</Text>,
                    help: <Text id='input.subscriptionBox1Title-help'>Title for 1. subscription box.</Text>,
                    name: 'subscriptionBox1Title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox1Title,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox1Body'>subscriptionBox1Body</Text>,
                    placeholder: <Text id='input.subscriptionBox1Body'>subscriptionBox1Body</Text>,
                    help: <Text id='input.subscriptionBox1Body-help'>Body text for the 1. subscription box.</Text>,
                    name: 'subscriptionBox1Body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox1Body,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox1Button'>subscriptionBox1Button</Text>,
                    placeholder: <Text id='input.subscriptionBox1Button'>subscriptionBox1Button</Text>,
                    help: <Text id='input.subscriptionBox1Button-help'>Button text for 1. subscription box.</Text>,
                    name: 'subscriptionBox1Button',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox1Button,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox1Price'>subscriptionBox1Price</Text>,
                    placeholder: <Text id='input.subscriptionBox1Price'>subscriptionBox1Price</Text>,
                    help: <Text id='input.subscriptionBox1Price-help'>Price for 1. subscription box.</Text>,
                    name: 'subscriptionBox1Price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.subscriptionBox1Price,
                    showInList: false,
                },

                {
                    title: <Text id='input.subscriptionBox2Title'>subscriptionBox2Title</Text>,
                    placeholder: <Text id='input.subscriptionBox2Title'>subscriptionBox2Title</Text>,
                    help: <Text id='input.subscriptionBox2Title-help'>Title for 2. subscription box.</Text>,
                    name: 'subscriptionBox2Title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox2Title,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox2Body'>subscriptionBox2Body</Text>,
                    placeholder: <Text id='input.subscriptionBox2Body'>subscriptionBox2Body</Text>,
                    help: <Text id='input.subscriptionBox2Body-help'>Body text for the 2. subscription box.</Text>,
                    name: 'subscriptionBox2Body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox2Body,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox2Button'>subscriptionBox2Button</Text>,
                    placeholder: <Text id='input.subscriptionBox2Button'>subscriptionBox2Button</Text>,
                    help: <Text id='input.subscriptionBox2Button-help'>Button text for 2. subscription box.</Text>,
                    name: 'subscriptionBox2Button',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox2Button,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox2Price'>subscriptionBox2Price</Text>,
                    placeholder: <Text id='input.subscriptionBox2Price'>subscriptionBox2Price</Text>,
                    help: <Text id='input.subscriptionBox2Price-help'>Price for 2. subscription box.</Text>,
                    name: 'subscriptionBox2Price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.subscriptionBox2Price,
                    showInList: false,
                },

                {
                    title: <Text id='input.subscriptionBox3Title'>subscriptionBox3Title</Text>,
                    placeholder: <Text id='input.subscriptionBox3Title'>subscriptionBox3Title</Text>,
                    help: <Text id='input.subscriptionBox3Title-help'>Title for 3. subscription box.</Text>,
                    name: 'subscriptionBox3Title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox3Title,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox3Body'>subscriptionBox3Body</Text>,
                    placeholder: <Text id='input.subscriptionBox3Body'>subscriptionBox3Body</Text>,
                    help: <Text id='input.subscriptionBox3Body-help'>Body text for the 3. subscription box.</Text>,
                    name: 'subscriptionBox3Body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox3Body,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox3Button'>subscriptionBox3Button</Text>,
                    placeholder: <Text id='input.subscriptionBox3Button'>subscriptionBox3Button</Text>,
                    help: <Text id='input.subscriptionBox3Button-help'>Button text for 3. subscription box.</Text>,
                    name: 'subscriptionBox3Button',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox3Button,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox3Price'>subscriptionBox3Price</Text>,
                    placeholder: <Text id='input.subscriptionBox3Price'>subscriptionBox3Price</Text>,
                    help: <Text id='input.subscriptionBox3Price-help'>Price for 3. subscription box.</Text>,
                    name: 'subscriptionBox3Price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.subscriptionBox3Price,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-location-title'>Race location</Text>,
            key: 'Race location',
            fields: [
                {
                    title: <Text id='input.startLatlng'>StartLatlng</Text>,
                    placeholder: <Text id='input.startLatlng'>StartLatlng</Text>,
                    name: 'startLatlng',
                    type: 'arrayElement',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.startLatlng,
                    showInList: false
                },
                {
                    title: <Text id='input.endLatlng'>EndLatlng</Text>,
                    placeholder: <Text id='input.endLatlng'>EndLatlng</Text>,
                    name: 'endLatlng',
                    type: 'arrayElement',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.endLatlng,
                    showInList: false
                },
                {
                    title: <Text id='input.locationCity'>LocationCity</Text>,
                    placeholder: <Text id='input.locationCity'>LocationCity</Text>,
                    name: 'locationCity',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.locationCity,
                    showInList: false,
                },
                {
                    title: <Text id='input.locationState'>LocationState</Text>,
                    placeholder: <Text id='input.locationState'>LocationState</Text>,
                    name: 'locationState',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.locationState,
                    showInList: false,
                },
                {
                    title: <Text id='input.locationCountry'>LocationCountry</Text>,
                    placeholder: <Text id='input.locationCountry'>LocationCountry</Text>,
                    name: 'locationCountry',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.locationCountry,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-contact-title'>Race contact info</Text>,
            key: 'Race contact info',
            fields: [
                {
                    title: <Text id='input.emailSender'>emailSender</Text>,
                    placeholder: <Text id='input.emailSender'>emailSender</Text>,
                    help: <Text id='input.emailSender-help'>Must be an approved sender in the system. If you are unsure, please do NOT change this.</Text>,
                    name: 'emailSender',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.emailSender,
                    showInList: false,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.emailSender && !util.validateEmail(obj.emailSender)) {
                            const invalidChars = findInvalidEmailChars(obj.emailSender);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('emailSender', `Invalid emailSender. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('emailSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('emailSender', '');
                        holdingStore.setFeedbackButton('emailSender', null, null);
                    },
                },
                {
                    title: <Text id='input.smsSender'>smsSender</Text>,
                    placeholder: <Text id='input.smsSender'>smsSender</Text>,
                    help: <Text id='input.smsSender-help'>Must follow these rules: Alphanumeric. Maximum of 11 characters. No spaces. No special characters. The sender ID must be the brand name of the company sending the SMS. If you are unsure, please do NOT change this.</Text>,
                    name: 'smsSender',
                    maxLength: 11,
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.smsSender,
                    showInList: false,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.smsSender && !/^[a-zA-Z0-9]+$/.test(obj.smsSender)) {
                            holdingStore.setFeedbackError('smsSender', 'Invalid smsSender. Must be 11 digits.');
                        } else {
                            holdingStore.setFeedbackError('smsSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('smsSender', '');
                        holdingStore.setFeedbackButton('smsSender', null, null);
                    },
                },
                {
                    title: <Text id='input.contactEmail'>ContactEmail</Text>,
                    placeholder: <Text id='input.contactEmail'>ContactEmail</Text>,
                    name: 'contactEmail',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contactEmail,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.contactCellphone'>ContactCellphone</Text>,
                    placeholder: <Text id='input.contactCellphone'>ContactCellphone</Text>,
                    name: 'contactCellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contactCellphone,
                    showInList: false,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.facebook,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.instagram,
                    showInList: false,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.race-signup-text-title'>Race signup text</Text>,
            key: 'Race signup texts',
            fields: [
                {
                    title: <Text id='input.declarationText'>declarationText</Text>,
                    placeholder: <Text id='input.declarationText'>declarationText</Text>,
                    name: 'declarationText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.declarationText,
                    showInList: false,
                },
                {
                    title: <Text id='input.termsAndAgreementText'>termsAndAgreementText</Text>,
                    placeholder: <Text id='input.termsAndAgreementText'>termsAndAgreementText</Text>,
                    name: 'termsAndAgreementText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.termsAndAgreementText,
                    showInList: false,
                },
                {
                    title: <Text id='input.licenseText'>licenseText</Text>,
                    placeholder: <Text id='input.licenseText'>licenseText</Text>,
                    name: 'licenseText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.licenseText,
                    showInList: false,
                },
                {
                    title: <Text id='input.preCheckText'>preCheckText</Text>,
                    placeholder: <Text id='input.preCheckText'>preCheckText</Text>,
                    name: 'preCheckText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.preCheckText,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-media-title'>Race media</Text>,
            key: 'Race media',
            fields: [
                {
                    title: <Text id='input.sponsors'>sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>sponsors</Text>,
                    help: <Text id='input.race-sponsors-help'>Add sponsor images to promote your sponsors.</Text>,
                    name: 'sponsors',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                    addToImages: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: 'Autoplay Video',
                    placeholder: 'Autoplay Video',
                    name: 'autoplayVideo',
                    type: 'toggle',
                    icon: 'fa-solid fa-video',
                    getValue: obj => obj.autoplayVideo || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
        {
            name: <Text id='input.race-settings-title'>Race settings</Text>,
            key: 'Race settings',
            fields: [
                {
                    title: <Text id='input.nonstop'>Nonstop</Text>,
                    placeholder: <Text id='input.nonstop'>Nonstop</Text>,
                    name: 'nonstop',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.nonstop,
                    showInList: true,
                },
                {
                    title: <Text id='input.raceAdmins'>Race Admins</Text>,
                    placeholder: <Text id='input.raceAdmins'>Race Admins</Text>,
                    name: 'raceAdmins',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-user-shield',
                    lookupArray: 'adminRaceAdmins',
                    searchMethod: 'searchUsersForRace',
                    getValue: obj => obj.raceAdmins?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.mediaWorkers'>Media Workers</Text>,
                    placeholder: <Text id='input.mediaWorkers'>Media Workers</Text>,
                    name: 'mediaWorkers',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-video',
                    searchMethod: 'searchUsersForRace',
                    getValue: obj => obj.mediaWorkers?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.timeTrackers'>Time Trackers</Text>,
                    placeholder: <Text id='input.timeTrackers'>Time Trackers</Text>,
                    name: 'timeTrackers',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-stopwatch',
                    searchMethod: 'searchUsersForRace',
                    getValue: obj => obj.timeTrackers?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.raceLeaders'>Race Leaders</Text>,
                    placeholder: <Text id='input.raceLeaders'>Race Leaders</Text>,
                    name: 'raceLeaders',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-flag-checkered',
                    searchMethod: 'searchUsersForRace',
                    getValue: obj => obj.raceLeaders?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.tdLeaders'>TD Leaders</Text>,
                    placeholder: <Text id='input.tdLeaders'>TD Leaders</Text>,
                    name: 'tdLeaders',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-user-tie',
                    searchMethod: 'searchUsersForRace',
                    getValue: obj => obj.tdLeaders?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.tvRadioProducers'>TV/Radio Producers</Text>,
                    placeholder: <Text id='input.tvRadioProducers'>TV/Radio Producers</Text>,
                    name: 'tvRadioProducers',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-broadcast-tower',
                    searchMethod: 'searchUsersForRace',
                    getValue: obj => obj.tvRadioProducers?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.scooterBibs'>ScooterBibs</Text>,
                    placeholder: <Text id='input.scooterBibs'>ScooterBibs</Text>,
                    name: 'scooterBibs',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.scooterBibs,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.mapCredits'>MapCredits</Text>,
                    placeholder: <Text id='input.mapCredits'>MapCredits</Text>,
                    name: 'mapCredits',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.mapCredits,
                    showInList: false,
                },
                {
                    title: <Text id='input.mapType'>mapType</Text>,
                    placeholder: <Text id='input.mapType'>mapType</Text>,
                    name: 'mapType',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.mapType,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkpointOuterFence'>CheckpointOuterFence</Text>,
                    placeholder: <Text id='input.checkpointOuterFence'>CheckpointOuterFence</Text>,
                    name: 'checkpointOuterFence',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.checkpointOuterFence,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkpointArrivalFence'>CheckpointArrivalFence</Text>,
                    placeholder: <Text id='input.checkpointArrivalFence'>CheckpointArrivalFence</Text>,
                    name: 'checkpointArrivalFence',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.checkpointArrivalFence,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkpointDepartureFence'>CheckpointDepartureFence</Text>,
                    placeholder: <Text id='input.checkpointDepartureFence'>CheckpointDepartureFence</Text>,
                    name: 'checkpointDepartureFence',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.checkpointDepartureFence,
                    showInList: false,
                },
                {
                    title: <Text id='input.cutOffSpeedMeterSec'>CutOffSpeedMeterSec</Text>,
                    placeholder: <Text id='input.cutOffSpeedMeterSec'>CutOffSpeedMeterSec</Text>,
                    name: 'cutOffSpeedMeterSec',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.cutOffSpeedMeterSec,
                    showInList: false,
                },
                {
                    title: <Text id='input.cutOffDurationSec'>CutOffDurationSec</Text>,
                    placeholder: <Text id='input.cutOffDurationSec'>CutOffDurationSec</Text>,
                    name: 'cutOffDurationSec',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.cutOffDurationSec,
                    showInList: true,
                },
                {
                    title: <Text id='input.secondsToShowFinishMushers'>SecondsToShowFinishMushers</Text>,
                    placeholder: <Text id='input.secondsToShowFinishMushers'>SecondsToShowFinishMushers</Text>,
                    name: 'secondsToShowFinishMushers',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.secondsToShowFinishMushers,
                    showInList: false,
                },
                {
                    title: <Text id='input.speedGroupSize'>SpeedGroupSize</Text>,
                    placeholder: <Text id='input.speedGroupSize'>SpeedGroupSize</Text>,
                    name: 'speedGroupSize',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.speedGroupSize,
                    showInList: false,
                },
                {
                    title: <Text id='input.numberOfLastTrackPoints'>NumberOfLastTrackPoints</Text>,
                    placeholder: <Text id='input.numberOfLastTrackPoints'>NumberOfLastTrackPoints</Text>,
                    name: 'numberOfLastTrackPoints',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.numberOfLastTrackPoints,
                    showInList: false,
                },
                {
                    title: <Text id='input.useGpsDataToCalcPercentDone'>UseGpsDataToCalcPercentDone</Text>,
                    placeholder: <Text id='input.useGpsDataToCalcPercentDone'>UseGpsDataToCalcPercentDone</Text>,
                    name: 'useGpsDataToCalcPercentDone',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.useGpsDataToCalcPercentDone,
                    showInList: false,
                },
                {
                    title: <Text id='input.showTheoreticalPosition'>ShowTheoreticalPosition</Text>,
                    placeholder: <Text id='input.showTheoreticalPosition'>ShowTheoreticalPosition</Text>,
                    name: 'showTheoreticalPosition',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.showTheoreticalPosition,
                    showInList: false,
                },
                {
                    title: <Text id='input.showDistanceMarkers'>ShowDistanceMarkers</Text>,
                    placeholder: <Text id='input.showDistanceMarkers'>ShowDistanceMarkers</Text>,
                    name: 'showDistanceMarkers',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.showDistanceMarkers,
                    showInList: false,
                },
                {
                    title: <Text id='input.maxTrackIdxToSkip'>MaxTrackIdxToSkip</Text>,
                    placeholder: <Text id='input.maxTrackIdxToSkip'>MaxTrackIdxToSkip</Text>,
                    name: 'maxTrackIdxToSkip',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.maxTrackIdxToSkip,
                    showInList: false,
                },
                {
                    title: <Text id='input.minTimeToNextCpSec'>MinTimeToNextCpSec</Text>,
                    placeholder: <Text id='input.minTimeToNextCpSec'>MinTimeToNextCpSec</Text>,
                    name: 'minTimeToNextCpSec',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.minTimeToNextCpSec,
                    showInList: false,
                },
                {
                    title: <Text id='input.countryRegexp'>CountryRegexp</Text>,
                    placeholder: <Text id='input.countryRegexp'>CountryRegexp</Text>,
                    help: <Text id='input.countryRegexp-help'>Only show tracking for countries matching this regexp.</Text>,
                    name: 'countryRegexp',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.countryRegexp,
                    showInList: false,
                },
                {
                    title: <Text id='input.countryRegexpMessage'>CountryRegexpMessage</Text>,
                    placeholder: <Text id='input.countryRegexpMessage'>CountryRegexpMessage</Text>,
                    name: 'countryRegexpMessage',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.countryRegexpMessage,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-link-title'>Race link buttons</Text>,
            key: 'Race link buttons',
            fields: [
                {
                    title: <Text id='input.link1'>Link 1</Text>,
                    placeholder: <Text id='input.link1'>Link 1</Text>,
                    help: <Text id='input.link1-help'>Valid link 1.</Text>,
                    name: 'link1',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link1,
                    showInList: false,
                },
                {
                    title: <Text id='input.link1Text'>Link Text 1</Text>,
                    placeholder: <Text id='input.link1Text'>Link Text 1</Text>,
                    help: <Text id='input.link1Text-help'>Link description 1.</Text>,
                    name: 'link1Text',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link1Text,
                    showInList: false,
                },
                {
                    title: <Text id='input.link2'>Link 2</Text>,
                    placeholder: <Text id='input.link2'>Link 2</Text>,
                    help: <Text id='input.link2-help'>Valid link 2.</Text>,
                    name: 'link2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link2,
                    showInList: false,
                },
                {
                    title: <Text id='input.link2Text'>Link Text 2</Text>,
                    placeholder: <Text id='input.link2Text'>Link Text 2</Text>,
                    help: <Text id='input.link2Text-help'>Link description 2.</Text>,
                    name: 'link2Text',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link2Text,
                    showInList: false,
                },
                {
                    title: <Text id='input.link3'>Link 3</Text>,
                    placeholder: <Text id='input.link3'>Link 3</Text>,
                    help: <Text id='input.link3-help'>Valid link 3.</Text>,
                    name: 'link3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link3,
                    showInList: false,
                },
                {
                    title: <Text id='input.link3Text'>Link Text 3</Text>,
                    placeholder: <Text id='input.link3Text'>Link Text 3</Text>,
                    help: <Text id='input.link3Text-help'>Link description 3.</Text>,
                    name: 'link3Text',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link3Text,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-map-title'>Race Map Details</Text>,
            key: 'Race map details',
            fields: [
                {
                    title: <Text id='input.waypoints'>waypoints</Text>,
                    placeholder: <Text id='input.waypoints'>waypoints</Text>,
                    name: 'waypoints',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-map-marker',
                    getValue: obj => obj.waypoints,
                    displayArrayObject: obj => <>
                        {obj.name}:<br /> {obj.lat},{obj.lng}`;
                    </>,
                    datalist: [
                        'name', 'lat', 'lng', 'rotateDeg', 'icon',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.race-vipps-payment-title'>Vipps payment settings</Text>,
            key: 'Race vipps settings',
            fields: [
                {
                    title: <Text id='input.vippsName'>vippsName</Text>,
                    placeholder: <Text id='input.vippsName'>vippsName</Text>,
                    help: <Text id='input.vippsName-help'>Vippsname has to be a-z and lowercase only.</Text>,
                    name: 'vippsName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsName,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsVersion'>vippsVersion</Text>,
                    placeholder: <Text id='input.vippsVersion'>vippsVersion</Text>,
                    help: <Text id='input.vippsVersion-help'>vippsVersion has to 1.0.0</Text>,
                    name: 'vippsVersion',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsVersion,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsPluginName'>vippsPluginName</Text>,
                    placeholder: <Text id='input.vippsPluginName'>vippsPluginName</Text>,
                    help: <Text id='input.vippsPluginName-help'>vippsPluginName has to be a-z and lowercase only.</Text>,
                    name: 'vippsPluginName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsPluginName,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsPluginVersion'>vippsPluginVersion</Text>,
                    placeholder: <Text id='input.vippsPluginVersion'>vippsPluginVersion</Text>,
                    help: <Text id='input.vippsPluginVersion-help'>vippsPluginVersion has to 1.0.0</Text>,
                    name: 'vippsPluginVersion',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsPluginVersion,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsMerchantSerialNumber'>vippsMerchantSerialNumber</Text>,
                    placeholder: <Text id='input.vippsMerchantSerialNumber'>vippsMerchantSerialNumber</Text>,
                    help: <Text id='input.vippsMerchantSerialNumber-help'>Vipps MSN number.</Text>,
                    name: 'vippsMerchantSerialNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsMerchantSerialNumber,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsClientId'>vippsClientId</Text>,
                    placeholder: <Text id='input.vippsClientId'>vippsClientId</Text>,
                    name: 'vippsClientId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsClientId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsClientSecret'>vippsClientSecret</Text>,
                    placeholder: <Text id='input.vippsClientSecret'>vippsClientSecret</Text>,
                    name: 'vippsClientSecret',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsClientSecret,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsSubscriptionKey1'>vippsSubscriptionKey1</Text>,
                    placeholder: <Text id='input.vippsSubscriptionKey1'>vippsSubscriptionKey1</Text>,
                    name: 'vippsSubscriptionKey1',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsSubscriptionKey1,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsSubscriptionKey2'>vippsSubscriptionKey2</Text>,
                    placeholder: <Text id='input.vippsSubscriptionKey2'>vippsSubscriptionKey2</Text>,
                    name: 'vippsSubscriptionKey2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsSubscriptionKey2,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsUseTestMode'>vippsUseTestMode</Text>,
                    placeholder: <Text id='input.vippsUseTestMode'>vippsUseTestMode</Text>,
                    name: 'vippsUseTestMode',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsUseTestMode,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsTestPhoneNumber'>vippsTestPhoneNumber</Text>,
                    placeholder: <Text id='input.vippsTestPhoneNumber'>vippsTestPhoneNumber</Text>,
                    name: 'vippsTestPhoneNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTestPhoneNumber,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsTermsOfSale'>vippsTermsOfSale</Text>,
                    placeholder: <Text id='input.vippsTermsOfSale'>vippsTermsOfSale</Text>,
                    name: 'vippsTermsOfSale',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTermsOfSale,
                    showInList: false,
                    isAdminField: false,
                },
            ],
        },
        {
            name: <Text id='input.race-bank-payment-title'>Bank payment settings</Text>,
            key: 'Race bank payment settings',
            fields: [
                {
                    title: <Text id='input.bankCompany'>bankCompany</Text>,
                    placeholder: <Text id='input.bankCompany'>bankCompany</Text>,
                    name: 'bankCompany',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankCompany,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankAddress'>bankAddress</Text>,
                    placeholder: <Text id='input.bankAddress'>bankAddress</Text>,
                    name: 'bankAddress',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankAddress,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankPostalcode'>bankPostalcode</Text>,
                    placeholder: <Text id='input.bankPostalcode'>bankPostalcode</Text>,
                    name: 'bankPostalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankPostalcode,
                    showInList: false,
                    isAdminField: false,
                    // callback: async (obj, updateValue) => {
                    //     if (obj.postalcode && obj.postalcode.length === 4) {
                    //         const response = await postnummerLookup(obj.postalcode);
                    //
                    //         if (response) {
                    //             const place = response[0]?.poststed;
                    //             updateValue({
                    //                 name: 'postalplace',
                    //                 value: place,
                    //                 id: obj.id,
                    //             });
                    //         }
                    //     }
                    // },
                },
                {
                    title: <Text id='input.bankPostalplace'>bankPostalplace</Text>,
                    placeholder: <Text id='input.bankPostalplace'>bankPostalplace</Text>,
                    name: 'bankPostalplace',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankPostalplace,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankAccountNumber'>bankAccountNumber</Text>,
                    placeholder: <Text id='input.bankAccountNumber'>bankAccountNumber</Text>,
                    name: 'bankAccountNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankAccountNumber,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankIBAN'>bankIBAN</Text>,
                    placeholder: <Text id='input.bankIBAN'>bankIBAN</Text>,
                    name: 'bankIBAN',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankIBAN,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankSWIFT'>bankSWIFT</Text>,
                    placeholder: <Text id='input.bankSWIFT'>bankSWIFT</Text>,
                    name: 'bankSWIFT',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankSWIFT,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankMarkPayment'>bankMarkPayment</Text>,
                    placeholder: <Text id='input.bankMarkPayment'>bankMarkPayment</Text>,
                    name: 'bankMarkPayment',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankMarkPayment,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankDueDate'>bankDueDate</Text>,
                    placeholder: <Text id='input.bankDueDate'>bankDueDate</Text>,
                    name: 'bankDueDate',
                    type: 'date',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankDueDate,
                    displayValue: val => val ? util.formatDate(val, {
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankOtherInfo'>bankOtherInfo</Text>,
                    placeholder: <Text id='input.bankOtherInfo'>bankOtherInfo</Text>,
                    help: <Text id='input.bankOtherInfo-help'>Alternative manual payment like Vipps number or other.</Text>,
                    name: 'bankOtherInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankOtherInfo,
                    showInList: false,
                    isAdminField: false,
                },
            ],
        },
    ],
    raceClass: [
        {
            name: <Text id='input.race-class-basics-title'>Race Class basics</Text>,
            key: 'Race Class basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'select',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.race,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'races',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}`,
                    showInList: false,
                    hideIfAdmin: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'select',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.race,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'adminRaces',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}`,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.year'>Year</Text>,
                    placeholder: <Text id='input.year'>Year</Text>,
                    name: 'year',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.year,
                    showInList: true,
                    styles: 'min-width: 50px;',
                },
                {
                    title: <Text id='input.currency'>Currency</Text>,
                    placeholder: <Text id='input.currency'>Currency</Text>,
                    help: <Text id='input.currency-help'>Currency for signup fees and payments in this class. Leave blank for NOK. Use the ISO code, for example SEK or EUR. Vipps only works with NOK, other currencies go through PayPal.</Text>,
                    name: 'currency',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.currency,
                    showInList: true,
                    isRecommended: true,
                    styles: 'min-width: 30px;',
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    styles: 'min-width: 120px;',
                    isRecommended: true,
                },
                {
                    title: <Text id='input.tag'>Tag</Text>,
                    placeholder: <Text id='input.tag'>Tag</Text>,
                    name: 'tag',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.tag,
                    showInList: false,
                },
                {
                    title: <Text id='input.warning'>warning</Text>,
                    placeholder: <Text id='input.warning'>warning</Text>,
                    name: 'warning',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.warning,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-info-title'>Race Class Info</Text>,
            key: 'Race Class Info',
            fields: [
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.body,
                    showInList: false,
                },
                {
                    title: <Text id='input.info'>Info</Text>,
                    placeholder: <Text id='input.info'>Info</Text>,
                    name: 'info',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.info,
                    showInList: false,
                },
                {
                    title: <Text id='input.terms'>Terms</Text>,
                    placeholder: <Text id='input.terms'>Terms</Text>,
                    name: 'terms',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.terms,
                    showInList: false,
                },
                {
                    title: <Text id='input.trackInfo'>TrackInfo</Text>,
                    placeholder: <Text id='input.trackInfo'>TrackInfo</Text>,
                    name: 'trackInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.prices'>Prices</Text>,
                    placeholder: <Text id='input.prices'>Prices</Text>,
                    name: 'prices',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.prices,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: false,
                },
                {
                    title: <Text id='input.rules'>Rules</Text>,
                    placeholder: <Text id='input.rules'>Rules</Text>,
                    name: 'rules',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rules,
                    showInList: false,
                },
                {
                    title: <Text id='input.readyToSignup'>ReadyToSignup</Text>,
                    placeholder: <Text id='input.readyToSignup'>ReadyToSignup</Text>,
                    name: 'readyToSignup',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.readyToSignup,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-info-english-title'>Race Class Info English</Text>,
            key: 'Race Class Info English',
            fields: [
                {
                    title: <Text id='input.trackInfoEn'>trackInfoEn</Text>,
                    placeholder: <Text id='input.trackInfoEn'>trackInfoEn</Text>,
                    name: 'trackInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.infoEn'>infoEn</Text>,
                    placeholder: <Text id='input.infoEn'>infoEn</Text>,
                    name: 'infoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.infoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.termsEn'>termsEn</Text>,
                    placeholder: <Text id='input.termsEn'>termsEn</Text>,
                    name: 'termsEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.termsEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.pricesEn'>pricesEn</Text>,
                    placeholder: <Text id='input.pricesEn'>pricesEn</Text>,
                    name: 'pricesEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.pricesEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifyingEn'>qualifyingEn</Text>,
                    placeholder: <Text id='input.qualifyingEn'>qualifyingEn</Text>,
                    name: 'qualifyingEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifyingEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.rulesEn'>rulesEn</Text>,
                    placeholder: <Text id='input.rulesEn'>rulesEn</Text>,
                    name: 'rulesEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rulesEn,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-dates-title'>Race Class dates</Text>,
            key: 'Race Class dates',
            fields: [
                {
                    title: <Text id='input.startDate'>StartDate</Text>,
                    placeholder: <Text id='input.startDate'>StartDate</Text>,
                    name: 'startDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.startDate,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.endDate,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupStartDate'>SignupStartDate</Text>,
                    placeholder: <Text id='input.signupStartDate'>SignupStartDate</Text>,
                    name: 'signupStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupStartDate,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupEndDate'>SignupEndDate</Text>,
                    placeholder: <Text id='input.signupEndDate'>SignupEndDate</Text>,
                    name: 'signupEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupEndDate,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signupDelayedEndDate'>SignupDelayedEndDate</Text>,
                    placeholder: <Text id='input.signupDelayedEndDate'>SignupDelayedEndDate</Text>,
                    name: 'signupDelayedEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupDelayedEndDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.musherMeetingStartDate'>Musher Meeting startDate</Text>,
                    placeholder: <Text id='input.musherMeetingStartDate'>Musher Meeting startDate</Text>,
                    name: 'musherMeetingStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.musherMeetingStartDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.musherMeetingEndDate'>Musher Meeting endDate</Text>,
                    placeholder: <Text id='input.musherMeetingEndDate'>Musher Meeting endDate</Text>,
                    name: 'musherMeetingEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.musherMeetingEndDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkInStartDate'>CheckIn StartDate</Text>,
                    placeholder: <Text id='input.checkInStartDate'>CheckIn StartDate</Text>,
                    name: 'checkInStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.checkInStartDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkInEndDate'>CheckIn EndDate</Text>,
                    placeholder: <Text id='input.checkInEndDate'>CheckIn EndDate</Text>,
                    name: 'checkInEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.checkInEndDate,
                    showInList: false,
                },
            ],
        },


        {
            name: <Text id='input.race-class-settings-title'>Race Class settings</Text>,
            key: 'Race Class settings',
            fields: [
                {
                    title: <Text id='input.dogs'>Dogs</Text>,
                    placeholder: <Text id='input.dogs'>Dogs</Text>,
                    name: 'dogs',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.dogs,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.dogsInfo'>dogsInfo</Text>,
                    placeholder: <Text id='input.dogsInfo'>dogsInfo</Text>,
                    name: 'dogsInfo',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dogsInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.doNotCountRestTime'>Do not count rest time</Text>,
                    placeholder: <Text id='input.doNotCountRestTime'>Do not count rest time</Text>,
                    help: <Text id='input.doNotCountRestTime-help'>Toggle if you do not want to calculate rest time. Typically used for stage races with one stage every day.</Text>,
                    name: 'doNotCountRestTime',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.doNotCountRestTime ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.sortOrderResults'>SortOrderResults</Text>,
                    placeholder: <Text id='input.sortOrderResults'>SortOrderResults</Text>,
                    help: <Text id='input.sortOrderResults-help'>Sort order for results. Nothing for normal races, "timeused" for stage races with rest and "raceTimeused" for stage races new start every day.</Text>,
                    name: 'sortOrderResults',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sortOrderResults,
                    showInList: false,
                },
                {
                    title: <Text id='input.distance'>Distance</Text>,
                    placeholder: <Text id='input.distance'>Distance</Text>,
                    name: 'distance',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.distance,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.minDistance'>MinDistance</Text>,
                    placeholder: <Text id='input.minDistance'>MinDistance</Text>,
                    name: 'minDistance',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.minDistance,
                    showInList: false,
                },
                {
                    title: <Text id='input.requiredRestMinutes'>RequiredRestMinutes</Text>,
                    placeholder: <Text id='input.requiredRestMinutes'>RequiredRestMinutes</Text>,
                    name: 'requiredRestMinutes',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.requiredRestMinutes,
                    showInList: true,
                },
                {
                    title: <Text id='input.elevation'>Elevation</Text>,
                    placeholder: <Text id='input.elevation'>Elevation</Text>,
                    name: 'elevation',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.elevation,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.raceClassTrack'>RaceClassTrack</Text>,
                    placeholder: <Text id='input.raceClassTrack'>RaceClassTrack</Text>,
                    name: 'raceClassTrack',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClassTrack,
                    showInList: true,
                },
                {
                    title: <Text id='input.maxMushers'>maxMushers</Text>,
                    placeholder: <Text id='input.maxMushers'>maxMushers</Text>,
                    help: <Text id='input.maxMushers-help'>Max number of mushers in this class.</Text>,
                    name: 'maxMushers',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.maxMushers,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.bibColor'>bibColor</Text>,
                    placeholder: <Text id='input.bibColor'>bibColor</Text>,
                    name: 'bibColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bibColor,
                    showInList: false,
                },
                {
                    title: <Text id='input.startBib'>startBib</Text>,
                    placeholder: <Text id='input.startBib'>startBib</Text>,
                    help: <Text id='input.startBib-help'>Start bib number in this class</Text>,
                    name: 'startBib',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.startBib,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.endBib'>endBib</Text>,
                    placeholder: <Text id='input.endBib'>endBib</Text>,
                    help: <Text id='input.endBib-help'>End bib number in this class</Text>,
                    name: 'endBib',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.endBib,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.startInterval'>startInterval</Text>,
                    placeholder: <Text id='input.startInterval'>startInterval</Text>,
                    help: <Text id='input.startInterval-help'>Start interval in minutes</Text>,
                    name: 'startInterval',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.startInterval,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.obligRest'>ObligRest</Text>,
                    placeholder: <Text id='input.obligRest'>ObligRest</Text>,
                    name: 'obligRest',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.obligRest,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-fees-title'>Race Class fees</Text>,
            key: 'Race Class fees',
            fields: [
                {
                    title: <Text id='input.signupfee'>Signupfee</Text>,
                    placeholder: <Text id='input.signupfee'>Signupfee</Text>,
                    name: 'signupfee',
                    help: <Text id='input.signupfee-help'>Optional fee paid at signup, on top of the entry fee. Leave at 0 if the class only has an entry fee. With payment required before approval, this is the amount the musher must pay first.</Text>,
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signupfee,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signuppayment'>Signuppayment</Text>,
                    placeholder: <Text id='input.signuppayment'>Signuppayment</Text>,
                    name: 'signuppayment',
                    help: <Text id='input.signuppayment-help'>The entry fee for the class. With payment required before approval and no signup fee, this is the amount the musher must pay first.</Text>,
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signuppayment,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.signuppaymentDelayed'>SignuppaymentDelayed</Text>,
                    placeholder: <Text id='input.signuppaymentDelayed'>SignuppaymentDelayed</Text>,
                    name: 'signuppaymentDelayed',
                    help: <Text id='input.signuppaymentDelayed-help'>Not used by the payment flow yet. Leave the field empty.</Text>,
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signuppaymentDelayed,
                    showInList: false,
                },
                {
                    title: <Text id='input.seedingPaymentAmount'>seedingPaymentAmount</Text>,
                    placeholder: <Text id='input.seedingPaymentAmount'>seedingPaymentAmount</Text>,
                    name: 'seedingPaymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.seedingPaymentAmount,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-checkpoints-title'>Race Class checkpoints</Text>,
            key: 'Race Class checkpoints',
            fields: [
                {
                    title: <Text id='input.checkpoints'>checkpoints</Text>,
                    placeholder: <Text id='input.checkpoints'>checkpoints</Text>,
                    name: 'checkpoints',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.checkpoints,
                    displayArrayObject: obj => <div class='d-flex flex-column w-100'>

                        <div>{obj.sortOrder}: {obj.name}</div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>fromPrev:</span></div><div class='w-50 text-start ms-1'>{obj.distanceFromPrev} km</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>rest:</span></div><div class='w-50 text-start ms-1'>{util.secToHms(obj.requiredRestMinutes * 60 || 0)}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>timeEq:</span></div><div class='w-50 text-start ms-1'>{obj.timeEqualization ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>useRemainingRest:</span></div><div class='w-50 text-start ms-1'>{obj.useRemainingRest ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>notPartOfTotalRest:</span></div><div class='w-50 text-start ms-1'>{obj.notPartOfTotalRest ? <i class='fa-solid fa-square-check text-warning' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>isOptRest:</span></div><div class='w-50 text-start ms-1'>{obj.isOptionalRest ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>isStart:</span></div><div class='w-50 text-start ms-1'>{obj.isStartLine ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>isFinish:</span></div><div class='w-50 text-start ms-1'>{obj.isFinishLine ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>isPrologue:</span></div><div class='w-50 text-start ms-1'>{obj.isPrologue ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>lat:</span></div><div class='w-50 text-start ms-1'>{obj.lat}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>lng:</span></div><div class='w-50 text-start ms-1'>{obj.lng}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>altitude:</span></div><div class='w-50 text-start ms-1'>{obj.altitude}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>rotateDeg:</span></div><div class='w-50 text-start ms-1'>{obj.rotateDeg}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>geofenceIn:</span></div><div class='w-50 text-start ms-1'>{obj.geofenceArrivalRadius ? `${obj.geofenceArrivalRadius}m` : <span class='text-muted'>-</span>}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-end me-1'><span class='font-weight-lighter'>geofenceOut:</span></div><div class='w-50 text-start ms-1'>{obj.geofenceDepartureRadius ? `${obj.geofenceDepartureRadius}m` : <span class='text-muted'>-</span>}</div></div>
                        {obj.proximityAlertEnabled ? <div class='d-flex flex-row'>
                            <div class='w-50 text-end me-1'><span class='font-weight-lighter'>proxAlert:</span></div>
                            <div class='w-50 text-start ms-1'>
                                <i class='fa-solid fa-square-check text-success' /> {obj.proximityAlertRadius ? `${obj.proximityAlertRadius}m` : '-'}
                            </div>
                        </div> : null}
                    </div>,

                    datalist: [
                        'name', 'sortOrder', 'distanceFromPrev',
                        'requiredRestMinutes', 'useRemainingRest', 'notPartOfTotalRest',
                        'timeEqualization', 'isStartLine', 'isFinishLine',
                        'estSpeed', 'estTimeToCpMinutes', 'estRestMinutes',
                        'requiredRestMinutesOptional', 'timeEqualizationOptional',
                        'isPrologue',
                        'isDogDrop', 'isOptionalRest',
                        'lat', 'lng', 'altitude',
                        'rotateDeg',
                        'geofenceArrivalRadius', 'geofenceDepartureRadius',
                        'proximityAlertEnabled', 'proximityAlertRadius',
                        'proximityAlertCellphones', 'proximityAlertMessage',
                    ],
                    datalistHelp: {
                        name: 'Name of the checkpoint.',
                        sortOrder: 'Sort order of the checkpoint. 1, 2, 3, etc.',
                        distanceFromPrev: 'Distance from previous checkpoint in km.',
                        requiredRestMinutes: 'Mandatory rest time in minutes. Musher must rest this long at this checkpoint.',
                        useRemainingRest: 'Calculate remaining mandatory rest at this checkpoint. Use on the last checkpoint where rest can be taken.',
                        notPartOfTotalRest: 'Rest at this checkpoint does NOT count toward mandatory rest. Use for hospitality stops where mushers can rest but it should not reduce their required rest time at mandatory rest checkpoints.',
                        timeEqualization: 'Time equalization applied. Release times are adjusted so all mushers have equal rest.',
                        isStartLine: 'Mark as start line. Only ONE checkpoint can be start.',
                        isFinishLine: 'Mark as finish line. Only ONE checkpoint can be finish.',
                        estSpeed: 'Estimated speed to checkpoint in km/h.',
                        estTimeToCpMinutes: 'Estimated time to checkpoint in minutes.',
                        estRestMinutes: 'Estimated rest time in minutes.',
                        requiredRestMinutesOptional: 'Optional rest minutes. For flexible rest locations where musher can choose to rest.',
                        timeEqualizationOptional: 'Optional time equalization. Applied only if musher takes rest at this flexible rest checkpoint.',
                        isPrologue: 'Stage to this checkpoint is a prologue (short qualifying stage).',
                        isDogDrop: 'Dogs can be dropped at this checkpoint.',
                        isOptionalRest: 'Flexible rest location. Musher can choose to take their mandatory rest here.',
                        lat: 'Latitude of the checkpoint.',
                        lng: 'Longitude of the checkpoint.',
                        altitude: 'Altitude of the checkpoint in meters.',
                        rotateDeg: 'Rotation of the checkpoint icon in degrees.',
                        geofenceArrivalRadius: 'GPS geofence radius in meters for auto check-in. Contestant is marked as arrived when 2 consecutive GPS points are inside this radius. Suggested: 500m.',
                        geofenceDepartureRadius: 'GPS geofence radius in meters for auto check-out. Must be larger than arrival radius. For FINISH LINE: use a much larger radius (1500-2000m) because mushers often move away quickly to park their sled/car. With slow GPS reporting (5-6 min intervals), arrival may be missed if they exit this zone before the 2nd GPS point arrives. Suggested: 700m for checkpoints, 1500-2000m for finish line.',
                        proximityAlertEnabled: 'Enable SMS alerts when mushers approach this checkpoint.',
                        proximityAlertRadius: 'Distance in meters to trigger SMS alert (e.g., 3000 for 3 km). Must be larger than geofence arrival radius. Requires geofencing to be enabled.',
                        proximityAlertCellphones: 'Comma-separated phone numbers for SMS alerts. Include country code (e.g., +4791520191,+4799887766).',
                        proximityAlertMessage: 'Custom message template. Placeholders: {musherName}, {bib}, {distance}, {checkpoint}. Leave empty for default.',
                    },
                    toggleFields: [
                        'isStartLine', 'isFinishLine', 'isDogDrop', 'isOptionalRest',
                        'useRemainingRest', 'notPartOfTotalRest', 'isPrologue', 'timeEqualization', 'timeEqualizationOptional',
                        'proximityAlertEnabled',
                    ],
                    drawerSize: 'medium',
                    showInList: false,
                    displayHeight: '300px',
                    displayWidth: '280px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.waypoints'>waypoints</Text>,
                    placeholder: <Text id='input.waypoints'>waypoints</Text>,
                    name: 'waypoints',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-map-marker',
                    getValue: obj => obj.waypoints,
                    displayArrayObject: obj => <>
                        {obj.name}:<br /> {obj.lat},{obj.lng}`;
                    </>,
                    datalist: [
                        'name', 'lat', 'lng', 'rotateDeg', 'icon',
                    ],
                    showInList: false,
                    displayHeight: '100px',
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-media-title'>Race Class media</Text>,
            key: 'Race Class media',
            fields: [
                {
                    title: <Text id='input.sponsors'>sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>sponsors</Text>,
                    help: <Text id='input.raceclass-sponsors-help'>Add sponsor images to promote your sponsors.</Text>,
                    name: 'sponsors',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                    addToImages: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
    ],
    raceClassContestant: [
        {
            name: <Text id='input.race-class-contestant-basics-title'>Race Class Contestant basics</Text>,
            key: 'Race Class Contestant basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.signupDate'>signupDate</Text>,
                    placeholder: <Text id='input.signupDate'>signupDate</Text>,
                    name: 'signupDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupDate,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.raceClass'>RaceClass</Text>,
                    placeholder: <Text id='input.raceClass'>RaceClass</Text>,
                    name: 'raceClass',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClass,
                    showInList: true,
                    isAdminField: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.raceClass'>RaceClass</Text>,
                    placeholder: <Text id='input.raceClass'>RaceClass</Text>,
                    name: 'raceClass',
                    type: 'select',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.raceClass,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'raceClasses',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title} (${e.distance}km)`,
                    showInList: false,
                    hideIfAdmin: false,
                },
                {
                    title: <Text id='input.raceClass'>RaceClass</Text>,
                    placeholder: <Text id='input.raceClass'>RaceClass</Text>,
                    name: 'raceClass',
                    type: 'select',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.raceClass,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'adminRaceClasses',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title} (${e.distance}km)`,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.approved'>Approved</Text>,
                    placeholder: <Text id='input.approved'>Approved</Text>,
                    name: 'approved',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.approved ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasVaccines'>hasVaccines</Text>,
                    placeholder: <Text id='input.hasVaccines'>hasVaccines</Text>,
                    name: 'hasVaccines',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasVaccines ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasSignedDeclaration'>hasSignedDeclaration</Text>,
                    placeholder: <Text id='input.hasSignedDeclaration'>hasSignedDeclaration</Text>,
                    name: 'hasSignedDeclaration',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasSignedDeclaration ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasDeliveredChipList'>hasDeliveredChipList</Text>,
                    placeholder: <Text id='input.hasDeliveredChipList'>hasDeliveredChipList</Text>,
                    name: 'hasDeliveredChipList',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasDeliveredChipList ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasReceivedBib'>hasReceivedBib</Text>,
                    placeholder: <Text id='input.hasReceivedBib'>hasReceivedBib</Text>,
                    name: 'hasReceivedBib',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasReceivedBib ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasReceivedTracker'>hasReceivedTracker</Text>,
                    placeholder: <Text id='input.hasReceivedTracker'>hasReceivedTracker</Text>,
                    name: 'hasReceivedTracker',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasReceivedTracker ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasPreCheck'>hasPreCheck</Text>,
                    placeholder: <Text id='input.hasPreCheck'>hasPreCheck</Text>,
                    name: 'hasPreCheck',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasPreCheck ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasLicense'>hasLicense</Text>,
                    placeholder: <Text id='input.hasLicense'>hasLicense</Text>,
                    name: 'hasLicense',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasLicense ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasWeighedEmergencyFood'>hasWeighedEmergencyFood</Text>,
                    placeholder: <Text id='input.hasWeighedEmergencyFood'>hasWeighedEmergencyFood</Text>,
                    name: 'hasWeighedEmergencyFood',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasWeighedEmergencyFood ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasPreRaceCheck'>hasPreRaceCheck</Text>,
                    placeholder: <Text id='input.hasPreRaceCheck'>hasPreRaceCheck</Text>,
                    name: 'hasPreRaceCheck',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasPreRaceCheck ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.status'>Status</Text>,
                    placeholder: <Text id='input.status'>Status</Text>,
                    name: 'status',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.status,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.raceStatus'>RaceStatus</Text>,
                    placeholder: <Text id='input.raceStatus'>RaceStatus</Text>,
                    name: 'raceStatus',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.raceStatus,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.isFinished'>isFinished</Text>,
                    placeholder: <Text id='input.isFinished'>isFinished</Text>,
                    name: 'isFinished',
                    type: 'toggle',
                    icon: 'fa-solid fa-flag-checkered',
                    getValue: obj => obj.isFinished ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                    showInAdminTable: true,
                },
                {
                    title: <Text id='input.isDNS'>isDNS</Text>,
                    placeholder: <Text id='input.isDNS'>isDNS</Text>,
                    name: 'isDNS',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isDNS ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                    showInAdminTable: true,
                },
                {
                    title: <Text id='input.isScratched'>isScratched</Text>,
                    placeholder: <Text id='input.isScratched'>isScratched</Text>,
                    name: 'isScratched',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isScratched ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                    showInAdminTable: true,
                },
                {
                    title: <Text id='input.comment'>comment</Text>,
                    placeholder: <Text id='input.comment'>comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                    showInList: false,
                    showInRaceAdminList: false,
                },
                {
                    title: <Text id='input.raceComment'>RaceComment</Text>,
                    placeholder: <Text id='input.raceComment'>RaceComment</Text>,
                    name: 'raceComment',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.raceComment,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.firstname,
                    showInList: true,
                    showInRaceAdminList: true,
                    noEdit: true,
                    csvField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.lastname,
                    showInList: true,
                    showInRaceAdminList: true,
                    noEdit: true,
                    csvField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.sportsClub'>SportsClub</Text>,
                    placeholder: <Text id='input.sportsClub'>SportsClub</Text>,
                    name: 'sportsClub',
                    type: 'text',
                    icon: 'fa-solid fa-people-group',
                    getValue: obj => obj.sportsClub,
                    showInList: true,
                    showInRaceAdminList: true,
                    csvField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.licenseNumber'>License Number</Text>,
                    placeholder: <Text id='input.licenseNumber'>License Number</Text>,
                    name: 'licenseNumber',
                    type: 'text',
                    icon: 'fa-duotone fa-id-card',
                    getValue: obj => obj.licenseNumber,
                    showInList: false,
                    csvField: true,
                },
                {
                    title: <Text id='input.kennel'>kennel</Text>,
                    placeholder: <Text id='input.kennel'>kennel</Text>,
                    name: 'kennel',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.kennel,
                    showInList: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    type: 'date',
                    min: '1900-01-01',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    showInRaceAdminList: true,
                    csvField: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-handler-title'>Race Class Contestant handler</Text>,
            key: 'Race Class Contestant handler',
            fields: [
                {
                    title: <Text id='input.handler1Firstname'>Handler 1 Firstname</Text>,
                    placeholder: <Text id='input.handler1Firstname'>Handler 1 Firstname</Text>,
                    name: 'handler1Firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler1Firstname,
                    showInList: false,
                    showInRaceAdminList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.handler1Lastname'>Handler 1 Lastname</Text>,
                    placeholder: <Text id='input.handler1Lastname'>Handler 1 Lastname</Text>,
                    name: 'handler1Lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler1Lastname,
                    showInList: false,
                    showInRaceAdminList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.handler1Cellphone'>Handler 1 Cellphone</Text>,
                    placeholder: <Text id='input.handler1Cellphone'>Handler 1 Cellphone</Text>,
                    name: 'handler1Cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.handler1Cellphone,
                    showInList: false,
                    showInRaceAdminList: true,
                    isInternalField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.handler2Firstname'>Handler 2 Firstname</Text>,
                    placeholder: <Text id='input.handler2Firstname'>Handler 2 Firstname</Text>,
                    name: 'handler2Firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler2Firstname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler2Lastname'>Handler 2 Lastname</Text>,
                    placeholder: <Text id='input.handler2Lastname'>Handler 2 Lastname</Text>,
                    name: 'handler2Lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler2Lastname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler2Cellphone'>Handler 2 Cellphone</Text>,
                    placeholder: <Text id='input.handler2Cellphone'>Handler 2 Cellphone</Text>,
                    name: 'handler2Cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.handler2Cellphone,
                    showInList: false,
                    showInRaceAdminList: true,
                    isInternalField: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-companion-title'>Race Class Contestant companion</Text>,
            key: 'Race Class Contestant companion',
            fields: [
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionFirstname'>companionFirstname</Text>,
                    placeholder: <Text id='input.companionFirstname'>companionFirstname</Text>,
                    name: 'companionFirstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.companionFirstname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionLastname'>companionLastname</Text>,
                    placeholder: <Text id='input.companionLastname'>companionLastname</Text>,
                    name: 'companionLastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.companionLastname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionCellphone'>companionCellphone</Text>,
                    placeholder: <Text id='input.companionCellphone'>companionCellphone</Text>,
                    name: 'companionCellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.companionCellphone,
                    showInList: false,
                    showInRaceAdminList: true,
                    isInternalField: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionBirth'>companionBirth</Text>,
                    placeholder: <Text id='input.companionBirth'>companionBirth</Text>,
                    name: 'companionBirth',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.companionBirth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    showInRaceAdminList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-payment-title'>Race Class Contestant payment</Text>,
            key: 'Race Class Contestant payment',
            fields: [
                {
                    title: <Text id='input.signupfeeAmount'>signupfeeAmount</Text>,
                    placeholder: <Text id='input.signupfeeAmount'>signupfeeAmount</Text>,
                    name: 'signupfeeAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signupfeeAmount,
                    showInList: true,
                    isAdminField: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.signuppaymentAmount'>signuppaymentAmount</Text>,
                    placeholder: <Text id='input.signuppaymentAmount'>signuppaymentAmount</Text>,
                    name: 'signuppaymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signuppaymentAmount,
                    showInList: true,
                    isAdminField: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.restPaymentAmount'>restPaymentAmount</Text>,
                    placeholder: <Text id='input.restPaymentAmount'>restPaymentAmount</Text>,
                    name: 'restPaymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.restPaymentAmount,
                    showInList: true,
                    isAdminField: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.seedingPaymentAmount'>seedingPaymentAmount</Text>,
                    placeholder: <Text id='input.seedingPaymentAmount'>seedingPaymentAmount</Text>,
                    name: 'seedingPaymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.seedingPaymentAmount,
                    showInList: true,
                    isAdminField: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.paymentMethod'>paymentMethod</Text>,
                    placeholder: <Text id='input.paymentMethod'>paymentMethod</Text>,
                    name: 'paymentMethod',
                    type: 'select',
                    datalist: [['cash', 'Kontant'], ['card', 'Kort'], ['invoice', 'Faktura'], ['account', 'Konto'], ['creditNote', 'Kreditnota']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentMethod,
                    showInList: true,
                    isAdminField: true,
                },

                {
                    title: <Text id='input.paidSignupfee'>PaidSignupfee</Text>,
                    placeholder: <Text id='input.paidSignupfee'>PaidSignupfee</Text>,
                    name: 'paidSignupfee',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidSignupfee,
                    showInList: true,
                },
                {
                    title: <Text id='input.paidSignup'>PaidSignup</Text>,
                    placeholder: <Text id='input.paidSignup'>PaidSignup</Text>,
                    name: 'paidSignup',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidSignup,
                    showInList: true,
                },
                {
                    title: <Text id='input.paidRest'>paidRest</Text>,
                    placeholder: <Text id='input.paidRest'>paidRest</Text>,
                    name: 'paidRest',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidRest,
                    showInList: true,
                },
                {
                    title: <Text id='input.paidSeeding'>paidSeeding</Text>,
                    placeholder: <Text id='input.paidSeeding'>paidSeeding</Text>,
                    name: 'paidSeeding',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidSeeding,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-raceinfo-title'>Race Class Contestant raceinfo</Text>,
            key: 'Race Class Contestant raceinfo',
            fields: [
                {
                    title: <Text id='input.bib'>Bib</Text>,
                    placeholder: <Text id='input.bib'>Bib</Text>,
                    name: 'bib',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bib,
                    showInList: true,
                    showInRaceAdminList: true,
                    csvField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.startTime'>StartTime</Text>,
                    placeholder: <Text id='input.startTime'>StartTime</Text>,
                    name: 'startTime',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.startTime,
                    showInList: true,
                    showInRaceAdminList: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.declarationDate'>DeclarationDate</Text>,
                    placeholder: <Text id='input.declarationDate'>DeclarationDate</Text>,
                    name: 'declarationDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.declarationDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.termsAndAgreement'>TermsAndAgreement</Text>,
                    placeholder: <Text id='input.termsAndAgreement'>TermsAndAgreement</Text>,
                    name: 'termsAndAgreement',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.termsAndAgreement,
                    showInList: false,
                },
                {
                    title: <Text id='input.declaration'>Declaration</Text>,
                    placeholder: <Text id='input.declaration'>Declaration</Text>,
                    name: 'declaration',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.declaration,
                    showInList: false,
                },
                {
                    title: <Text id='input.license'>License</Text>,
                    placeholder: <Text id='input.license'>License</Text>,
                    name: 'license',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.license,
                    showInList: false,
                },
                {
                    title: <Text id='input.preCheck'>PreCheck</Text>,
                    placeholder: <Text id='input.preCheck'>PreCheck</Text>,
                    name: 'preCheck',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.preCheck,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-name-address-title'>Race Class Contestant name & address</Text>,
            key: 'Race Class Contestant name & address',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.address,
                    showInList: false,
                    isInternalField: true,
                    csvField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                    isInternalField: true,
                    csvField: true,
                    isRecommended: true,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalcode',
                                    value: obj.postalcode,
                                    id: obj.id,
                                });
                                updateValue({
                                    name: 'postalplace',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.postalplace'>Postalplace</Text>,
                    placeholder: <Text id='input.postalplace'>Postalplace</Text>,
                    name: 'postalplace',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.postalplace,
                    showInList: false,
                    csvField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj.country,
                    showInList: false,
                    showInRaceAdminList: true,
                    csvField: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-contact-title'>Race Class Contestant contact info</Text>,
            key: 'Race Class Contestant contact info',
            fields: [
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.email,
                    showInList: true,
                    isInternalField: true,
                    csvField: true,
                    isRecommended: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                    isInternalField: true,
                    noEdit: true,
                    csvField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'text',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.cellphone,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-brands fa-facebook',
                    getValue: obj => obj.facebook,
                    showInList: false,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-brands fa-instagram',
                    getValue: obj => obj.instagram,
                    showInList: false,
                },
                {
                    title: <Text id='input.web'>web</Text>,
                    placeholder: <Text id='input.web'>web</Text>,
                    name: 'web',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.web,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-settings-title'>Race Class Contestant settings</Text>,
            key: 'Race Class Contestant settings',
            fields: [
                {
                    title: <Text id='input.dogs'>Dogs</Text>,
                    placeholder: <Text id='input.dogs'>Dogs</Text>,
                    name: 'dogs',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.dogs,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.obligRest'>ObligRest</Text>,
                    placeholder: <Text id='input.obligRest'>ObligRest</Text>,
                    name: 'obligRest',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.obligRest,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price</Text>,
                    name: 'price',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.price,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.maxTrackIdxToSkip'>MaxTrackIdxToSkip</Text>,
                    placeholder: <Text id='input.maxTrackIdxToSkip'>MaxTrackIdxToSkip</Text>,
                    name: 'maxTrackIdxToSkip',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.maxTrackIdxToSkip,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-qualifying-title'>Race Class qualifying</Text>,
            key: 'Race Class qualifying',
            fields: [
                {
                    title: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    placeholder: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    name: 'veteranRookie',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veteranRookie ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: false,
                    showInRaceAdminList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-about-title'>Race Class About yourself</Text>,
            key: 'Race Class About yourself',
            fields: [
                {
                    title: <Text id='input.occupation'>Occupation</Text>,
                    placeholder: <Text id='input.occupation'>Occupation</Text>,
                    name: 'occupation',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.occupation,
                    showInList: false,
                },

                {
                    title: <Text id='input.hobbies'>Hobbies</Text>,
                    placeholder: <Text id='input.hobbies'>Hobbies</Text>,
                    name: 'hobbies',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hobbies,
                    showInList: false,
                },
                {
                    title: <Text id='input.funFacts'>FunFacts</Text>,
                    placeholder: <Text id='input.funFacts'>FunFacts</Text>,
                    name: 'funFacts',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.funFacts,
                    showInList: false,
                },
                {
                    title: <Text id='input.ambitions'>Ambitions</Text>,
                    placeholder: <Text id='input.ambitions'>Ambitions</Text>,
                    name: 'ambitions',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ambitions,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-about-media-title'>Race Class About Media</Text>,
            key: 'Race Class About Media',
            fields: [
                {
                    title: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    placeholder: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    name: 'localNewsPaper',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.localNewsPaper,
                    showInList: false,
                },
                {
                    title: <Text id='input.okToShareInfoWithMedia'>OkToShareInfoWithMedia</Text>,
                    placeholder: <Text id='input.okToShareInfoWithMedia'>OkToShareInfoWithMedia</Text>,
                    name: 'okToShareInfoWithMedia',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.okToShareInfoWithMedia ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.rnb'>Rnb</Text>,
                    placeholder: <Text id='input.rnb'>Rnb</Text>,
                    name: 'rnb',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rnb ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.sponsors'>Sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>Sponsors</Text>,
                    name: 'sponsors',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                },
                {
                    title: <Text id='input.leadDogs'>LeadDogs</Text>,
                    placeholder: <Text id='input.leadDogs'>LeadDogs</Text>,
                    name: 'leadDogs',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.leadDogs,
                    showInList: false,
                },
                {
                    title: <Text id='input.oldMerits'>OldMerits</Text>,
                    placeholder: <Text id='input.oldMerits'>OldMerits</Text>,
                    name: 'oldMerits',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.oldMerits,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-media-title'>Race Class Contestant media</Text>,
            key: 'Race Class Contestant media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    showInRaceAdminList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
    ],
    raceClassSignup: [
        {
            name: <Text id='input.race-class-signup-basics-title'>Basic info</Text>,
            help: <Text id='input.race-class-signup-basics-help'>Basic info about you. Please fill in all the fields.</Text>,
            key: 'Basic info',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.raceClass'>RaceClass</Text>,
                    placeholder: <Text id='input.raceClass'>RaceClass</Text>,
                    name: 'raceClass',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClass,
                    showInList: true,
                    isAdminField: true,
                },

                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.firstname,
                    showInList: true,
                    noEdit: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.lastname,
                    showInList: true,
                    noEdit: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.sportsClub'>SportsClub</Text>,
                    placeholder: <Text id='input.sportsClub'>SportsClub</Text>,
                    name: 'sportsClub',
                    type: 'text',
                    icon: 'fa-solid fa-people-group',
                    getValue: obj => obj.sportsClub,
                    showInList: true,
                    // Required per race: see lib/raceSignupRequiredFields.js (signupRequireSportsClub).
                },
                {
                    title: <Text id='input.licenseNumber'>License Number</Text>,
                    placeholder: <Text id='input.licenseNumber'>License Number</Text>,
                    name: 'licenseNumber',
                    type: 'text',
                    icon: 'fa-duotone fa-id-card',
                    getValue: obj => obj.licenseNumber,
                    showInList: true,
                },
                {
                    title: <Text id='input.kennel'>kennel</Text>,
                    placeholder: <Text id='input.kennel'>kennel</Text>,
                    name: 'kennel',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.kennel,
                    showInList: true,
                },
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    type: 'date',
                    min: '1900-01-01',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    // Required per race: see lib/raceSignupRequiredFields.js (signupRequireBirth).
                },

                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionFirstname'>companionFirstname</Text>,
                    placeholder: <Text id='input.companionFirstname'>companionFirstname</Text>,
                    name: 'companionFirstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.companionFirstname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionLastname'>companionLastname</Text>,
                    placeholder: <Text id='input.companionLastname'>companionLastname</Text>,
                    name: 'companionLastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.companionLastname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionCellphone'>companionCellphone</Text>,
                    placeholder: <Text id='input.companionCellphone'>companionCellphone</Text>,
                    name: 'companionCellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.companionCellphone,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionBirth'>companionBirth</Text>,
                    placeholder: <Text id='input.companionBirth'>companionBirth</Text>,
                    name: 'companionBirth',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.companionBirth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: true,
                    showInRaceAdminList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-contact-title'>Contact info</Text>,
            help: <Text id='input.race-class-signup-contact-help'>Information about how we can get in contact with you. Also add information about your precense in social media.</Text>,
            key: 'Contact info',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.address,
                    showInList: false,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalcode',
                                    value: obj.postalcode,
                                    id: obj.id,
                                });
                                updateValue({
                                    name: 'postalplace',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.postalplace'>Postalplace</Text>,
                    placeholder: <Text id='input.postalplace'>Postalplace</Text>,
                    name: 'postalplace',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.postalplace,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj.country,
                    showInList: true,
                    // Required per race: see lib/raceSignupRequiredFields.js (signupRequireCountry).
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.email,
                    showInList: true,
                    isRequired: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                    noEdit: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-brands fa-facebook',
                    getValue: obj => obj.facebook,
                    showInList: false,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-brands fa-instagram',
                    getValue: obj => obj.instagram,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-qualifying-title'>Race qualifying</Text>,
            help: <Text id='input.race-class-signup-qualifying-help'>Are you a veteran in this race class? Describe your qualifying for this race.</Text>,
            key: 'Race qualifying',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    placeholder: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    help: <Text id='input.veteranRookie-help'>Did you complete the current race class before?</Text>,
                    name: 'veteranRookie',
                    type: 'toggle',
                    icon: 'fa-solid fa-shield-dog',
                    getValue: obj => obj.veteranRookie ? 1 : 0,
                    showInList: true,
                    showInRaceAdminList: true,
                    // isRequired: true,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    help: <Text id='input.qualifying-help'>Describe your qualifying for this race.</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: true,
                    // Required per race: see lib/raceSignupRequiredFields.js (signupRequireQualifying).
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-about-media-title'>About Media</Text>,
            help: <Text id='input.race-class-signup-about-media-help'>Some information to help us promote the race in a better way. Choose if you allow us to share basic info with the media.</Text>,
            key: 'About Media',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    placeholder: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    name: 'localNewsPaper',
                    type: 'text',
                    icon: 'fa-solid fa-newspaper',
                    getValue: obj => obj.localNewsPaper,
                    showInList: true,
                },
                {
                    title: <Text id='input.okToShareInfoWithMedia'>Ok to share info with media</Text>,
                    placeholder: <Text id='input.okToShareInfoWithMedia'>Ok to share info with media</Text>,
                    help: <Text id='input.okToShareInfoWithMedia-help'>This will make it easier for the local press to write about you and your team.</Text>,
                    name: 'okToShareInfoWithMedia',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.okToShareInfoWithMedia ? 1 : 0,
                    showInList: true,
                    // isRequired: true,
                },
                {
                    title: <Text id='input.rnb'>RNB team</Text>,
                    placeholder: <Text id='input.rnb'>RNB team</Text>,
                    help: <Text id='input.rnb-help'>Are you racing a pure breed, then you should check this field.</Text>,
                    name: 'rnb',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rnb ? 1 : 0,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasPreRaceCheck'>hasPreRaceCheck</Text>,
                    placeholder: <Text id='input.hasPreRaceCheck'>hasPreRaceCheck</Text>,
                    name: 'hasPreRaceCheck',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasPreRaceCheck ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sponsors'>Sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>Sponsors</Text>,
                    help: <Text id='input.sponsors-help'>List your sponsors to promote them on your presentation page.</Text>,
                    name: 'sponsors',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sponsors,
                    showInList: true,
                },
                {
                    title: <Text id='input.leadDogs'>lead Dogs</Text>,
                    placeholder: <Text id='input.leadDogs'>lead Dogs</Text>,
                    name: 'leadDogs',
                    type: 'text',
                    icon: 'fa-solid fa-dog',
                    getValue: obj => obj.leadDogs,
                    showInList: true,
                },
                {
                    title: <Text id='input.oldMerits'>old merits</Text>,
                    placeholder: <Text id='input.oldMerits'>old merits</Text>,
                    help: <Text id='input.oldMerits-help'>Describe your old merits and things you have achived in the sport.</Text>,
                    name: 'oldMerits',
                    type: 'textarea',
                    icon: 'fa-solid fa-medal',
                    getValue: obj => obj.oldMerits,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-about-title'>About yourself</Text>,
            help: <Text id='input.race-class-signup-about-help'>Some information about you. This can be used by the speakers and in our presentation of the race.</Text>,
            key: 'About yourself',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.occupation'>Occupation</Text>,
                    placeholder: <Text id='input.occupation'>Occupation</Text>,
                    name: 'occupation',
                    type: 'text',
                    icon: 'fa-solid fa-briefcase',
                    getValue: obj => obj.occupation,
                    showInList: true,
                },
                {
                    title: <Text id='input.hobbies'>Hobbies</Text>,
                    placeholder: <Text id='input.hobbies'>Hobbies</Text>,
                    help: <Text id='input.hobbies-help'>List your main hobbies.</Text>,
                    name: 'hobbies',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hobbies,
                    showInList: true,
                },
                {
                    title: <Text id='input.funFacts'>fun facts</Text>,
                    placeholder: <Text id='input.funFacts'>fun facts</Text>,
                    help: <Text id='input.funFacts-help'>Describe some fun facts about yourself.</Text>,
                    name: 'funFacts',
                    type: 'textarea',
                    icon: 'fa-solid fa-face-smile',
                    getValue: obj => obj.funFacts,
                    showInList: true,
                },
                {
                    title: <Text id='input.ambitions'>Ambitions</Text>,
                    placeholder: <Text id='input.ambitions'>Ambitions</Text>,
                    help: <Text id='input.ambitions-help'>Your ambitions for this race.</Text>,
                    name: 'ambitions',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ambitions,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-images-file-links-title'>Images, files and links</Text>,
            help: <Text id='input.race-class-signup-images-file-links-help'>Upload some nice images of you. This is going to be used on the webpage and in other presentations of the race.</Text>,
            key: 'Images, files and links',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-help'>Upload a nice image of yourself.</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    showInRaceAdminList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-handler-title'>Race Class Contestant handler</Text>,
            help: <Text id='input.race-class-contestant-handler-help'>Add information about your handler. In some classes it is required to have 2 handlers. If you don't have this sorted out yet, you can add this later.</Text>,
            key: 'Race Class Contestant handler',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.handler1Firstname'>Handler 1 Firstname</Text>,
                    placeholder: <Text id='input.handler1Firstname'>Handler 1 Firstname</Text>,
                    name: 'handler1Firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler1Firstname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler1Lastname'>Handler 1 Lastname</Text>,
                    placeholder: <Text id='input.handler1Lastname'>Handler 1 Lastname</Text>,
                    name: 'handler1Lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler1Lastname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler1Cellphone'>Handler 1 Cellphone</Text>,
                    placeholder: <Text id='input.handler1Cellphone'>Handler 1 Cellphone</Text>,
                    name: 'handler1Cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.handler1Cellphone,
                    showInList: true,
                    showInRaceAdminList: true,
                    isInternalField: true,
                },
                {
                    title: <Text id='input.handler2Firstname'>Handler 2 Firstname</Text>,
                    placeholder: <Text id='input.handler2Firstname'>Handler 2 Firstname</Text>,
                    name: 'handler2Firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler2Firstname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler2Lastname'>Handler 2 Lastname</Text>,
                    placeholder: <Text id='input.handler2Lastname'>Handler 2 Lastname</Text>,
                    name: 'handler2Lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler2Lastname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler2Cellphone'>Handler 2 Cellphone</Text>,
                    placeholder: <Text id='input.handler2Cellphone'>Handler 2 Cellphone</Text>,
                    name: 'handler2Cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.handler2Cellphone,
                    showInList: true,
                    showInRaceAdminList: true,
                    isInternalField: true,
                },
            ],
        },
        // {
        //     name: 'Dogs',
        //     fields: [
        //         {
        //             title: <Text id='input.dogs'>Dogs</Text>,
        //             placeholder: <Text id='input.dogs'>Dogs</Text>,
        //             name: 'dogs',
        //             type: 'arrayObject',
        //             icon: 'fa-solid fa-tag',
        //             getValue: obj => obj.dogs,
        //             datalist: [
        //                 'url', 'name', 'description'
        //             ],
        //             showInList: false,
        //             displayHeight: '60px',
        //             hideOnCreate: false,
        //         },
        //     ],
        // },
    ],
    raceResult: [
        {
            name: <Text id='input.raceresult-basics-title'>Race Result basics</Text>,
            key: 'Race Result basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                    csvField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.raceClass'>raceClass</Text>,
                    placeholder: <Text id='input.raceClass'>raceClass</Text>,
                    name: 'raceClass',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClass,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    placeholder: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    name: 'raceClassContestant',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClassContestant,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.bib'>bib</Text>,
                    placeholder: <Text id='input.bib'>bib</Text>,
                    name: 'bib',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.bib,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.checkpoint'>checkpoint</Text>,
                    placeholder: <Text id='input.checkpoint'>checkpoint</Text>,
                    name: 'checkpoint',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.checkpoint,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.timestamp'>timestamp</Text>,
                    placeholder: <Text id='input.timestamp'>timestamp</Text>,
                    name: 'timestamp',
                    type: 'datetime-local',
                    step: 1,
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.timestamp,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: true,
                    showInRaceAdminList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.eventType'>eventType</Text>,
                    placeholder: <Text id='input.eventType'>eventType</Text>,
                    name: 'eventType',
                    type: 'select',
                    icon: 'fa-duotone fa-calendar-check',
                    getValue: obj => obj.eventType,
                    datalist: [['in', 'In'], ['out', 'Out'], ['scratch', 'Scratch']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.event'>event</Text>,
                    placeholder: <Text id='input.event'>event</Text>,
                    name: 'event',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.eventType,
                    displayValue: (val, obj) => {
                        return (<>
                            {obj.eventType}
                        </>);
                    },
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.dogs'>dogs</Text>,
                    placeholder: <Text id='input.dogs'>dogs</Text>,
                    name: 'dogs',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.dogs,
                    showInList: true,
                },
                {
                    title: <Text id='input.optionalRest'>optionalRest</Text>,
                    placeholder: <Text id='input.optionalRest'>optionalRest</Text>,
                    name: 'optionalRest',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.optionalRest,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.raceresult-media-title'>RaceResult media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'raceresult media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    timeTracker: [
        {
            name: <Text id='input.timetracker-basics-title'>TimeTracker basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'TimeTracker basics',
            fields: [
                {
                    title: <Text id='input.client'>Client</Text>,
                    placeholder: <Text id='input.client'>Client</Text>,
                    name: 'client',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.client,
                    showInList: true,
                },
                {
                    title: <Text id='input.project'>Project</Text>,
                    placeholder: <Text id='input.project'>Project</Text>,
                    name: 'project',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.project,
                    showInList: true,
                },
                {
                    title: <Text id='input.work'>Work</Text>,
                    placeholder: <Text id='input.work'>Work</Text>,
                    name: 'work',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.work,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: false,
                },
                {
                    title: <Text id='input.startTime'>startTime</Text>,
                    placeholder: <Text id='input.startTime'>startTime</Text>,
                    name: 'startTime',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.startTime || null,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.endTime'>endTime</Text>,
                    placeholder: <Text id='input.endTime'>endTime</Text>,
                    name: 'endTime',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.endTime || null,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.totalTime'>totalTime</Text>,
                    placeholder: <Text id='input.totalTime'>totalTime</Text>,
                    name: 'totalTime',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => {
                        const timeUsed = util.dateDiff(obj.startTime, obj.endTime || new Date());
                        return `${util.secToHms(timeUsed.seconds, true)}`;
                    },
                    displayValue: (val, obj) => {
                        const timeUsed = util.dateDiff(obj.startTime, obj.endTime || new Date());
                        return <>
                            {!obj.endTime ? <i class='fa-duotone fa-stopwatch fa-beat-fade' /> : ''} {util.secToHms(timeUsed.seconds, true)}
                        </>;
                    },
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.timetracker-media-title'>timetracker media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'timetracker media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    gpsDevice: [
        {
            name: <Text id='input.gpsDevice-basics-title'>GPS Device</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'GPS Device',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.raceClass'>raceClass</Text>,
                    placeholder: <Text id='input.raceClass'>raceClass</Text>,
                    name: 'raceClass',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClass,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    placeholder: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    name: 'raceClassContestant',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClassContestant,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                // {
                //     title: <Text id='input.calendarId'>calendarId</Text>,
                //     placeholder: <Text id='input.calendarId'>calendarId</Text>,
                //     name: 'calendarId',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.calendarId,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.gpsSerial'>gpsSerial</Text>,
                    placeholder: <Text id='input.gpsSerial'>gpsSerial</Text>,
                    name: 'gpsSerial',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gpsSerial,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.gpsId'>gpsId</Text>,
                    placeholder: <Text id='input.gpsId'>gpsId</Text>,
                    name: 'gpsId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gpsId,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.description,
                },
            ],
        },
        {
            name: <Text id='input.gpsDevice-dates-title'>gpsDevice dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'gpsDevice dates',
            fields: [
                {
                    title: <Text id='input.start'>Start</Text>,
                    placeholder: <Text id='input.start'>Start</Text>,
                    name: 'start',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.start,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.start);
                        const end = new Date(obj.end);
                        if (!obj.end || end < start) {
                            const end = new Date(obj.start);
                            end.setMinutes(end.getMinutes() + 15);
                            updateValue({
                                name: 'end',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.end'>End</Text>,
                    placeholder: <Text id='input.end'>End</Text>,
                    name: 'end',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.end,
                    showInList: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.gpsDevice-media-title'>gpsDevice media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'gpsDevice media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    gpsDevicesAvailable: [
        {
            name: <Text id='input.gpsDevicesAvailable-basics-title'>GPS Devices Available</Text>,
            key: 'GPS Devices Available',
            fields: [
                {
                    title: <Text id='input.id'>id</Text>,
                    placeholder: <Text id='input.id'>id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.gpsId'>GPS ID</Text>,
                    placeholder: <Text id='input.gpsId'>GPS ID</Text>,
                    name: 'gpsId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gpsId,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.gpsSerial'>Serial Number</Text>,
                    placeholder: <Text id='input.gpsSerial'>Serial Number</Text>,
                    name: 'gpsSerial',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gpsSerial,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.status'>Status</Text>,
                    placeholder: <Text id='input.status'>Status</Text>,
                    name: 'status',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.status,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.description,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    isAdminField: true,
                },
            ],
        },
    ],
    stream: [
        {
            name: <Text id='input.stream-basics-title'>Stream</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Stream',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.frontpage'>frontpage</Text>,
                    placeholder: <Text id='input.frontpage'>frontpage</Text>,
                    name: 'frontpage',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.frontpage || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.pinToTop'>Pin to top</Text>,
                    placeholder: <Text id='input.pinToTop'>Pin to top</Text>,
                    name: 'pinToTop',
                    type: 'toggle',
                    icon: 'fa-solid fa-thumbtack',
                    getValue: obj => obj.pinToTop || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.streamId'>streamId</Text>,
                    placeholder: <Text id='input.streamId'>streamId</Text>,
                    name: 'streamId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.streamId,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.streamUrl'>streamUrl</Text>,
                    placeholder: <Text id='input.streamUrl'>streamUrl</Text>,
                    name: 'streamUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.streamUrl,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.description,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.color'>Color</Text>,
                    placeholder: <Text id='input.color-placeholder'>Display color</Text>,
                    name: 'color',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-palette',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.color,
                    showInList: false,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.stream-dates-title'>stream dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'stream dates',
            fields: [
                {
                    title: <Text id='input.start'>Start</Text>,
                    placeholder: <Text id='input.start'>Start</Text>,
                    name: 'start',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.start,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.start);
                        const end = new Date(obj.end);
                        if (!obj.end || end < start) {
                            const end = new Date(obj.start);
                            end.setMinutes(end.getMinutes() + 15);
                            updateValue({
                                name: 'end',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.end'>End</Text>,
                    placeholder: <Text id='input.end'>End</Text>,
                    name: 'end',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.end,
                    showInList: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.stream-media-title'>stream media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'stream media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    streamDevice: [
        {
            name: <Text id='input.streamDevice-basics-title'>streamDevice</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.publicIp'>publicIp</Text>,
                    placeholder: <Text id='input.publicIp'>publicIp</Text>,
                    name: 'publicIp',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.publicIp,
                    showInList: true,
                },
                {
                    title: <Text id='input.ip'>ip</Text>,
                    placeholder: <Text id='input.ip'>ip</Text>,
                    name: 'ip',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ip,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.streamDeviceId'>streamDeviceId</Text>,
                    placeholder: <Text id='input.streamDeviceId'>streamDeviceId</Text>,
                    name: 'streamDeviceId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.streamDeviceId,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.streamName'>streamName</Text>,
                    placeholder: <Text id='input.streamName'>streamName</Text>,
                    name: 'streamName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.streamName,
                    showInList: true,
                    isRecommended: true,
                },

                {
                    title: <Text id='input.videoSize'>videoSize</Text>,
                    placeholder: <Text id='input.videoSize'>videoSize</Text>,
                    name: 'videoSize',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.videoSize,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.frameRate'>frameRate</Text>,
                    placeholder: <Text id='input.frameRate'>frameRate</Text>,
                    name: 'frameRate',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.frameRate,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.videoBitRate'>videoBitRate</Text>,
                    placeholder: <Text id='input.videoBitRate'>videoBitRate</Text>,
                    name: 'videoBitRate',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.videoBitRate,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.description,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.streamDevice-dates-title'>streamDevice dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice dates',
            fields: [
                {
                    title: <Text id='input.date'>date</Text>,
                    placeholder: <Text id='input.date'>date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.date,
                    showInList: true,
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.streamDevice-usercontrol-title'>streamDevice user control</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice user control',
            fields: [
                {
                    title: <Text id='input.brightness'>brightness</Text>,
                    placeholder: <Text id='input.brightness'>brightness</Text>,
                    name: 'brightness',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.brightness,
                    showInList: false,
                },
                {
                    title: <Text id='input.contrast'>contrast</Text>,
                    placeholder: <Text id='input.contrast'>contrast</Text>,
                    name: 'contrast',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.contrast,
                    showInList: false,
                },
                {
                    title: <Text id='input.saturation'>saturation</Text>,
                    placeholder: <Text id='input.saturation'>saturation</Text>,
                    name: 'saturation',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.saturation,
                    showInList: false,
                },
                {
                    title: <Text id='input.white_balance_automatic'>white_balance_automatic</Text>,
                    placeholder: <Text id='input.white_balance_automatic'>white_balance_automatic</Text>,
                    name: 'white_balance_automatic',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.white_balance_automatic,
                    showInList: false,
                },
                {
                    title: <Text id='input.gain'>gain</Text>,
                    placeholder: <Text id='input.gain'>gain</Text>,
                    name: 'gain',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.gain,
                    showInList: false,
                },
                {
                    title: <Text id='input.power_line_frequency'>power_line_frequency</Text>,
                    placeholder: <Text id='input.power_line_frequency'>power_line_frequency</Text>,
                    name: 'power_line_frequency',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.power_line_frequency,
                    showInList: false,
                },
                {
                    title: <Text id='input.white_balance_temperature'>white_balance_temperature</Text>,
                    placeholder: <Text id='input.brightness'>white_balance_temperature</Text>,
                    name: 'white_balance_temperature',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.white_balance_temperature,
                    showInList: false,
                },
                {
                    title: <Text id='input.sharpness'>sharpness</Text>,
                    placeholder: <Text id='input.sharpness'>sharpness</Text>,
                    name: 'sharpness',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sharpness,
                    showInList: false,
                },
                {
                    title: <Text id='input.backlight_compensation'>backlight_compensation</Text>,
                    placeholder: <Text id='input.backlight_compensation'>backlight_compensation</Text>,
                    name: 'backlight_compensation',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.backlight_compensation,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.streamDevice-cameracontrol-title'>streamDevice camera control</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice camera control',
            fields: [
                {
                    title: <Text id='input.auto_exposure'>auto_exposure</Text>,
                    placeholder: <Text id='input.auto_exposure'>auto_exposure</Text>,
                    name: 'auto_exposure',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.auto_exposure,
                    showInList: false,
                },
                {
                    title: <Text id='input.exposure_time_absolute'>exposure_time_absolute</Text>,
                    placeholder: <Text id='input.exposure_time_absolute'>exposure_time_absolute</Text>,
                    name: 'exposure_time_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.exposure_time_absolute,
                    showInList: false,
                },
                {
                    title: <Text id='input.exposure_dynamic_framerate'>exposure_dynamic_framerate</Text>,
                    placeholder: <Text id='input.exposure_dynamic_framerate'>exposure_dynamic_framerate</Text>,
                    name: 'exposure_dynamic_framerate',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.exposure_dynamic_framerate,
                    showInList: false,
                },
                {
                    title: <Text id='input.pan_absolute'>pan_absolute</Text>,
                    placeholder: <Text id='input.pan_absolute'>pan_absolute</Text>,
                    name: 'pan_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.pan_absolute,
                    showInList: false,
                },
                {
                    title: <Text id='input.tilt_absolute'>tilt_absolute</Text>,
                    placeholder: <Text id='input.tilt_absolute'>tilt_absolute</Text>,
                    name: 'tilt_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.tilt_absolute,
                    showInList: false,
                },
                {
                    title: <Text id='input.focus_absolute'>focus_absolute</Text>,
                    placeholder: <Text id='input.focus_absolute'>focus_absolute</Text>,
                    name: 'focus_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.focus_absolute,
                    showInList: false,
                },
                {
                    title: <Text id='input.focus_automatic_continuous'>focus_automatic_continuous</Text>,
                    placeholder: <Text id='input.focus_automatic_continuous'>focus_automatic_continuous</Text>,
                    name: 'focus_automatic_continuous',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.focus_automatic_continuous,
                    showInList: false,
                },
                {
                    title: <Text id='input.zoom_absolute'>zoom_absolute</Text>,
                    placeholder: <Text id='input.zoom_absolute'>zoom_absolute</Text>,
                    name: 'zoom_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.zoom_absolute,
                    showInList: false,
                },
            ]
        },
        {
            name: <Text id='input.streamDevice-media-title'>streamDevice media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    convention: [
        {
            name: <Text id='input.convention-basics-title'>Convention basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    help: <Text id='input.convention-active-help'>Internal status flag. Does not affect who can see the convention - use public for visibility.</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.public'>public</Text>,
                    placeholder: <Text id='input.public'>public</Text>,
                    help: <Text id='input.convention-public-help'>Visible to everyone, including visitors who are not logged in or members. When off, only members can see it.</Text>,
                    name: 'public',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.public || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isCompetition'>isCompetition</Text>,
                    placeholder: <Text id='input.isCompetition'>isCompetition</Text>,
                    help: <Text id='input.isCompetition-help'>Marks this as a competition (stevne) instead of training. Shows the competition badge, counts in yearly competition statistics, ticks Konkurranse on the weapon application, and enables extra-start payment.</Text>,
                    name: 'isCompetition',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isCompetition || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isApproved'>isApproved</Text>,
                    placeholder: <Text id='input.isApproved'>isApproved</Text>,
                    help: <Text id='input.isApproved-help'>Shooters can no longer choose their own lag: an admin places every shooter on a lag. Also marks the convention as approved for official reporting, so it is included in the Rubic report and the weapon-application activity list. Turn it on from the start for Norgesfelt and competitions where the organizers set up the lag.</Text>,
                    name: 'isApproved',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isApproved || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isAdminScoring'>isAdminScoring</Text>,
                    placeholder: <Text id='input.isAdminScoring'>isAdminScoring</Text>,
                    help: <Text id='input.isAdminScoring-help'>Only range officials and convention administrators can register scores. Shooters cannot enter or edit their own results.</Text>,
                    name: 'isAdminScoring',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isAdminScoring || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isCancelled'>Cancelled</Text>,
                    help: <Text id='input.isCancelled-help'>The convention is cancelled. It stays visible, but is clearly marked as cancelled (AVLYST) on lists and the convention page.</Text>,
                    name: 'isCancelled',
                    type: 'toggle',
                    icon: 'fa-solid fa-ban',
                    getValue: obj => obj.isCancelled ? 1 : 0,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.hideParticipants'>Hide participant list</Text>,
                    help: <Text id='input.hideParticipants-help'>Hide the participants (Deltakere) list on the convention page.</Text>,
                    name: 'hideParticipants',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye-slash',
                    getValue: obj => obj.hideParticipants ? 1 : 0,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.hideStages'>Hide round list</Text>,
                    help: <Text id='input.hideStages-help'>Hide the stages/rounds (Lag) list on the convention page.</Text>,
                    name: 'hideStages',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye-slash',
                    getValue: obj => obj.hideStages ? 1 : 0,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.resultsLocked'>resultsLocked</Text>,
                    placeholder: <Text id='input.resultsLocked'>resultsLocked</Text>,
                    help: <Text id='input.resultsLocked-help'>Results are locked and approved.</Text>,
                    name: 'resultsLocked',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultsLocked ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.registrationIsOpen'>Registration Is Open</Text>,
                    placeholder: <Text id='input.registrationIsOpen'>Registration Is Open</Text>,
                    help: <Text id='input.registrationIsOpen-help'>Registration is open for all to signup.</Text>,
                    name: 'registrationIsOpen',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.registrationIsOpen ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.selfSignup'>Self Signup</Text>,
                    placeholder: <Text id='input.selfSignup'>Self Signup</Text>,
                    help: <Text id='input.selfSignup-help'>Allow users to self-signup for stages.</Text>,
                    name: 'selfSignup',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-check',
                    getValue: obj => obj.selfSignup ? 1 : 0,
                    showInList: true,
                },
                {
                    title: 'Signup Product ID',
                    placeholder: 'Product ID for signup fee',
                    help: 'Product ID to charge as signup fee via Vipps.',
                    name: 'signupProduct',
                    type: 'number',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.signupProduct || '',
                    showInList: true,
                },
                {
                    title: 'Signup Product Name',
                    placeholder: 'Product name',
                    help: 'Cached name of the signup fee product.',
                    name: 'signupProductName',
                    type: 'text',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.signupProductName || '',
                    showInList: false,
                },
                {
                    title: 'Signup Product Price',
                    placeholder: 'Price incl. VAT',
                    help: 'Cached price with VAT for the signup fee.',
                    name: 'signupProductPrice',
                    type: 'number',
                    icon: 'fa-solid fa-money-bill',
                    getValue: obj => obj.signupProductPrice || '',
                    showInList: false,
                },
                {
                    title: <Text id='input.isNsfClassLeading'>isNsfClassLeading</Text>,
                    placeholder: <Text id='input.isNsfClassLeading'>isNsfClassLeading</Text>,
                    help: <Text id='input.isNsfClassLeading-help'>Shows the Klasseforende badge - the results count toward NSF class advancement.</Text>,
                    name: 'isNsfClassLeading',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isNsfClassLeading || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.convention-nsfId'>NSF id</Text>,
                    placeholder: <Text id='input.convention-nsfId'>NSF ID</Text>,
                    help: <Text id='input.nsfId-help'>Unique identifier at NSF. Format is an uuidv4</Text>,
                    name: 'nsfId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.nsfId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.nsfNumber'>nsfNumber</Text>,
                    placeholder: <Text id='input.nsfNumber'>nsfNumber</Text>,
                    help: <Text id='input.nsfNumber-help'>Unique number at NSF. Format is an integer.</Text>,
                    name: 'nsfNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.nsfNumber,
                    showInList: true,
                },
                {
                    title: <Text id='input.location'>location</Text>,
                    placeholder: <Text id='input.location'>location</Text>,
                    help: <Text id='input.location-help'>Location of the convention.</Text>,
                    name: 'location',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.location,
                    showInList: true,
                },
                {
                    title: <Text id='input.convention-lat'>Latitude</Text>,
                    placeholder: <Text id='input.convention-lat'>Latitude</Text>,
                    help: <Text id='input.convention-lat-help'>Latitude of the convention location. Used for yr.no weather forecast.</Text>,
                    name: 'lat',
                    type: 'number',
                    icon: 'fa-solid fa-location-crosshairs',
                    getValue: obj => obj.lat,
                    showInList: true,
                },
                {
                    title: <Text id='input.convention-lng'>Longitude</Text>,
                    placeholder: <Text id='input.convention-lng'>Longitude</Text>,
                    help: <Text id='input.convention-lng-help'>Longitude of the convention location. Used for yr.no weather forecast.</Text>,
                    name: 'lng',
                    type: 'number',
                    icon: 'fa-solid fa-location-crosshairs',
                    getValue: obj => obj.lng,
                    showInList: true,
                },
                {
                    title: <Text id='input.organizer'>organizer</Text>,
                    placeholder: <Text id='input.organizer'>organizer</Text>,
                    help: <Text id='input.organizer-help'>Name of the organizer.</Text>,
                    name: 'organizer',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.organizer,
                    showInList: true,
                },
                {
                    title: <Text id='input.judges'>judges</Text>,
                    placeholder: <Text id='input.judges'>judges</Text>,
                    help: <Text id='input.judges-help'>Name of the judges.</Text>,
                    name: 'judges',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.judges,
                    showInList: true,
                },
                {
                    title: 'title',
                    placeholder: 'title',
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    isAdminField: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.ingress'>ingress</Text>,
                    placeholder: <Text id='input.ingress'>ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.ingress,
                    showInList: false,
                    isRecommended: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.body'>body</Text>,
                    placeholder: <Text id='input.body'>body</Text>,
                    name: 'body',
                    type: 'textareaEditor',
                    viewHtml: false,
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                    showInList: false,
                    // hideOnCreate: true,
                },
            ]
        },
        {
            name: <Text id='input.convention-settings-title'>convention settings</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention settings',
            fields: [
                {
                    title: <Text id='input.convention-type'>Convention type</Text>,
                    placeholder: <Text id='input.convention-type'>Convention type</Text>,
                    name: 'type',
                    type: 'select',
                    icon: 'fa-duotone fa-face-thinking',
                    getValue: obj => obj.type,
                    datalist: [['felt', 'Felt'], ['norgesfelt', 'NorgesFelt'], ['hurtig', 'Hurtig'], ['nais', 'NAIS'], ['standard', 'Standard'], ['t-96', 'T-96'], ['finpistol', 'VM-Finpistol'], ['grovpistol', 'VM-Grovpistol'], ['silhuett', 'Silhuettpistol'], ['luftpistol', 'Luftpistol 10m'], ['griseskyting', 'Griseskyting'], ['skarpskytter', 'Skarpskyttermerke'], ['tidsoving', 'Tidsøvelse'], ['tidskonk', 'Tidskonkurranse'], ['fri', 'Fri (poeng)'], ['ipsc', 'IPSC (dynamisk)']],
                    showInList: true,
                },
                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: obj => obj.members?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of shooting instructor.', 'Lastname of shooting instructor.',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.participants'>participants</Text>,
                    placeholder: <Text id='input.participants'>participants</Text>,
                    name: 'participants',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                            <span class='font-weight-lighter'>Date:</span> {obj.signupDate ? util.isoDate(obj.signupDate) : ''}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants1'>participants round 1</Text>,
                    placeholder: <Text id='input.participants1'>participants round 1</Text>,
                    name: 'participants1',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants1?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants2'>participants round 2</Text>,
                    placeholder: <Text id='input.participants2'>participants round 2</Text>,
                    name: 'participants2',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants2?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants3'>participants round 3</Text>,
                    placeholder: <Text id='input.participants3'>participants round 3</Text>,
                    name: 'participants3',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants3?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants4'>participants round 4</Text>,
                    placeholder: <Text id='input.participants4'>participants round 4</Text>,
                    name: 'participants4',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants4?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants5'>participants round 5</Text>,
                    placeholder: <Text id='input.participants5'>participants round 5</Text>,
                    name: 'participants5',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants5?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.stages-rounds'>Rounds</Text>,
                    placeholder: <Text id='input.stages-rounds'>Rounds</Text>,
                    help: <Text id='input.stages-rounds-help'>Stages define the activity in this conventions. Types are: "hurtig", "nais", "norgesfelt", "felt", "standard".</Text>,
                    name: 'stages',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    getValue: obj => obj.stages?.map(m => m),
                    datalist: [
                        'type', 'name', 'stages', 'description',
                    ],
                    datalistHelp: [
                        'Rundetype', 'Navn på runden.', 'Antall standplasser på runden.', 'Beskrivelse av runden.',
                    ],
                    datalistValues: [
                        ['felt', 'norgesfelt', 'nais', 'hurtig', 'standard', 't-96', 'ipsc'], ['Runde 1', 'Runde 2', 'Runde 3'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Type:</span> {obj.type}<br />
                            <span class='font-weight-lighter'>Name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>Stages:</span> {obj.stages}<br />
                            <span class='font-weight-lighter'>Description:</span> {obj.description}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '150px',
                },
            ],
        },
        {
            name: <Text id='input.convention-dates-title'>convention dates</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention dates',
            fields: [
                {
                    title: <Text id='input.startDate'>StartDate</Text>,
                    placeholder: <Text id='input.startDate'>StartDate</Text>,
                    name: 'startDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.startDate,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    callback: (obj, updateValue) => {
                        const startDate = new Date(obj.startDate);
                        const endDate = new Date(obj.endDate);
                        if (!obj.endDate || endDate < startDate) {
                            const end = new Date(obj.startDate);
                            end.setMinutes(end.getMinutes() + 120);
                            updateValue({
                                name: 'endDate',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.endDate,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    minFunc: obj => obj.startDate ? util.isoDate(obj.startDate, { dateTimeSep: 'T' }) : null,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.stageInterval'>stageInterval</Text>,
                    placeholder: <Text id='input.stageInterval'>stageInterval</Text>,
                    help: <Text id='input.stageInterval-help'>Minutes between each stage start.</Text>,
                    name: 'stageInterval',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.stageInterval,
                    showInList: true,
                },
                {
                    title: <Text id='input.stageSlots'>stageSlots</Text>,
                    placeholder: <Text id='input.stageSlots'>stageSlots</Text>,
                    help: <Text id='input.stageSlots-help'>Number of slots in each stage.</Text>,
                    name: 'stageSlots',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.stageSlots,
                    showInList: true,
                },
                {
                    title: <Text id='input.stageGapHours'>Stage gap (hours)</Text>,
                    placeholder: <Text id='input.stageGapHours-placeholder'>2</Text>,
                    help: <Text id='input.stageGapHours-help'>Minimum hours required between stages a user can sign up for. Defaults to 2.</Text>,
                    name: 'stageGapHours',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => (obj.stageGapHours == null ? '' : obj.stageGapHours),
                },
                {
                    title: <Text id='input.registrationStartDate'>registrationStartDate</Text>,
                    placeholder: <Text id='input.registrationStartDate'>registrationStartDate</Text>,
                    name: 'registrationStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.registrationStartDate,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    callback: (obj, updateValue) => {
                        const registrationStartDate = new Date(obj.registrationStartDate);
                        const registrationEndDate = new Date(obj.registrationEndDate);
                        if (!obj.registrationEndDate || registrationEndDate < registrationStartDate) {
                            const end = new Date(obj.registrationStartDate);
                            end.setMinutes(end.getMinutes() + 120);
                            updateValue({
                                name: 'registrationEndDate',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.registrationEndDate'>registrationEndDate</Text>,
                    placeholder: <Text id='input.registrationEndDate'>registrationEndDate</Text>,
                    name: 'registrationEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.registrationEndDate,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    minFunc: obj => obj.registrationStartDate ? util.isoDate(obj.registrationStartDate, { dateTimeSep: 'T' }) : null,
                    showInList: false,
                },

                {
                    title: <Text id='input.stageStands'>stageStands</Text>,
                    placeholder: <Text id='input.stageStands'>stageStands</Text>,
                    name: 'stageStands',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.stageStands?.map(m => m),
                    datalist: [
                        'stand', 'scoreHits', 'scoreGroups', 'scoreTargets', 'scoreInner',
                        'shootingTime', 'shootingPosition', 'magnumShootingTime', 'magnumShootingPosition', 'magnumNotes', 'notes',
                        'ipscPaperTargets', 'ipscHitsPerPaper', 'ipscPoppers', 'ipscPlates', 'ipscNoShoots',
                        'ipscScoringMethod', 'ipscMinRounds', 'ipscBrief',
                    ],
                    datalistHelp: [
                        'stand #', 'Total hits', 'Total groups', 'Total targets.', 'Total iX.',
                        'Shooting time (seconds)', 'Shooting position (free text)',
                        'Magnum: shooting time (seconds)', 'Magnum: shooting position (free text)',
                        'Magnum: extra info (e.g. "Magnum skyter ikke på stålet")', 'General notes',
                        'IPSC: paper targets', 'IPSC: hits per paper (default 2)', 'IPSC: poppers', 'IPSC: plates', 'IPSC: no-shoots',
                        'IPSC: scoring method (comstock)', 'IPSC: min rounds', 'IPSC: stage brief',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Stand:</span> {obj.stand}<br />
                            <span class='font-weight-lighter'>scoreHits:</span> {obj.scoreHits}<br />
                            <span class='font-weight-lighter'>scoreGroups:</span> {obj.scoreGroups}<br />
                            <span class='font-weight-lighter'>scoreTargets:</span> {obj.scoreTargets}<br />
                            <span class='font-weight-lighter'>scoreInner:</span> {obj.scoreInner}<br />
                            {obj.shootingTime > 0 && <><span class='font-weight-lighter'>shootingTime:</span> {obj.shootingTime}s<br /></>}
                            {!!obj.shootingPosition && <><span class='font-weight-lighter'>shootingPosition:</span> {obj.shootingPosition}<br /></>}
                            {obj.magnumShootingTime > 0 && <><span class='font-weight-lighter'>magnumShootingTime:</span> {obj.magnumShootingTime}s<br /></>}
                            {!!obj.magnumShootingPosition && <><span class='font-weight-lighter'>magnumShootingPosition:</span> {obj.magnumShootingPosition}<br /></>}
                            {!!obj.magnumNotes && <><span class='font-weight-lighter'>magnumNotes:</span> {obj.magnumNotes}<br /></>}
                            {!!obj.notes && <><span class='font-weight-lighter'>notes:</span> {obj.notes}<br /></>}
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.stageStandsSpecial'>Max – Special (pistol/revolver)</Text>,
                    placeholder: <Text id='input.stageStandsSpecial'>Max – Special (pistol/revolver)</Text>,
                    help: <Text id='input.stageStandsSpecial-help'>Per-stand max for special pistol/revolver (5 shots). Leave empty to fall back to the default max capped at 5.</Text>,
                    name: 'stageStandsSpecial',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    getValue: obj => obj.stageStandsSpecial?.map(m => m),
                    datalist: [
                        'stand', 'scoreHits', 'scoreGroups', 'scoreTargets', 'scoreInner',
                        'shootingTime', 'shootingPosition', 'notes',
                    ],
                    datalistHelp: [
                        'stand #', 'Total hits', 'Total groups', 'Total targets.', 'Total iX.',
                        'Shooting time (seconds)', 'Shooting position (free text)', 'General notes',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Stand:</span> {obj.stand}<br />
                            <span class='font-weight-lighter'>scoreHits:</span> {obj.scoreHits}<br />
                            <span class='font-weight-lighter'>scoreGroups:</span> {obj.scoreGroups}<br />
                            <span class='font-weight-lighter'>scoreTargets:</span> {obj.scoreTargets}<br />
                            <span class='font-weight-lighter'>scoreInner:</span> {obj.scoreInner}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.stageStandsMagnum'>Max – Magnum</Text>,
                    placeholder: <Text id='input.stageStandsMagnum'>Max – Magnum</Text>,
                    help: <Text id='input.stageStandsMagnum-help'>Per-stand max for magnum (5 shots). Leave empty to fall back to the default max capped at 5.</Text>,
                    name: 'stageStandsMagnum',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    getValue: obj => obj.stageStandsMagnum?.map(m => m),
                    datalist: [
                        'stand', 'scoreHits', 'scoreGroups', 'scoreTargets', 'scoreInner',
                        'shootingTime', 'shootingPosition', 'notes',
                    ],
                    datalistHelp: [
                        'stand #', 'Total hits', 'Total groups', 'Total targets.', 'Total iX.',
                        'Shooting time (seconds)', 'Shooting position (free text)', 'General notes',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Stand:</span> {obj.stand}<br />
                            <span class='font-weight-lighter'>scoreHits:</span> {obj.scoreHits}<br />
                            <span class='font-weight-lighter'>scoreGroups:</span> {obj.scoreGroups}<br />
                            <span class='font-weight-lighter'>scoreTargets:</span> {obj.scoreTargets}<br />
                            <span class='font-weight-lighter'>scoreInner:</span> {obj.scoreInner}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.stageStandImages'>Stand Images</Text>,
                    placeholder: <Text id='input.stageStandImages'>Stand Images</Text>,
                    name: 'stageStandImages',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.stageStandImages,
                    showInList: false,
                },
                {
                    title: <Text id='input.rangeDescription'>Range description for the illustration</Text>,
                    placeholder: <Text id='input.rangeDescription-placeholder'>Open-fronted wooden shelter with red-painted frames, a long red bench as the firing line…</Text>,
                    help: <Text id='input.rangeDescription-help'>How the range looks: shelter, firing line, berm, targets, surroundings. Used as the scene in the AI-drawn recap illustration. English works best.</Text>,
                    name: 'rangeDescription',
                    type: 'textarea',
                    icon: 'fa-solid fa-mountain-sun',
                    getValue: obj => obj.rangeDescription,
                    showInList: false,
                },


            ],
        },
        {
            name: <Text id='input.convention-media-title'>convention media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention media',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    help: <Text id='input.tags-help'>Tags are used to find similar news. Add as many as you like.</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: 'Autoplay Video',
                    placeholder: 'Autoplay Video',
                    help: <Text id='input.autoplayVideo-help'>Autoplay the first video (muted, looping) in the convention header instead of the image gallery.</Text>,
                    name: 'autoplayVideo',
                    type: 'toggle',
                    icon: 'fa-solid fa-video',
                    getValue: obj => obj.autoplayVideo || null,
                    showInList: false,
                },

            ]
        },
        {
            name: <Text id='input.convention-sponsors-title'>convention sponsors</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention sponsors',
            fields: [
                {
                    title: <Text id='input.sponsors'>Sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>Sponsors</Text>,
                    name: 'sponsors',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                },
                {
                    title: <Text id='input.sponsorsMeta'>Sponsors meta</Text>,
                    placeholder: <Text id='input.sponsorsMeta'>Sponsors meta</Text>,
                    name: 'sponsorsMeta',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.sponsorsMeta,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>url:</span> {obj.url}<br />
                            <span class='font-weight-lighter'>name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>description:</span> {obj.description}<br />
                        </div>
                    </>,
                    showInList: false,
                    displayHeight: '100px',
                    displayWidth: '300px',
                },
            ],
        },
    ],
    conventionResult: [
        {
            name: <Text id='input.conventionResult-basics-title'>conventionResult basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'conventionResult',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.convention'>convention</Text>,
                    placeholder: <Text id='input.convention'>convention</Text>,
                    name: 'convention',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.convention,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.targetIndex'>targetIndex</Text>,
                    placeholder: <Text id='input.targetIndex'>targetIndex</Text>,
                    name: 'targetIndex',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.targetIndex,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.stage'>stage</Text>,
                    placeholder: <Text id='input.stage'>stage</Text>,
                    name: 'stage',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.stage,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.stand'>stand</Text>,
                    placeholder: <Text id='input.stand'>stand</Text>,
                    name: 'stand',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.stand,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.participant'>participant</Text>,
                    placeholder: <Text id='input.participant'>participant</Text>,
                    name: 'participant',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.participant,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'title',
                    placeholder: 'title',
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isAdminField: false,
                },

                {
                    title: <Text id='input.scoreTotal'>scoreTotal</Text>,
                    placeholder: <Text id='input.scoreTotal'>scoreTotal</Text>,
                    name: 'scoreTotal',
                    type: 'forceNumber',
                    pattern: '[0-9]*',
                    inputmode: 'numeric',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.scoreTotal,
                    showInList: true,
                },
                {
                    title: <Text id='input.scoreHits'>scoreHits</Text>,
                    placeholder: <Text id='input.scoreHits'>scoreHits</Text>,
                    name: 'scoreHits',
                    type: 'forceNumber',
                    pattern: '[0-9]*',
                    inputmode: 'numeric',
                    icon: 'fa-solid fa-input-numeric',
                    step: 1,
                    min: 0,
                    max: 6,
                    getValue: obj => obj.scoreHits,
                    showInList: true,
                },
                {
                    title: <Text id='input.scoreTargets'>scoreTargets</Text>,
                    placeholder: <Text id='input.scoreTargets'>scoreTargets</Text>,
                    name: 'scoreTargets',
                    type: 'forceNumber',
                    pattern: '[0-9]*',
                    inputmode: 'numeric',
                    icon: 'fa-solid fa-input-numeric',
                    step: 1,
                    min: 0,
                    max: 6,
                    getValue: obj => obj.scoreTargets,
                    showInList: true,
                },
                {
                    title: <Text id='input.scoreInner'>scoreInner</Text>,
                    placeholder: <Text id='input.scoreInner'>scoreInner</Text>,
                    name: 'scoreInner',
                    type: 'forceNumber',
                    pattern: '[0-9]*',
                    inputmode: 'numeric',
                    icon: 'fa-solid fa-input-numeric',
                    step: 1,
                    min: 0,
                    max: 6,
                    getValue: obj => obj.scoreInner,
                    showInList: true,
                },
                {
                    title: <Text id='input.scorePenalty'>scorePenalty</Text>,
                    placeholder: <Text id='input.scorePenalty'>scorePenalty</Text>,
                    name: 'scorePenalty',
                    type: 'forceNumber',
                    icon: 'fa-solid fa-input-numeric',
                    step: 1,
                    min: 0,
                    max: 6,
                    getValue: obj => obj.scorePenalty,
                    showInList: true,
                },

            ]
        },
        {
            name: <Text id='input.conventionResult-media-title'>conventionResult media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'conventionResult media',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    help: <Text id='input.tags-help'>Tags are used to find similar news. Add as many as you like.</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },

            ]
        },
    ],
    competitionResult: [
        {
            name: <Text id='input.competitionResult-basics-title'>competitionResult basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'competitionResult',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.date'>date</Text>,
                    placeholder: <Text id='input.date'>date</Text>,
                    name: 'date',
                    type: 'date',
                    min: '2020-01-01',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    displayValue: obj => obj ? `${util.formatDate(obj, { locale: 'nb-NO' })}` : null,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.title'>title</Text>,
                    placeholder: <Text id='input.title'>title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.competitionId'>competitionId</Text>,
                    placeholder: <Text id='input.competitionId'>competitionId</Text>,
                    name: 'competitionId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.competitionId,
                    showInList: true,
                },
                {
                    title: <Text id='input.organizer'>organizer</Text>,
                    placeholder: <Text id='input.organizer'>organizer</Text>,
                    name: 'organizer',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.organizer,
                    showInList: true,
                },
                {
                    title: <Text id='input.place'>place</Text>,
                    placeholder: <Text id='input.place'>place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.place,
                    showInList: true,
                },

                {
                    title: <Text id='input.program'>program</Text>,
                    placeholder: <Text id='input.program'>program</Text>,
                    name: 'program',
                    type: 'select',
                    icon: 'fa-duotone fa-face-thinking',
                    getValue: obj => obj.type,
                    datalist: [
                        ['Fin', 'Fin'],
                        ['Grov', 'Grov'],
                        ['Militær', 'Militær'],
                        ['Revolver', 'Revolver'],
                        ['Sp.revolver', 'Sp.revolver'],
                        ['Sp.pistol', 'Sp.pistol'],
                        ['Mag 1', 'Mag 1'],
                        ['Mag 2', 'Mag 2'],
                        ['Militær RP', 'Militær RP'],
                        ['Revolver RP', 'Revolver RP'],
                    ],
                    showInList: true,
                },

                {
                    title: <Text id='input.class'>class</Text>,
                    placeholder: <Text id='input.class'>class</Text>,
                    name: 'class',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.class,
                    showInList: true,
                },
                {
                    title: <Text id='input.rank'>rank</Text>,
                    placeholder: <Text id='input.rank'>rank</Text>,
                    name: 'rank',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.rank,
                    showInList: true,
                },
                {
                    title: <Text id='input.score'>score</Text>,
                    placeholder: <Text id='input.score'>score</Text>,
                    name: 'score',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.score,
                    showInList: true,
                },
                {
                    title: <Text id='input.promotionClass'>promotionClass</Text>,
                    placeholder: <Text id='input.promotionClass'>promotionClass</Text>,
                    name: 'promotionClass',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.promotionClass,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>country</Text>,
                    placeholder: <Text id='input.country'>country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.country,
                    showInList: true,
                },
                {
                    title: <Text id='input.url'>url</Text>,
                    placeholder: <Text id='input.url'>url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.url,
                    showInList: true,
                },
            ]
        },
        {
            name: <Text id='input.competitionResult-media-title'>competitionResult media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'competitionResult media',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    help: <Text id='input.tags-help'>Tags are used to find similar news. Add as many as you like.</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                },

            ]
        },
    ],
    coffee: [
        {
            name: 'Coffee Card Image',
            key: 'Coffee Card Image',
            isCollapsed: false,
            backgroundColor: 'var(--bs-primary-bg-subtle) !important',
            borderColor: 'var(--bs-primary-border-subtle) !important',
            fields: [
                {
                    title: 'Images',
                    placeholder: 'Upload coffee card image to extract data with AI',
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-camera',
                    getValue: obj => obj.images,
                    showInList: false,
                },
            ],
        },
        {
            name: 'Coffee Info',
            key: 'Coffee Info',
            isCollapsed: false,
            backgroundColor: 'var(--bs-info-bg-subtle) !important',
            borderColor: 'var(--bs-info-border-subtle) !important',
            fields: [
                {
                    title: 'Name',
                    placeholder: 'Coffee name',
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-coffee',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: 'Period',
                    placeholder: 'Delivery period (e.g., September 2025)',
                    name: 'period',
                    type: 'text',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.period,
                    showInList: true,
                },
                {
                    title: 'Flavor',
                    placeholder: 'Flavor description',
                    name: 'flavor',
                    type: 'textarea',
                    icon: 'fa-solid fa-lemon',
                    getValue: obj => obj.flavor,
                    showInList: true,
                },
                {
                    title: 'Notes',
                    placeholder: 'Your personal tasting notes',
                    name: 'notes',
                    type: 'textarea',
                    icon: 'fa-solid fa-note-sticky',
                    getValue: obj => obj.notes,
                    showInList: false,
                },
            ],
        },
        {
            name: 'Origin',
            key: 'Origin',
            isCollapsed: true,
            fields: [
                {
                    title: 'Country',
                    placeholder: 'Country of origin',
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj.country,
                    showInList: true,
                },
                {
                    title: 'Location',
                    placeholder: 'Specific location/region',
                    name: 'location',
                    type: 'text',
                    icon: 'fa-solid fa-map-marker',
                    getValue: obj => obj.location,
                    showInList: false,
                },
                {
                    title: 'Producer',
                    placeholder: 'Producer or farm name',
                    name: 'producer',
                    type: 'text',
                    icon: 'fa-solid fa-farm',
                    getValue: obj => obj.producer,
                    showInList: true,
                },
                {
                    title: 'Altitude',
                    placeholder: 'Altitude (e.g., 1,950 masl)',
                    name: 'altitude',
                    type: 'text',
                    icon: 'fa-solid fa-mountain',
                    getValue: obj => obj.altitude,
                    showInList: false,
                },
            ],
        },
        {
            name: 'Coffee Details',
            key: 'Coffee Details',
            isCollapsed: true,
            fields: [
                {
                    title: 'Variety',
                    placeholder: 'Coffee variety (e.g., Mixed Heirloom)',
                    name: 'variety',
                    type: 'text',
                    icon: 'fa-solid fa-seedling',
                    getValue: obj => obj.variety,
                    showInList: false,
                },
                {
                    title: 'Process',
                    placeholder: 'Processing method',
                    name: 'process',
                    type: 'text',
                    icon: 'fa-solid fa-cogs',
                    getValue: obj => obj.process,
                    showInList: true,
                },
                {
                    title: 'Harvest',
                    placeholder: 'Harvest period',
                    name: 'harvest',
                    type: 'text',
                    icon: 'fa-solid fa-calendar-alt',
                    getValue: obj => obj.harvest,
                    showInList: false,
                },
            ],
        },
        {
            name: 'Roaster',
            key: 'Roaster',
            isCollapsed: true,
            fields: [
                {
                    title: 'Roastery',
                    placeholder: 'Roastery name',
                    name: 'roastery',
                    type: 'text',
                    icon: 'fa-solid fa-fire',
                    getValue: obj => obj.roastery,
                    showInList: true,
                },
                {
                    title: 'Roaster Machine',
                    placeholder: 'Roasting machine (e.g., Probat P25)',
                    name: 'roasterMachine',
                    type: 'text',
                    icon: 'fa-solid fa-industry',
                    getValue: obj => obj.roasterMachine,
                    showInList: false,
                },
                {
                    title: 'Roaster City',
                    placeholder: 'City',
                    name: 'roasterCity',
                    type: 'text',
                    icon: 'fa-solid fa-city',
                    getValue: obj => obj.roasterCity,
                    showInList: false,
                },
                {
                    title: 'Roaster Country',
                    placeholder: 'Country',
                    name: 'roasterCountry',
                    type: 'text',
                    icon: 'fa-solid fa-flag',
                    getValue: obj => obj.roasterCountry,
                    showInList: false,
                },
                {
                    title: 'Website',
                    placeholder: 'Roaster website',
                    name: 'roasterWeb',
                    type: 'url',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj.roasterWeb,
                    showInList: false,
                },
                {
                    title: 'Social',
                    placeholder: 'Social media handle',
                    name: 'roasterSocial',
                    type: 'text',
                    icon: 'fa-brands fa-instagram',
                    getValue: obj => obj.roasterSocial,
                    showInList: false,
                },
            ],
        },
        {
            name: 'Scores',
            key: 'Scores',
            isCollapsed: true,
            fields: [
                {
                    title: 'Your Score',
                    placeholder: 'Your personal score',
                    name: 'score',
                    type: 'number',
                    min: 0,
                    max: 100,
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.score,
                    showInList: true,
                },
                {
                    title: 'Cupping Score',
                    placeholder: 'Professional cupping score',
                    name: 'cuppingScore',
                    type: 'number',
                    min: 0,
                    max: 100,
                    icon: 'fa-solid fa-award',
                    getValue: obj => obj.cuppingScore,
                    showInList: false,
                },
            ],
        },
        {
            name: 'Tasting Ratings (1-10)',
            key: 'Tasting Ratings',
            isCollapsed: true,
            fields: [
                {
                    title: 'Aroma',
                    placeholder: 'Aroma (1-10)',
                    name: 'aroma',
                    type: 'range',
                    min: 1,
                    max: 10,
                    icon: 'fa-solid fa-wind',
                    getValue: obj => obj.aroma,
                    showInList: false,
                },
                {
                    title: 'Acidity',
                    placeholder: 'Acidity (1-10)',
                    name: 'acidity',
                    type: 'range',
                    min: 1,
                    max: 10,
                    icon: 'fa-solid fa-lemon',
                    getValue: obj => obj.acidity,
                    showInList: false,
                },
                {
                    title: 'Sweetness',
                    placeholder: 'Sweetness (1-10)',
                    name: 'sweetness',
                    type: 'range',
                    min: 1,
                    max: 10,
                    icon: 'fa-solid fa-candy-cane',
                    getValue: obj => obj.sweetness,
                    showInList: false,
                },
                {
                    title: 'Body',
                    placeholder: 'Body (1-10)',
                    name: 'body',
                    type: 'range',
                    min: 1,
                    max: 10,
                    icon: 'fa-solid fa-weight-hanging',
                    getValue: obj => obj.body,
                    showInList: false,
                },
                {
                    title: 'Flavor Rating',
                    placeholder: 'Flavor (1-10)',
                    name: 'flavorRating',
                    type: 'range',
                    min: 1,
                    max: 10,
                    icon: 'fa-solid fa-face-smile',
                    getValue: obj => obj.flavorRating,
                    showInList: false,
                },
            ],
        },
    ],
    course: [
        {
            name: <Text id='input.course-basics-title'>Course basics</Text>,
            key: 'Course basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.public'>Public</Text>,
                    placeholder: <Text id='input.public'>Public</Text>,
                    name: 'public',
                    type: 'toggle',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj.public ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-text',
                    getValue: obj => obj.body,
                    showInList: false,
                },
                {
                    title: <Text id='input.type'>Type</Text>,
                    placeholder: <Text id='input.type'>Type</Text>,
                    name: 'type',
                    type: 'text',
                    icon: 'fa-solid fa-tags',
                    getValue: obj => obj.type,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.course-dates-title'>Course dates</Text>,
            key: 'Course dates',
            fields: [
                {
                    title: <Text id='input.dates'>Course dates</Text>,
                    placeholder: <Text id='input.dates'>Course dates</Text>,
                    help: <Text id='input.dates-help'>Add multiple dates for this course. Each date can have its own time, slots, price, and location.</Text>,
                    name: 'dates',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-calendar-days',
                    getValue: obj => obj.dates?.map(m => m),
                    datalist: [
                        'title', 'startTime', 'endTime', 'slots', 'price', 'location', 'description',
                    ],
                    datalistHelp: {
                        title: 'Optional title for this date',
                        startTime: 'Start date and time (YYYY-MM-DD HH:mm)',
                        endTime: 'End date and time (YYYY-MM-DD HH:mm)',
                        slots: 'Max number of participants (0 = unlimited)',
                        price: 'Price override (leave empty to use course price)',
                        location: 'Location name',
                        description: 'Additional notes for this date',
                    },
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            {obj.title && <><span class='font-weight-bold'>{obj.title}</span><br /></>}
                            <span class='font-weight-lighter'>Date:</span> {obj.startTime ? util.formatDate(obj.startTime, {
                                hour12: false,
                                hour: '2-digit',
                                minute: '2-digit',
                                day: '2-digit',
                                month: 'short',
                                year: '2-digit',
                                locale: 'nb-NO',
                            }) : 'Not set'}<br />
                            {obj.endTime && <><span class='font-weight-lighter'>End:</span> {util.formatDate(obj.endTime, {
                                hour12: false,
                                hour: '2-digit',
                                minute: '2-digit',
                                locale: 'nb-NO',
                            })}<br /></>}
                            <span class='font-weight-lighter'>Slots:</span> {obj.slots || 'Unlimited'}<br />
                            {obj.price && <><span class='font-weight-lighter'>Price:</span> {obj.price} kr<br /></>}
                            {obj.location && <><span class='font-weight-lighter'>Location:</span> {obj.location}<br /></>}
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'large',
                    displayWidth: '250px',
                    displayHeight: '120px',
                },
            ],
        },
        {
            name: <Text id='input.course-registration-title'>Registration settings</Text>,
            key: 'Course registration',
            fields: [
                {
                    title: <Text id='input.registrationIsOpen'>Registration Is Open</Text>,
                    placeholder: <Text id='input.registrationIsOpen'>Registration Is Open</Text>,
                    name: 'registrationIsOpen',
                    type: 'toggle',
                    icon: 'fa-solid fa-door-open',
                    getValue: obj => obj.registrationIsOpen ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.selfSignup'>Self Signup</Text>,
                    placeholder: <Text id='input.selfSignup'>Self Signup</Text>,
                    help: <Text id='input.selfSignup-help'>Allow users to self-signup for dates.</Text>,
                    name: 'selfSignup',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-check',
                    getValue: obj => obj.selfSignup ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.registrationStartDate'>Registration Start Date</Text>,
                    placeholder: <Text id='input.registrationStartDate'>Registration Start Date</Text>,
                    name: 'registrationStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar-plus',
                    getValue: obj => obj.registrationStartDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.registrationEndDate'>Registration End Date</Text>,
                    placeholder: <Text id='input.registrationEndDate'>Registration End Date</Text>,
                    name: 'registrationEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar-minus',
                    getValue: obj => obj.registrationEndDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.courseExam'>Exam after the course</Text>,
                    placeholder: <Text id='input.courseExam'>Exam after the course</Text>,
                    help: <Text id='input.courseExam-help'>Participants are invited to this exam when you click «Send exam» on the course page. Exams are made under Prøve → Administrer.</Text>,
                    name: 'exam',
                    type: 'select',
                    icon: 'fa-solid fa-bullseye',
                    getValue: obj => obj.exam || '',
                    datalistName: 'examOptions',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}${e.isActive === false ? ' (inaktiv)' : ''}`,
                    addToDatalist: [0, '— Ingen prøve —'],
                    showInList: false,
                },
                {
                    title: <Text id='input.slots'>Slots</Text>,
                    placeholder: <Text id='input.slots-placeholder'>0 = unlimited</Text>,
                    help: <Text id='input.slots-help'>Maximum number of participants. Set to 0 for unlimited.</Text>,
                    name: 'slots',
                    type: 'number',
                    icon: 'fa-solid fa-users',
                    getValue: obj => obj.slots,
                    showInList: true,
                },
                {
                    title: <Text id='input.signupFields'>Signup fields</Text>,
                    placeholder: <Text id='input.signupFields'>Signup fields</Text>,
                    help: <Text id='input.signupFields-help'>Custom fields to collect during signup (e.g., child's name, age, etc.)</Text>,
                    name: 'signupFields',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.signupFields,
                    showInList: false,
                    fields: {
                        name: 'Field name (internal)',
                        label: 'Label (displayed to user)',
                        type: 'Type (text, number, email, tel, date, select)',
                        options: 'Options for select (comma separated)',
                        required: 'Required (true/false)',
                    },
                },
            ],
        },
        {
            name: <Text id='input.course-pricing-title'>Pricing</Text>,
            key: 'Course pricing',
            fields: [
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-coins',
                    getValue: obj => obj.price,
                    showInList: true,
                },
                {
                    title: <Text id='input.vat'>VAT %</Text>,
                    placeholder: <Text id='input.vat'>VAT %</Text>,
                    name: 'vat',
                    type: 'number',
                    icon: 'fa-solid fa-percent',
                    getValue: obj => obj.vat,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.course-location-title'>Location</Text>,
            key: 'Course location',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-location-dot',
                    getValue: obj => obj.address,
                    showInList: false,
                },
                {
                    title: <Text id='input.postalcode'>Postal code</Text>,
                    placeholder: <Text id='input.postalcode'>Postal code</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-mailbox',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                },
                {
                    title: <Text id='input.place'>Place</Text>,
                    placeholder: <Text id='input.place'>Place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-city',
                    getValue: obj => obj.place,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-flag',
                    getValue: obj => obj.country,
                    showInList: false,
                },
                {
                    title: <Text id='input.url'>URL</Text>,
                    placeholder: <Text id='input.url'>URL</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.url,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.course-media-title'>Media</Text>,
            key: 'Course media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tags',
                    getValue: obj => obj.tags,
                    showInList: false,
                },
            ],
        },
    ],
    courseDate: [
        {
            name: <Text id='input.courseDate-basics-title'>Date settings</Text>,
            key: 'CourseDate basics',
            fields: [
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.date'>Date</Text>,
                    placeholder: <Text id='input.date'>Date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    showInList: true,
                },
                {
                    title: <Text id='input.endDate'>End date</Text>,
                    placeholder: <Text id='input.endDate'>End date</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.endDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.slots'>Slots</Text>,
                    placeholder: <Text id='input.slots'>Slots (0 = unlimited)</Text>,
                    help: <Text id='input.slots-help'>Maximum number of participants. Set to 0 for unlimited.</Text>,
                    name: 'slots',
                    type: 'number',
                    icon: 'fa-solid fa-users',
                    getValue: obj => obj.slots,
                    showInList: true,
                },
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price (override)</Text>,
                    help: <Text id='input.price-override-help'>Leave empty to use course default price.</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-coins',
                    getValue: obj => obj.price,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-text',
                    getValue: obj => obj.description,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.courseDate-location-title'>Date location</Text>,
            key: 'CourseDate location',
            fields: [
                {
                    title: <Text id='input.location'>Location</Text>,
                    placeholder: <Text id='input.location'>Location name</Text>,
                    name: 'location',
                    type: 'text',
                    icon: 'fa-solid fa-location-dot',
                    getValue: obj => obj.location,
                    showInList: true,
                },
                {
                    title: <Text id='input.lat'>Latitude</Text>,
                    placeholder: <Text id='input.lat'>Latitude</Text>,
                    name: 'lat',
                    type: 'number',
                    icon: 'fa-solid fa-map-pin',
                    getValue: obj => obj.lat,
                    showInList: false,
                },
                {
                    title: <Text id='input.lon'>Longitude</Text>,
                    placeholder: <Text id='input.lon'>Longitude</Text>,
                    name: 'lon',
                    type: 'number',
                    icon: 'fa-solid fa-map-pin',
                    getValue: obj => obj.lon,
                    showInList: false,
                },
            ],
        },
    ],
    // ── E-learning (C module) ──────────────────────────────────────────────
    elearningCourse: [
        {
            name: <Text id='input.elearningCourse-basics-title'>E-learning course</Text>,
            key: 'E-learning course basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    help: <Text id='input.elearningCourse-active-help'>Inactive courses are hidden from the list. Progress and badges are kept.</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-toggle-on',
                    getValue: obj => (obj.active ? 1 : 0),
                    showInList: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.elearningCourse-ingress-placeholder'>Short description shown on the course card</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.ingress,
                    showInList: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.elearningCourse-images-help'>The first image is the cover on the course card.</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.estimatedMinutes'>Estimated minutes</Text>,
                    placeholder: <Text id='input.estimatedMinutes'>Estimated minutes</Text>,
                    name: 'estimatedMinutes',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj.estimatedMinutes,
                    showInList: true,
                },
                {
                    title: <Text id='input.sortOrder'>Sort order</Text>,
                    placeholder: <Text id='input.sortOrder'>Sort order</Text>,
                    name: 'sortOrder',
                    type: 'number',
                    icon: 'fa-solid fa-arrow-down-1-9',
                    getValue: obj => obj.sortOrder,
                    showInList: true,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                },
            ],
        },
        {
            name: <Text id='input.elearningCourse-badge-title'>Badge and exam</Text>,
            key: 'E-learning course badge',
            fields: [
                {
                    title: <Text id='input.badgeTitle'>Badge title</Text>,
                    placeholder: <Text id='input.badgeTitle'>Badge title</Text>,
                    help: <Text id='input.badgeTitle-help'>Name of the badge shown on the user's profile. Defaults to the course title.</Text>,
                    name: 'badgeTitle',
                    type: 'text',
                    icon: 'fa-solid fa-award',
                    getValue: obj => obj.badgeTitle,
                    showInList: true,
                },
                {
                    title: <Text id='input.badgeImages'>Badge image</Text>,
                    placeholder: <Text id='input.badgeImages'>Badge image</Text>,
                    name: 'badgeImages',
                    type: 'image',
                    icon: 'fa-solid fa-medal',
                    getValue: obj => obj.badgeImages,
                    showInList: false,
                },
                {
                    title: <Text id='input.elearningExam'>Exam required</Text>,
                    placeholder: <Text id='input.elearningExam'>Exam required</Text>,
                    help: <Text id='input.elearningExam-help'>The badge is granted when this exam is passed. Choose «No exam» to grant it when all lessons are done. Exams are made under Prøve → Administrer.</Text>,
                    name: 'exam',
                    type: 'select',
                    icon: 'fa-solid fa-clipboard-check',
                    getValue: obj => obj.exam || '',
                    datalistName: 'examOptions',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}${e.isActive === false ? ' (inaktiv)' : ''}`,
                    addToDatalist: [0, '— Ingen prøve —'],
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.elearningCourse-domains-title'>Domains</Text>,
            key: 'E-learning course domains',
            fields: [
                {
                    title: <Text id='input.customers'>customers</Text>,
                    placeholder: <Text id='input.customers'>customers</Text>,
                    help: <Text id='input.elearningCourse-customers-help'>The course is listed only on these domains (customers).</Text>,
                    name: 'customers',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminCustomers',
                    getValue: obj => obj.customers?.map(m => m),
                    datalist: [
                        'id', 'name',
                    ],
                    displayArrayObject: obj => `${obj.id}: ${obj.name}`,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
    ],
    elearningLesson: [
        {
            name: <Text id='input.elearningLesson-basics-title'>Lesson</Text>,
            key: 'E-learning lesson basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.elearningLesson-course'>Course id</Text>,
                    placeholder: <Text id='input.elearningLesson-course'>Course id</Text>,
                    name: 'course',
                    type: 'number',
                    icon: 'fa-solid fa-graduation-cap',
                    getValue: obj => obj.course,
                    showInList: false,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.sortOrder'>Sort order</Text>,
                    placeholder: <Text id='input.sortOrder'>Sort order</Text>,
                    name: 'sortOrder',
                    type: 'number',
                    icon: 'fa-solid fa-arrow-down-1-9',
                    getValue: obj => obj.sortOrder,
                    showInList: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.estimatedMinutes'>Estimated minutes</Text>,
                    placeholder: <Text id='input.estimatedMinutes'>Estimated minutes</Text>,
                    name: 'estimatedMinutes',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj.estimatedMinutes,
                    showInList: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.elearningLesson-body-placeholder'>Lesson text (markdown)</Text>,
                    name: 'body',
                    type: 'textareaEditor',
                    viewHtml: false,
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.youtube'>YouTube</Text>,
                    placeholder: <Text id='input.elearningLesson-youtube-placeholder'>https://www.youtube.com/watch?v=...</Text>,
                    help: <Text id='input.elearningLesson-youtube-help'>Optional. Embedded above the lesson text. Upload a video file under Files instead to host it yourself.</Text>,
                    name: 'youtube',
                    type: 'text',
                    icon: 'fa-brands fa-youtube',
                    getValue: obj => obj.youtube,
                    showInList: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    help: <Text id='input.elearningLesson-files-help'>Video files are transcoded and played inline.</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.elearningLesson-quiz-title'>Quiz</Text>,
            key: 'E-learning lesson quiz',
            fields: [
                {
                    title: <Text id='input.quiz'>Quiz questions</Text>,
                    placeholder: <Text id='input.quiz'>Quiz questions</Text>,
                    help: <Text id='input.quiz-help'>Optional multiple choice. Leave options empty to hide them. Correct answer is A, B, C or D.</Text>,
                    name: 'quiz',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-circle-question',
                    getValue: obj => obj.quiz,
                    datalist: [
                        'questionText', 'optionA', 'optionB', 'optionC', 'optionD', 'correctAnswer',
                    ],
                    datalistHelp: {
                        questionText: 'The question.',
                        optionA: 'Option A.',
                        optionB: 'Option B.',
                        optionC: 'Option C (leave empty to hide).',
                        optionD: 'Option D (leave empty to hide).',
                        correctAnswer: 'Correct answer: A, B, C or D.',
                    },
                    displayArrayObject: obj => obj.questionText,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '300px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.quizRequired'>Quiz must be passed</Text>,
                    placeholder: <Text id='input.quizRequired'>Quiz must be passed</Text>,
                    help: <Text id='input.quizRequired-help'>When on, every answer must be correct before the lesson can be marked done.</Text>,
                    name: 'quizRequired',
                    type: 'toggle',
                    icon: 'fa-solid fa-check-double',
                    getValue: obj => (obj.quizRequired === 0 ? 0 : 1),
                    showInList: false,
                },
            ],
        },
    ],
    lotTesting: [
        {
            name: 'LOT Identification',
            key: 'LOT Identification',
            isCollapsed: false,
            backgroundColor: 'var(--bs-primary-bg-subtle) !important',
            borderColor: 'var(--bs-primary-border-subtle) !important',
            fields: [
                {
                    title: 'LOT Number',
                    placeholder: 'LOT/batch identifier',
                    name: 'lotNumber',
                    type: 'text',
                    icon: 'fa-solid fa-barcode',
                    getValue: obj => obj.lotNumber,
                    showInList: true,
                },
                {
                    title: 'Brand',
                    placeholder: 'Ammunition brand',
                    name: 'brand',
                    type: 'text',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.brand,
                    showInList: true,
                },
                {
                    title: 'Caliber',
                    placeholder: 'e.g., 9mm, .308 Win',
                    name: 'caliber',
                    type: 'text',
                    icon: 'fa-solid fa-ruler',
                    getValue: obj => obj.caliber,
                    showInList: true,
                },
                {
                    title: 'Public',
                    placeholder: 'Make this entry visible to others',
                    name: 'isPublic',
                    type: 'toggle',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj.isPublic ? 1 : 0,
                    showInList: false,
                },
            ],
        },
        {
            name: 'Ammunition Components',
            key: 'Ammunition Components',
            isCollapsed: true,
            fields: [
                {
                    title: 'Casing',
                    placeholder: 'brass, steel, nickel-plated',
                    name: 'casing',
                    type: 'text',
                    icon: 'fa-solid fa-capsules',
                    getValue: obj => obj.casing,
                    showInList: false,
                },
                {
                    title: 'Primer',
                    placeholder: 'small pistol, large rifle, etc.',
                    name: 'primer',
                    type: 'text',
                    icon: 'fa-solid fa-circle-dot',
                    getValue: obj => obj.primer,
                    showInList: false,
                },
                {
                    title: 'Powder',
                    placeholder: 'Powder type/name',
                    name: 'powder',
                    type: 'text',
                    icon: 'fa-solid fa-fire',
                    getValue: obj => obj.powder,
                    showInList: false,
                },
                {
                    title: 'Powder Charge',
                    placeholder: 'Charge in grains',
                    name: 'powderCharge',
                    type: 'number',
                    step: 0.1,
                    icon: 'fa-solid fa-weight-scale',
                    getValue: obj => obj.powderCharge,
                    showInList: false,
                },
            ],
        },
        {
            name: 'Bullet Details',
            key: 'Bullet Details',
            isCollapsed: true,
            fields: [
                {
                    title: 'Bullet Brand',
                    placeholder: 'Bullet manufacturer',
                    name: 'bulletBrand',
                    type: 'text',
                    icon: 'fa-solid fa-industry',
                    getValue: obj => obj.bulletBrand,
                    showInList: true,
                },
                {
                    title: 'Bullet Type',
                    placeholder: 'FMJ, HP, BTHP, etc.',
                    name: 'bulletType',
                    type: 'text',
                    icon: 'fa-solid fa-shapes',
                    getValue: obj => obj.bulletType,
                    showInList: true,
                },
                {
                    title: 'Bullet Weight',
                    placeholder: 'Weight in grains',
                    name: 'bulletWeight',
                    type: 'number',
                    step: 0.1,
                    icon: 'fa-solid fa-weight-hanging',
                    getValue: obj => obj.bulletWeight,
                    showInList: true,
                },
                {
                    title: 'OAL',
                    placeholder: 'Overall Ammunition Length',
                    name: 'oal',
                    type: 'number',
                    step: 0.001,
                    icon: 'fa-solid fa-ruler-horizontal',
                    getValue: obj => obj.oal,
                    showInList: false,
                },
            ],
        },
        {
            name: 'Notes & Media',
            key: 'Notes & Media',
            isCollapsed: true,
            fields: [
                {
                    title: 'Notes',
                    placeholder: 'General notes about this LOT',
                    name: 'notes',
                    type: 'textarea',
                    icon: 'fa-solid fa-note-sticky',
                    getValue: obj => obj.notes,
                    showInList: false,
                },
                {
                    title: 'Images',
                    placeholder: 'Upload images',
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-camera',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: 'Files',
                    placeholder: 'Upload files',
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-file',
                    getValue: obj => obj.files,
                    showInList: false,
                },
            ],
        },
    ],
    broadcastChannel: [
        {
            name: 'Kanal',
            key: 'Broadcast Channel',
            fields: [
                {
                    title: 'Navn',
                    placeholder: 'Kanalnavn',
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-broadcast-tower',
                    getValue: obj => obj?.name,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Kortnavn',
                    placeholder: 'Kortnavn',
                    name: 'shortName',
                    type: 'text',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj?.shortName,
                    showInList: true,
                },
                {
                    title: 'Type',
                    placeholder: 'Type',
                    name: 'type',
                    type: 'select',
                    icon: 'fa-solid fa-tv',
                    getValue: obj => obj?.type,
                    datalist: [['tv', 'TV'], ['radio', 'Radio'], ['stream', 'Stream']],
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Farge',
                    placeholder: '#4CAF50',
                    name: 'color',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-palette',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj?.color,
                    showInList: false,
                },
                {
                    title: 'Tidssone',
                    placeholder: 'Europe/Oslo',
                    name: 'timezone',
                    type: 'text',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj?.timezone || 'Europe/Oslo',
                    showInList: false,
                },
                {
                    title: 'Sendedag starter (time)',
                    placeholder: '6',
                    name: 'broadcastDayStart',
                    type: 'number',
                    icon: 'fa-solid fa-sun',
                    getValue: obj => obj?.broadcastDayStart ?? 6,
                    showInList: false,
                },
                {
                    title: 'Sendedag slutter (time)',
                    placeholder: '6',
                    name: 'broadcastDayEnd',
                    type: 'number',
                    icon: 'fa-solid fa-moon',
                    getValue: obj => obj?.broadcastDayEnd ?? 6,
                    showInList: false,
                },
                {
                    title: 'Sortering',
                    placeholder: '0',
                    name: 'sortOrder',
                    type: 'number',
                    icon: 'fa-solid fa-sort',
                    getValue: obj => obj?.sortOrder ?? 0,
                    showInList: false,
                },
                {
                    title: 'Stream URL',
                    placeholder: 'https://...',
                    name: 'streamUrl',
                    type: 'text',
                    icon: 'fa-solid fa-video',
                    getValue: obj => obj?.streamUrl,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: 'Beskrivelse',
                    placeholder: 'Beskrivelse',
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-align-left',
                    getValue: obj => obj?.description,
                    showInList: false,
                },
                {
                    title: 'Kompakt visning',
                    placeholder: 'Kompakt kolonne med hover-detaljer',
                    name: 'compactView',
                    type: 'toggle',
                    icon: 'fa-solid fa-compress',
                    getValue: obj => obj?.compactView ? 1 : 0,
                    showInList: false,
                },
                {
                    title: 'Løp (Race ID)',
                    placeholder: '0',
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-flag-checkered',
                    getValue: obj => obj?.race,
                    showInList: true,
                },
            ],
        },
        {
            name: 'Posisjon',
            key: 'Position',
            fields: [
                {
                    title: 'Breddegrad (lat)',
                    placeholder: '69.0',
                    name: 'lat',
                    type: 'number',
                    step: 0.000001,
                    icon: 'fa-solid fa-location-dot',
                    getValue: obj => obj?.lat,
                    showInList: false,
                },
                {
                    title: 'Lengdegrad (lng)',
                    placeholder: '18.0',
                    name: 'lng',
                    type: 'number',
                    step: 0.000001,
                    icon: 'fa-solid fa-location-dot',
                    getValue: obj => obj?.lng,
                    showInList: false,
                },
                {
                    title: 'Rotasjon (grader)',
                    placeholder: '0',
                    name: 'rotateDeg',
                    type: 'number',
                    icon: 'fa-solid fa-rotate',
                    getValue: obj => obj?.rotateDeg ?? 0,
                    showInList: false,
                },
            ],
        },
    ],
    broadcastEvent: [
        {
            name: 'Hendelse',
            key: 'Broadcast Event',
            fields: [
                {
                    title: 'Tittel',
                    placeholder: 'Tittel',
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-heading',
                    getValue: obj => obj?.title,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Undertittel',
                    placeholder: 'Undertittel',
                    name: 'subtitle',
                    type: 'text',
                    icon: 'fa-solid fa-text',
                    getValue: obj => obj?.subtitle,
                    showInList: false,
                },
                {
                    title: 'Type',
                    placeholder: 'Type',
                    name: 'eventType',
                    type: 'select',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj?.eventType,
                    datalist: [
                        ['program', 'Program'],
                        ['commercial', 'Reklame'],
                        ['promo', 'Promo'],
                        ['ident', 'Ident'],
                        ['news', 'Nyheter'],
                        ['live', 'Live'],
                        ['break', 'Pause'],
                        ['filler', 'Filler'],
                    ],
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Start',
                    placeholder: 'Start',
                    name: 'scheduledStart',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj?.scheduledStart,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.scheduledStart);
                        const end = obj.scheduledEnd ? new Date(obj.scheduledEnd) : null;
                        if (!end || end <= start) {
                            const newEnd = new Date(start);
                            newEnd.setHours(newEnd.getHours() + 1);
                            updateValue({
                                name: 'scheduledEnd',
                                value: newEnd.toISOString(),
                            });
                        }
                    },
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Slutt',
                    placeholder: 'Slutt',
                    name: 'scheduledEnd',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj?.scheduledEnd,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Timing-type',
                    placeholder: 'Timing',
                    name: 'timingType',
                    type: 'select',
                    icon: 'fa-solid fa-stopwatch',
                    getValue: obj => obj?.timingType,
                    datalist: [
                        ['hard', 'Hard (fast tid)'],
                        ['soft', 'Soft (kan flyttes)'],
                        ['flexible', 'Fleksibel'],
                        ['float', 'Flytende'],
                    ],
                    showInList: false,
                },
                {
                    title: 'Kilde-type',
                    placeholder: 'Kilde',
                    name: 'sourceType',
                    type: 'select',
                    icon: 'fa-solid fa-file-video',
                    getValue: obj => obj?.sourceType || 'live',
                    datalist: [
                        ['live', 'Live'],
                        ['file', 'Fil'],
                        ['feed', 'Feed'],
                        ['manual', 'Manuell'],
                        ['placeholder', 'Plassholder'],
                    ],
                    showInList: false,
                },
                {
                    title: 'Asset ID',
                    placeholder: 'Asset ID',
                    name: 'assetId',
                    type: 'text',
                    icon: 'fa-solid fa-fingerprint',
                    getValue: obj => obj?.assetId,
                    showInList: false,
                },
                {
                    title: 'Filsti',
                    placeholder: '/path/to/file',
                    name: 'assetPath',
                    type: 'text',
                    icon: 'fa-solid fa-folder',
                    getValue: obj => obj?.assetPath,
                    showInList: false,
                },
                {
                    title: 'Farge',
                    placeholder: '#4CAF50',
                    name: 'backgroundColor',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-palette',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj?.backgroundColor,
                    showInList: false,
                },
                {
                    title: 'Beskrivelse',
                    placeholder: 'Beskrivelse',
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-align-left',
                    getValue: obj => obj?.description,
                    showInList: false,
                },
                {
                    title: 'Løp (Race ID)',
                    placeholder: '0',
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-flag-checkered',
                    getValue: obj => obj?.race,
                    showInList: true,
                },
            ],
        },
    ],
    broadcastDaySchedule: [
        {
            name: 'Sendeplan',
            key: 'Broadcast Day Schedule',
            fields: [
                {
                    title: 'Kanal',
                    placeholder: 'Kanal ID',
                    name: 'channel',
                    type: 'number',
                    icon: 'fa-solid fa-broadcast-tower',
                    getValue: obj => obj?.channel,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Dato',
                    placeholder: 'Dato',
                    name: 'date',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj?.date,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Status',
                    placeholder: 'Status',
                    name: 'status',
                    type: 'select',
                    icon: 'fa-solid fa-circle-info',
                    getValue: obj => obj?.status,
                    datalist: [
                        ['draft', 'Utkast'],
                        ['approved', 'Godkjent'],
                        ['live', 'Live'],
                        ['completed', 'Fullført'],
                    ],
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Tittel',
                    placeholder: 'Tittel',
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-heading',
                    getValue: obj => obj?.title,
                    showInList: true,
                },
                {
                    title: 'Notater',
                    placeholder: 'Interne notater',
                    name: 'notes',
                    type: 'textarea',
                    icon: 'fa-solid fa-align-left',
                    getValue: obj => obj?.notes,
                    showInList: false,
                },
                {
                    title: 'Versjon',
                    placeholder: '1',
                    name: 'version',
                    type: 'number',
                    icon: 'fa-solid fa-code-branch',
                    getValue: obj => obj?.version ?? 1,
                    showInList: false,
                },
            ],
        },
    ],
    broadcastProgram: [
        {
            name: 'Program',
            key: 'Broadcast Program',
            fields: [
                {
                    title: 'Navn',
                    placeholder: 'Programnavn',
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-film',
                    getValue: obj => obj?.name,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: 'Kortnavn',
                    placeholder: 'Kortnavn',
                    name: 'shortName',
                    type: 'text',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj?.shortName,
                    showInList: true,
                },
                {
                    title: 'Beskrivelse',
                    placeholder: 'Beskrivelse',
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-align-left',
                    getValue: obj => obj?.description,
                    showInList: false,
                },
                {
                    title: 'Sjanger',
                    placeholder: 'Sjanger',
                    name: 'genre',
                    type: 'text',
                    icon: 'fa-solid fa-masks-theater',
                    getValue: obj => obj?.genre,
                    showInList: true,
                },
                {
                    title: 'Standard varighet (sek)',
                    placeholder: '3600',
                    name: 'defaultDurationSeconds',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj?.defaultDurationSeconds,
                    showInList: false,
                },
                {
                    title: 'Standard hendelsestype',
                    placeholder: 'Type',
                    name: 'defaultEventType',
                    type: 'select',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj?.defaultEventType || 'program',
                    datalist: [
                        ['program', 'Program'],
                        ['commercial', 'Reklame'],
                        ['promo', 'Promo'],
                        ['ident', 'Ident'],
                        ['news', 'Nyheter'],
                        ['live', 'Live'],
                        ['break', 'Pause'],
                        ['filler', 'Filler'],
                    ],
                    showInList: false,
                },
                {
                    title: 'Farge',
                    placeholder: '#4CAF50',
                    name: 'defaultColor',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-palette',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj?.defaultColor,
                    showInList: false,
                },
                {
                    title: 'Bakgrunnsfarge',
                    placeholder: '#4CAF50',
                    name: 'defaultBackgroundColor',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-fill-drip',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj?.defaultBackgroundColor,
                    showInList: false,
                },
            ],
        },
    ],
    checklist: [
        {
            name: <Text id='input.checklist-basics'>Checklist</Text>,
            key: 'Checklist basics',
            fields: [
                {
                    title: <Text id='input.checklist-title'>Title</Text>,
                    placeholder: <Text id='input.checklist-title-ph'>Checklist title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-list-check',
                    getValue: obj => obj.title,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.checklist-intro'>Intro</Text>,
                    placeholder: <Text id='input.checklist-intro-ph'>Markdown intro shown at the top</Text>,
                    name: 'intro',
                    type: 'textareaEditor',
                    viewHtml: false,
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.intro,
                },
                {
                    title: <Text id='input.checklist-status'>Status</Text>,
                    name: 'status',
                    type: 'select',
                    icon: 'fa-solid fa-toggle-on',
                    datalist: [['draft', 'Draft'], ['active', 'Active'], ['archived', 'Archived']],
                    getValue: obj => obj.status || 'draft',
                    showInList: true,
                },
                {
                    title: <Text id='input.checklist-interval'>Recurrence (months)</Text>,
                    help: <Text id='input.checklist-interval-help'>0 = one-off. Otherwise re-issued every N months.</Text>,
                    name: 'recurrence.intervalMonths',
                    type: 'select',
                    icon: 'fa-solid fa-repeat',
                    datalist: [['0', 'One-off'], ['1', 'Every month'], ['3', 'Every 3 months'], ['6', 'Every 6 months'], ['12', 'Every 12 months']],
                    getValue: obj => `${obj.recurrence?.intervalMonths || 0}`,
                    showInList: true,
                },
                // NOTE: `customers` (assigned org ids, a [Number] array) is NOT a FormFieldsCompact
                // field — the generic `tags` control binds to object.tags and `text` won't round-trip
                // to a numeric array. It is managed by a dedicated editor in the admin edit component,
                // which persists a real numeric array via checklistStore.saveCustomers().
            ],
        },
    ],
};
